import {
  trackDubReviewModeToggled,
  trackMusicModeToggled,
  trackTmEntryMoved,
  trackTmEntryUpdated,
  trackTmPromotedFromManual,
  trackTranslationReview_CaptionsModeChanged,
  trackTranslationReview_Exited,
  trackTranslationReview_LinkCopied,
  trackTranslationReview_MarkerChipEditRequested,
  trackTranslationReview_MarkerChipRemoved,
  trackTranslationReview_ModeChanged,
} from '@src/analytics/EventTracker';
import { type AudioMode } from '@src/components/aiVoices/AudioModeSegmentedToggle';
import {
  type DubLevels,
  FULL_DUB_LEVEL,
} from '@src/components/aiVoices/DubLevelSlider';
import { DubRunProgress } from '@src/components/aiVoices/DubRunProgress';
import RedubLandedPulse from '@src/components/aiVoices/RedubLandedPulse';
import {
  useActiveDubRun,
  useLastCompletedDubRun,
} from '@src/components/aiVoices/useActiveDubRun';
import { useAutoRedubOnCommit } from '@src/components/aiVoices/useAutoRedubOnCommit';
import { useDubManifestFetch } from '@src/components/aiVoices/useDubbedLanguages';
import { useDubDescriptionRefresh } from '@src/components/aiVoices/useDubDescriptionRefresh';
import { useDubPlayback } from '@src/components/aiVoices/useDubPlayback';
import useLoadBrandKitFonts from '@src/components/BrandKit/utils/useLoadBrandKitFonts';
import {
  ContextualHint,
  dismissContextualHint,
} from '@src/components/common/ContextualHint';
import Tabs, { type Tab } from '@src/components/common/Tabs';
import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { TOP_LAYER_VARS, TOP_LAYER_Z } from '@src/components/common/topLayerZ';
import { useAnchoredDropdown } from '@src/components/common/useAnchoredDropdown';
import { ClipPreviewControlBar } from '@src/components/content-components/ClipPreviewControlBar';
import CreatomateClipPreview from '@src/components/content-components/CreatomateClipPreview';
import { useClipPreviewRenderer } from '@src/components/content-components/hooks/useClipPreviewRenderer';
import { usePlaybackState } from '@src/components/content-components/hooks/usePlaybackState';
import { useRenderedAspectRatio } from '@src/components/content-components/hooks/useRenderedAspectRatio';
import { useRendererPlayback } from '@src/components/content-components/hooks/useRendererPlayback';
import { transcriptTabLabel } from '@src/components/content-components/tabs';
import { TranscriptPlayerWithMagnifier } from '@src/components/content-components/TranscriptSpeakersTab/TranscriptPlayerWithMagnifier';
import EntryFormModal, {
  type CollectionOption,
} from '@src/components/Settings/sections/translationMemory/EntryFormModal';
import { SIDEBAR_SPRING } from '@src/components/sidebar/sidebarMotion';
import CreatorStudioSpeakersPanel from '@src/components/sidepanel/CreatorStudioSpeakersPanel';
import { SetDubOptionsCommand } from '@src/editor/commands/SetDubOptionsCommand';
import { normalizeDubVoiceVolume } from '@src/editor/dubbing/DubEngine';
import { sameDubLeadIn } from '@src/editor/dubbing/leadIn';
import {
  sameDubPerLanguageConfiguration,
  withLanguageSegmentLevels,
} from '@src/editor/dubbing/segmentLevels';
import { dubCaptionInputs } from '@src/editor/services/dubCaptionRows';
import {
  ensureReviewCaptions,
  followReviewDub,
  removeReviewCaptions,
  stopFollowingReviewDub,
  storyDubStretch,
} from '@src/editor/services/reviewCaptionTrack';
import { usePlayerSpaceShortcut } from '@src/hooks/shortcuts/usePlayerSpaceShortcut';
import { usePlayerVolume } from '@src/hooks/usePlayerVolume';
import { useSidebarState } from '@src/hooks/useSidebarState';
import { useTranscriptOperations } from '@src/hooks/useTranscriptOperations';
import { markersByLine } from '@src/services/dubbing/lineMarkers';
// PLAT-11381 — the word the host uses to declare what this review is over.
import { type ReviewTranscriptScope } from '@src/services/dubbing/reviewRedubScope';
import {
  useDatoClient,
  useDatoPerson,
  useDubbingStore,
  useEditor,
  useFeatureFlags,
  useTranscription,
  useTranslationMemoryEntryStore,
  useTranslationMemoryStore,
  useTranslationStore,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import {
  hasTypedManualFix,
  resolveOverrideSegmentIdForElementIds,
} from '@src/stores-v2/translationSpanEdit';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import BookIcon from '@src/svgs/BookIcon';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import CirclePlayIcon from '@src/svgs/CirclePlayIcon';
import { DocumentLinesIcon } from '@src/svgs/DocumentLinesIcon';
import MicrophoneIcon from '@src/svgs/MicrophoneIcon';
import { MuxPauseIcon } from '@src/svgs/MuxPauseIcon';
import SpeakersIcon from '@src/svgs/SpeakersIcon';
import TrackChangesIcon from '@src/svgs/TrackChangesIcon';
import { handleCopyToClipboard } from '@src/utility/general';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import { resolveDetectedLanguage } from '@src/utility/languageMatching';
import {
  linkedHighlightCss,
  type LinkedPhrase,
} from '@src/utility/linkedCrossLanguageHighlight';
import { LogManager } from '@src/utility/Logger';
import { spanReplaceLockCss } from '@src/utility/spanReplaceLockHighlight';
import {
  dispatchTranscriptSeek,
  TRANSCRIPT_PAUSE_EVENT,
} from '@src/utility/transcriptEvents';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import {
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
} from 'framer-motion';
import { observer } from 'mobx-react-lite';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styled, { createGlobalStyle, css } from 'styled-components';

import { AlignedBlocksView } from './AlignedBlocksView';
import {
  countActiveChanges,
  countTmBackedFragments,
  phrasesForPromoteKey,
} from './changedPhraseKit';
import {
  type CoverageCache,
  coverageLead,
  coverageSignature,
  entryForPhrase,
  verdictForLead,
  verdictForPhrase,
} from './coverageCache';
import { DirectedRetranslateModal } from './DirectedRetranslateModal';
import {
  captureSourceBaseline,
  computeStaleSignatures,
  type OriginalWordRef,
  resolveSelectionArrayRange,
} from './editableOriginal';
import { useFragmentRecombine } from './hooks/useFragmentRecombine';
import { useLinkedCrossLanguageHighlight } from './hooks/useLinkedCrossLanguageHighlight';
import { useReviewHandoff } from './hooks/useReviewHandoff';
import {
  SPAN_MACHINE_RETRANSLATE_UNAVAILABLE_MESSAGE,
  spanBlockMessage,
  useSpanReplace,
} from './hooks/useSpanReplace';
import { useTranslationSaveStatus } from './hooks/useTranslationSaveStatus';
import { ReplaceSpanEditor } from './ReplaceSpanEditor';
import {
  recordResolvedSignature,
  retranslateStaleFragment,
  toEditorFailureReason,
  visibleStaleLeadsFromSignatures,
} from './resolveStaleFragment';
import { ReviewCaptionOverlay } from './ReviewCaptionOverlay';
import {
  PickerMenu,
  PickerRow,
  PickerRowMeta,
  PickerTrigger,
} from './reviewPaneHeaderPicker';
import {
  type CaptionsMode,
  type ReviewMusicSeparation,
  ReviewPlayerAudioPickers,
} from './ReviewPlayerAudioPickers';
import { SECTION_REDUB_HINT_KEY, SectionRedubHint } from './SectionRedubHint';
import { SpanReplaceTrigger } from './SpanReplaceTrigger';
import { isStoryTranscriptEditingLocked } from './storyTranscriptEditLock';
import { TranscriptContextMenu } from './TranscriptContextMenu';
import { TranscriptViewControls } from './TranscriptViewControls';
import { TranslationChangesPanel } from './TranslationChangesPanel';
import { TranslationMemoryPanel } from './TranslationMemoryPanel';
import {
  resolvePreviousSource,
  TRANSLATION_SOURCE_SHORT_LABEL,
} from './translationProviderDisplay';
import {
  type ReviewExitMethod,
  type ReviewMode,
  TranslationReviewHeader,
} from './TranslationReviewHeader';
import { useReviewLevelsLine } from './useReviewLevelsLine';
import { WordReplacementModal } from './WordReplacementModal';

import type { TranslationViewMode } from './alignedBlocks';
import type { SplitControls } from './CoveragePopover';
import type {
  ConfirmDirectedFailureReason,
  ReDubSelectionRequest,
} from './hooks/useSpanReplace';
import type { ReviewHandoff } from '@src/components/aiVoices/reviewHandoff';
import type { DubReviewSave } from '@src/components/content-components/hooks/useClipDubComposition';
import type { PlaybackState } from '@src/components/content-components/hooks/usePlaybackState';
import type {
  ExternalPlayerControls,
  VideoTimeRange,
} from '@src/components/content-components/TranscriptSpeakersTab/TranscriptVideoPlayer';
import type { CustomKaraokeTemplateOption } from '@src/components/textProcessor/TextBrandingService';
import type { DubMusicMode } from '@src/editor/dubbing/DubEngine';
import type { DubLeadIn } from '@src/editor/dubbing/leadIn';
import type { DubOnReviewClock } from '@src/editor/dubbing/reviewDubPlayhead';
import type { DubSegmentLevels } from '@src/editor/dubbing/segmentLevels';
import type { KaraokeConfig } from '@src/editor/models/karaoke/KaraokeConfig';
import type { DubOptions } from '@src/editor/models/Timeline';
import type { CaptionFallbackTemplate } from '@src/editor/services/applyCaptionsLanguage';
import type { ReviewCaptionEditor } from '@src/editor/services/reviewCaptionTrack';
import type { KaraokeLine } from '@src/editor/types/karaoke';
import type { VideoEditor } from '@src/editor/VideoEditor';
import type { DubManifestDTO } from '@src/repositories/DubbingRepository';
import type {
  TranslationMemoryEntry,
  TranslationMemoryEntryInput,
} from '@src/repositories/TranslationMemoryEntryRepository';
import type { Result } from '@src/services/common/Result';
import type { RedubLanded } from '@src/services/dubbing/redubLanded';
import type { ITranscriptVideoCreator } from '@src/stores/ITranscriptVideoCreator';
import type {
  ClipEditSessionStore,
  VideoClipPreview,
} from '@src/stores-v2/ClipEditSessionStore';
import type { Language } from '@src/types.ts/language';
import type { Story } from '@src/types.ts/story';
import type { TranslationData } from '@src/types.ts/translation';

const logger = LogManager.getLogger('feature:translation-review');

/**
 * PLAT-10261 (T3) — the three-pane **Translation Review** workspace shell.
 *
 * A full-surface modal opened from the `TranslationToggle` "Review
 * translations" entry (Content Studio Speakers & Transcripts). It composes
 * the existing renderers in three columns — left = original transcript,
 * middle = translated transcript (same renderer with a `language` prop),
 * right = a Speakers/Summary panel above a compact player. **No backend**:
 * it renders on the data already in the store.
 *
 * Containment (D9): all new behavior lives here; the shared renderers are
 * mounted, never forked. The workspace must be rendered INSIDE the surface's
 * scoped `StoreContext.Provider` (e.g. `TranscriptSpeakersContent`) so the
 * panes/player/speakers-panel reuse the same scoped editor + adapter. React
 * portals preserve that context even though the DOM lands on `document.body`.
 *
 * Surface-agnostic (D11): the caller resolves the `videoCreator` (story
 * adapter for whole-script today; a clip-proxied one for the clip-preview
 * entry, T3.3 / PLAT-10286).
 *
 * View modes (T5 / PLAT-10264): Paragraph, Segment, and Fragment all render
 * through `AlignedBlocksView` as aligned rows — one row per block spanning the
 * original and translated columns (PLAT-10288 added Paragraph to this path;
 * epic decision Option A). Coverage badge (T8) and span replace (T6) layer on
 * later.
 */

// Threads the app's z-scale. This full-surface modal must sit ABOVE the app
// chrome it covers — left nav `MainMenu` (z=1000002) and `RightSidePanel`
// (z=1000003). The transient overlays it hosts (speaker ⋯ menu, Add/Edit
// modal, tooltips) keep their resting z by default; the clip-preview entry
// (which mounts above CLIP_PREVIEW_Z.translationReview = 1000030) lifts them
// onto the TOP_LAYER_Z scale (1000040+, common/topLayerZ.ts) only while open,
// via `liftOverlays` — so other surfaces are unchanged (T3.3 / PLAT-10286).
// Keep any new workspace z below 1000040.
// Exported so the deep-link restore veil (PLAT-11037) can paint at the exact
// same layer on this surface — a veil at a different z would either flash the
// page through or outlive the workspace it hands over to.
export const REVIEW_WORKSPACE_Z = 1000004;
/**
 * PLAT-11656 — the `page` layout's z on the story entry: a place beside the app
 * sidebar, so it sits UNDER `MainMenu` (1000002) and everything that already
 * cleared the modal z above still paints over it.
 */
export const REVIEW_PANEL_Z = 1000001;

/** Anchor id for the Split into Words inert-state explanation. */
/** Re-exported for the URL-state module and hosts (the type lives with the header). */
export type { ReviewMode } from './TranslationReviewHeader';

/** What the story host lends the review for its captions (PLAT-11683). */
export interface ReviewStoryCaptionsHost {
  isReady: boolean;
  editor: ReviewCaptionEditor | null;
  karaokeLines: readonly KaraokeLine[];
  refreshKaraokeState: () => void;
  /** The story's saved caption style; `null` when the story has no captions. */
  savedStyle: KaraokeConfig | null;
}

export interface TranslationReviewWorkspaceProps {
  /** The translated language under review (BCP 47). */
  language: string;
  /**
   * Source resolver (D11) — the scoped video creator/editor for this surface.
   * Content Studio passes the whole-story adapter; the clip-preview entry
   * (T3.3 / PLAT-10286) swaps a clip-proxied
   * one. The panes + player drive off it.
   */
  videoCreator: ITranscriptVideoCreator;
  /** Showcase-enabled languages — for the title label. */
  supportedLanguages: Language[];
  /** Player zoom level, lifted by the parent surface. */
  zoomLevel: number;
  /**
   * Overlay z-index. Defaults to REVIEW_WORKSPACE_Z (Content Studio S&T). The
   * clip-preview entry (T3.3 / PLAT-10286) passes a higher value to clear the
   * clip-preview modal stack (CLIP_PREVIEW_Z tops at 1000021).
   */
  zIndex?: number;
  /**
   * Story-absolute playback window for a clip-scoped review (PLAT-10286).
   * Threaded to the compact player, which treats the shared videoCreator
   * clock as range-local (0 = clip start).
   */
  timeRange?: VideoTimeRange;
  /**
   * Loading-placeholder poster for the compact player — the clip's rendered
   * thumbnail, shown while the whole-story video loads + seeks to the paused
   * position, then cleared (PLAT-10286). Absent ⇒ the story cover is used.
   */
  poster?: string;
  /**
   * Lift the shared transient overlays (context menus, speaker modals,
   * tooltips) above this workspace WHILE it is open. Only the clip-preview
   * entry needs it — its workspace sits above the clip-preview modal stack, so
   * those body-portaled overlays would otherwise render behind it. Off for the
   * S&T entry (its lower z already clears them) and every other surface, so
   * their z is unchanged at rest (PLAT-10286). */
  liftOverlays?: boolean;
  /**
   * PLAT-11656 — how the review sits in the app. `page`: a full-height panel
   * beside the app sidebar, which stays visible and clickable — the sidebar
   * marks the area, the header's trail marks the place inside it. No backdrop,
   * no close X, no Escape-to-exit; the back arrow is the one way out. `modal`
   * (default, tests and any future dialog host): a dialog over the surface
   * that opened it, with backdrop, X and Escape.
   */
  layout?: 'modal' | 'page';
  /**
   * Initial view-mode (PLAT-10264 / T5). Defaults to `'segment'` so the modal
   * opens on Segment view — the granularity a translation is generated and
   * re-generated at, so it is the one a reviewer's edits land on (PLAT-10777).
   * Seeds the local toggle state only; the user can switch to Paragraph /
   * Fragment freely afterwards.
   */
  initialViewMode?: TranslationViewMode;
  /**
   * Notified when the user switches view-mode (PLAT-10948). Both entries use
   * this to persist the choice as the VALUE of their review param
   * (`?clipTranslationReview=` / `?translationReview=`, whose presence is the
   * open state) so a shared/refreshed link restores it.
   */
  onViewModeChange?: (next: TranslationViewMode) => void;
  /**
   * Clip-renderer inputs (PLAT-10286 Step 3). Present on the clip-preview entry,
   * where the review mounts its OWN renderer (`ReviewClipPlayer`) to show the
   * edited montage. Absent on the Content Studio whole-story entry, which keeps
   * the original-video magnifier player. The player choice gates on these.
   */
  story?: Story;
  sessionStore?: ClipEditSessionStore;
  clip?: VideoClipPreview;
  /** Clip entry only: whether the clip is a Reel, for the header's trail. */
  clipKind?: 'Clip' | 'Reel';
  /**
   * PLAT-10600 (T12) / PLAT-11340 — enable the review's quick Original↔Dubbed
   * toggle + the hear-both overlay. Set by the caller when dubbing is enabled
   * AND the reviewed language has a dub. On the whole-story entry the toggle
   * drives `useDubPlayback`'s browser-audio overlay over the raw video. On the
   * clip entry it drives the review's OWN clip renderer instead, which
   * composites the dub itself through the dub engine (PLAT-10629) — the
   * browser-audio overlay stays off there, or the dub would be heard twice.
   * Off ⇒ the review plays the original audio only.
   */
  enableDubMonitor?: boolean;
  /**
   * PLAT-11685 — opt-in first dub from inside Dubbing Review. With the mic
   * button no longer gated on a dub existing, a reviewer can land in Dubbing
   * Review on an undubbed language; this is what they do about it. Receives the
   * reviewed language; the HOST owns the dub modal, exactly as it does for the
   * language menu's own "Dub with AI Voice" (the clip entry starts the clip's
   * dub, the story entry the story's). Absent ⇒ no action offered, so a surface
   * with dubbing switched off is unchanged.
   */
  onRequestDub?: (language: string) => void;
  /**
   * PLAT-11381 — whether this review is open over the whole story's transcript
   * or over one clip's slice of it, said outright by the host instead of being
   * inferred here from which optional props arrived. `'clip'` on the clip
   * preview entry, `'story'` on the Content Studio whole-story entry. Absent ⇒
   * the workspace falls back to the old inference (a clip and its edit session
   * were both handed in), so a host that has not been updated is unchanged.
   */
  transcriptScope?: ReviewTranscriptScope;
  /**
   * PLAT-11382 — re-dub the lines the reviewer has selected, so a delivery
   * that reads wrong can be re-taken where it was noticed. Receives the
   * reviewed language and the selection (its dub line ids, speakers and
   * anchor). The HOST owns the popover, exactly as it does for
   * `onRequestDub`; it opens the same selection re-dub popover it opens for a
   * selection on its own transcript (PLAT-12022). Absent ⇒ the "Re-dub AI
   * Voice" row is not in the selection menu at all.
   */
  onRequestReDubSelection?: (
    language: string,
    request: ReDubSelectionRequest,
  ) => void;
  /**
   * PLAT-11768 — an automatic re-dub has landed and the story's dub has
   * changed. The review re-reads its own audio; this tells the HOST to re-read
   * the copy it holds, so the surface underneath is not left playing (and
   * drawing) the take from before the edit.
   */
  onDubUpdated?: () => void;
  /**
   * PLAT-12024 — a re-dub the host's popover carried out has landed: the
   * lines it rewrote, when, and the toast's reveal token. The review mounts
   * `RedubLandedPulse` in its own transcript column, which lights those
   * lines' fragments, says on hover what was asked, and plays the passage on
   * the toast's action — the same treatment the host's transcript gives.
   */
  landedRedub?: RedubLanded | null;
  /**
   * PLAT-12047 / PLAT-12056 — an action asked for on the host's own
   * transcript, handed here so the per-line work happens in the review.
   * Once the words are rendered in this column the review scrolls to them
   * and either (`redub`) lights them with the landed wash and calls
   * `onRequestReDubSelection` with the first word as the anchor, or (`fix`)
   * opens the Fix Translation editor over them; then it says so through
   * `onHandoffConsumed`, and the host drops the hand-off. A re-dub whose
   * lines never render still opens the popover, anchored on the column.
   */
  pendingHandoff?: ReviewHandoff | null;
  onHandoffConsumed?: () => void;
  /**
   * PLAT-10600 (T12) — seed the review's transient audio mode from the
   * originating surface's current mode (continuity). The review diverges freely
   * afterwards and never persists it (a transient A-B monitoring choice, DE6).
   * Defaults to `'original'`.
   */
  initialAudioMode?: AudioMode;
  /**
   * PLAT-11340 — the story's kept music bed (signed address), so the clip
   * entry's own renderer lays it under the dub exactly as the clip preview
   * does. `null`/absent ⇒ no bed. Ignored on the whole-story entry, whose
   * overlay plays no bed.
   */
  dubMusicBedUrl?: string | null;
  /** The story's separated voice, which a clip's lead-in moves ahead of the dub. */
  dubVoiceStemUrl?: string | null;
  /**
   * PLAT-11665 — the review's transient Music choice (None / From Original)
   * under the dubbed voice, seeded like `initialAudioMode` from the surface's
   * current position and never persisted.
   */
  initialMusicMode?: DubMusicMode;
  /**
   * PLAT-11899 — the level the clip's dubbed voice is saved at (0–100), which
   * the review's own level starts from. Absent ⇒ full.
   */
  clipVoiceVolume?: number;
  /**
   * PLAT-11927 — the level the clip's original recording is saved at on Both
   * (0–100), which the review's own starts from. Absent ⇒ full.
   */
  clipOriginalVolume?: number;
  /** The lines the clip plays at their own levels, which the review's start from. */
  clipSegmentLevels?: DubSegmentLevels;
  /** The clip's original lead-in on Both, which the review's starts from. */
  clipLeadIn?: DubLeadIn;
  /**
   * PLAT-11899 / PLAT-11927 — save onto the clip what the review is playing:
   * its Voice and Music on every pick, and its levels too on Apply to Clip,
   * so leaving the review keeps what was chosen in it. The HOST saves, through
   * the clip preview's own editor: the review's player holds a separate copy
   * of the clip, and a save from there would be overwritten by the preview's
   * next one. Absent ⇒ the review is a preview only (the whole-story entry).
   */
  onApplyToClip?: (save: DubReviewSave) => void;
  /**
   * PLAT-11714 — the story's voice/music separation, so a reviewer who finds
   * the Music picker greyed out can make the bed from here instead of going
   * back to Transcript & Speakers for it. A narrow view of the host's
   * `useMusicSeparationController` — the review runs none of its own. Absent ⇒
   * the picker stays inert until a bed exists (the clip entry, deliberately:
   * separation is story-wide and minutes long).
   */
  musicSeparation?: ReviewMusicSeparation;
  /**
   * PLAT-10635: custom-template seed for the review player's own editor. When
   * present and the clip is still on the built-in "Impact" default, the review
   * produces captions from this template instead of Impact — matching what the
   * clip preview seeds on translate. Resolved by the parent surface (which owns
   * `textBrandingService`); absent ⇒ the review keeps the built-in default.
   */
  captionFallbackTemplate?: CaptionFallbackTemplate;
  /**
   * PLAT-11702 follow-up — every caption template the showcase has at this
   * video's aspect ratio, most recently used first. Listed in the Captions
   * dropdown when the video has no captions of its own, so the reviewer can
   * see it in any of the showcase's styles. Display only: the pick lives for
   * the session and is never written to the clip or the story.
   */
  captionTemplates?: CustomKaraokeTemplateOption[];
  /**
   * PLAT-11702 follow-up — the showcase's name for a caption template id,
   * for naming the row that stands for the video's own style. Resolves ids
   * `captionTemplates` does not carry: an Arbor or built-in preset, a
   * language variant, a template saved at another aspect ratio.
   */
  resolveCaptionTemplateName?: (templateId: string) => string | undefined;
  /**
   * PLAT-11877 — whether a caption template id is an Arbor / built-in preset
   * rather than one of the showcase's own, so the row standing for the
   * video's style can say where it came from.
   */
  isPresetCaptionTemplate?: (templateId: string) => boolean;
  /**
   * PLAT-11683 — story entry only: the transcript-scoped editor the review
   * draws its captions on (the host's own, already loaded with the story's
   * transcript and wired to the translation resolver), plus the style of the
   * captions the story already has (`null` when it has none). The review
   * produces a caption track on this editor while Captions is On — review
   * only, nothing saved — and removes it when it closes. Absent on the clip
   * entry, whose own renderer editor carries the captions.
   */
  storyCaptions?: ReviewStoryCaptionsHost;
  /**
   * Initial right-hand tab. Defaults to Changes. Seeds the local tab state
   * only — like `initialViewMode`, the user switches freely afterwards.
   */
  initialRightTab?: ReviewRightTab;
  /**
   * PLAT-11662 — which job the review opens on. The host takes it from the
   * control that was clicked (the globe / "Review Translation" vs the
   * microphone / "Review Dub"), or from the link. Absent ⇒ Dubbing when the
   * language has a dub, Translation otherwise (the pre-PLAT-11662 guess, kept
   * for links that carry no mode). Seeds the local state only; the header's
   * tabs switch freely afterwards.
   */
  initialReviewMode?: ReviewMode;
  /**
   * Initial proxy reference language for the left column (PLAT-10655).
   * Defaults to the true original.
   */
  initialReferenceLanguage?: string | null;
  /**
   * Report the URL-addressable view attributes whenever they change, so a host
   * surface can mirror them in the query string (Content Studio S&T does; the
   * clip-preview entry, whose modal isn't URL-addressable, doesn't). Purely an
   * outbound notification — the workspace stays the owner of this state.
   */
  onViewStateChange?: (state: {
    viewMode: TranslationViewMode;
    panel: ReviewRightTab;
    referenceLanguage: string | null;
    /** PLAT-11662 — Translation or Dubbing, so a shared link reopens the same job. */
    reviewMode: ReviewMode;
  }) => void;
  onClose: () => void;
}

/** The workspace's right-hand panel tabs (exported for URL persistence). */
export type ReviewRightTab =
  | 'changes'
  | 'translationMemory'
  | 'summary'
  | 'speakers';

type RightTab = ReviewRightTab;

/**
 * Distinct reviewers who edited this translation — collected from single-phrase
 * edits (`phrases[].editedBy`) and span-replace override segments
 * (`parentPhrases[].editedBy`), the same attribution the Changes tab reads.
 * Most-recently-active first (by last edit timestamp). Absent on machine output
 * and pre-attribution edits, so consumers gate on presence.
 */
function collectEditors(translation: TranslationData): string[] {
  const lastAt = new Map<string, string>();
  const note = (editedBy?: { name: string; at?: string }) => {
    if (!editedBy?.name) return;
    const at = editedBy.at ?? '';
    const prev = lastAt.get(editedBy.name);
    if (prev === undefined || at.localeCompare(prev) > 0) {
      lastAt.set(editedBy.name, at);
    }
  };
  for (const phrase of translation.phrases) note(phrase.editedBy);
  for (const parent of translation.parentPhrases ?? []) note(parent.editedBy);
  return [...lastAt.entries()]
    .sort((a, b) => b[1].localeCompare(a[1]))
    .map(([name]) => name);
}

export const TranslationReviewWorkspace: React.FC<TranslationReviewWorkspaceProps> =
  observer(function TranslationReviewWorkspace({
    language,
    videoCreator,
    supportedLanguages,
    zoomLevel,
    zIndex: zIndexProp,
    timeRange,
    poster,
    liftOverlays = false,
    layout = 'modal',
    initialViewMode = 'segment',
    onViewModeChange,
    story,
    sessionStore,
    clip,
    clipKind,
    enableDubMonitor = false,
    onRequestDub,
    transcriptScope,
    onRequestReDubSelection,
    onDubUpdated,
    landedRedub,
    pendingHandoff = null,
    onHandoffConsumed,
    initialAudioMode = 'original',
    dubMusicBedUrl = null,
    dubVoiceStemUrl = null,
    initialMusicMode = 'none',
    clipVoiceVolume = FULL_DUB_LEVEL,
    clipOriginalVolume = FULL_DUB_LEVEL,
    clipSegmentLevels,
    clipLeadIn,
    onApplyToClip,
    musicSeparation,
    captionFallbackTemplate,
    captionTemplates,
    resolveCaptionTemplateName,
    isPresetCaptionTemplate,
    storyCaptions,
    initialRightTab = 'changes',
    initialReviewMode,
    initialReferenceLanguage = null,
    onViewStateChange,
    onClose,
  }) {
    // Story entry passes no z. Modal: the workspace's own resting z. Page: under
    // the app nav — unless the host is above a modal stack (the clip entry) and
    // says so.
    const zIndex =
      zIndexProp ?? (layout === 'page' ? REVIEW_PANEL_Z : REVIEW_WORKSPACE_Z);
    const translationStore = useTranslationStore();
    // PLAT-10464 Atom 1 — source the transcript array ONCE here (the sole parent)
    // and pass it to `AlignedBlocksView` as the `transcriptElements` prop. Read in
    // the render body so this `observer` tracks late-loading transcript changes;
    // the returned value is already a cached stable ref (clip proxy on the clip
    // path, `observable.ref` on the whole-story path).
    // Same value on both clip and whole-story paths — no per-path branch here.
    const transcriptionStore = useTranscription();
    const sourcedArray = transcriptionStore.getFinalTranscriptionElements();
    // Source-agnostic playback state, declared at the top level (no clip needed)
    // so `playbackState` stays reachable here for Step 4's transcript sync. The
    // clip path's `ReviewClipPlayer` drives these callbacks from its renderer.
    const playback = usePlaybackState();
    // PLAT-11657 — the clip preview's transport bar under the Dubbing player.
    // Either player reports the same verbs (`ExternalPlayerControls`); the
    // story entry's scrub state lives here because its clock is the shared
    // MobX one (`videoCreator.time`), not `playback`'s.
    const [playerControls, setPlayerControls] =
      useState<ExternalPlayerControls | null>(null);
    const [storySeekTime, setStorySeekTime] = useState<number | null>(null);
    const [storySeeking, setStorySeeking] = useState(false);
    const [isPlayerFullscreen, setIsPlayerFullscreen] = useState(false);
    // Clip word-click seek: `AlignedBlocksView` (which holds the montage time
    // map) hands us a MONTAGE-clock time; we bump a nonce so repeat clicks on the
    // same word re-fire, and `ReviewClipPlayer` applies it to its own renderer.
    const [pendingMontageSeek, setPendingMontageSeek] = useState<{
      montageTime: number;
      nonce: number;
    } | null>(null);
    // PLAT-10464: the clip renderer opens paused and reports montage time only
    // once its editor is ready. `ReviewClipPlayer` flips this then; until it is
    // true the highlight is suppressed (the elements' montage-clock `ts`/`end_ts`
    // — read straight from the sourced array — would otherwise be compared
    // against a not-yet-valid `currentTime`).
    const [montageReady, setMontageReady] = useState(false);
    const translation = translationStore.getTranslation(language);
    // Live count of changed entries for the "Changes (N)" tab badge — computed
    // here (not in the panel) so it stays accurate even when the Changes tab is
    // not the mounted one. Reactive: reads the same store data the panel lists.
    const changeCount = countActiveChanges(
      translationStore.getSegments(language),
      translation?.phrases ?? [],
    );

    // PLAT-10266 (Card 1) — EDITABLE ORIGINAL column. On the whole-story host the
    // review mounts INSIDE `createClipRootProxy`, so `useEditor()` here resolves
    // the review-SCOPED `ClipPreviewEditorStore` PROXY (a whole-story-scoped
    // editor from `adapter.getScopedEditor()`), NOT the story `EditorStore`
    // (DESIGN §0.1 / C13; the F2 correction). On the clip host it resolves the
    // clip-scoped `ClipPreviewEditorStore` (the montage editor) — wrong (clip)
    // transcript — so the affordance is gated OFF there via `!clip` (DESIGN §4).
    // Also under `enableTranscriptEditor` for surface consistency, and null-guarded
    // on `editor` regardless.
    const editorStore = useEditor();
    const datoClientStore = useDatoClient();
    const featureFlags = useFeatureFlags();
    // Story-level transcript editing is locked on every surface unless the
    // `enableStoryTranscriptEditing` flag is on, this pane included — its
    // "Replace Term" runs the same story-level replace command as the
    // Transcript & Speakers tab. See `storyTranscriptEditLock.ts`.
    const editableOriginalEnabled =
      !isStoryTranscriptEditingLocked(featureFlags) &&
      !clip &&
      featureFlags.enableTranscriptEditor === true &&
      editorStore.editor != null;
    // Reuse the shipped "Replace Term" chain unchanged — only `handleTranscriptTextReplace`
    // is needed here. It reads array positions in the SAME `sourcedArray` we feed
    // the resolver + modal (`convertIndexToSegmentBoundaries` → `transcriptElements[startIndex]`).
    const { handleTranscriptTextReplace } = useTranscriptOperations(
      videoCreator,
      editorStore,
      datoClientStore,
      undefined,
      undefined,
      sourcedArray,
      false,
      [],
      undefined,
      false,
    );
    // In-session source-text baseline (DESIGN §6, option A — PURE, no store
    // mutation). Captured once when the transcript first loads; the staleness
    // selector compares live source text against it. An unresolved stale flag is
    // session-only (Denys-approved); a reopened workspace re-baselines.
    const sourceBaselineRef = useRef<ReturnType<
      typeof captureSourceBaseline
    > | null>(null);
    if (!sourceBaselineRef.current && sourcedArray.length > 0) {
      sourceBaselineRef.current = captureSourceBaseline(sourcedArray);
    }
    // Per stale translated-fragment lead, its edit-keyed SIGNATURE (what makes it
    // stale). Recomputes only when the sourced array or the reviewed translation
    // changes (both stable refs), so an original-column Replace — which syncs
    // synchronously to the scoped store (DESIGN §0.1/C3) — lights the overlapping
    // fragment amber immediately.
    const staleSignatures = useMemo<
      ReadonlyMap<number, string> | undefined
    >(() => {
      if (!editableOriginalEnabled || !sourceBaselineRef.current) {
        return undefined;
      }
      return computeStaleSignatures(
        sourceBaselineRef.current,
        sourcedArray,
        translation?.phrases ?? [],
      );
    }, [editableOriginalEnabled, sourcedArray, translation]);
    // Keep the latest signatures readable from the resolve callbacks (which are
    // memoized on stable deps) WITHOUT stale closures — the resolve records the
    // CURRENT signature (updated during render, the same idiom as the baseline ref).
    const staleSignaturesRef = useRef<ReadonlyMap<number, string> | undefined>(
      undefined,
    );
    staleSignaturesRef.current = staleSignatures;

    // PLAT-10266 (Card 5, F1) — session-only RESOLVED stale SIGNATURES (lead → the
    // signature that was resolved). Resolving (machine or manual) does NOT un-edit
    // the SOURCE word, so the selector KEEPS flagging that lead (it flags by the
    // source's edit STATE, DESIGN §6). Suppression is therefore EDIT-KEYED: a
    // resolved fragment is hidden ONLY while its covered span's signature is
    // UNCHANGED since the resolve; a fresh source edit changes the signature and
    // re-flags amber. Recorded ONLY when the lead is currently stale, so a normal
    // span-confirm on a not-stale fragment never pre-suppresses a future first edit
    // (F1 R2 + R3). Session only (Denys-approved, R4): an UNRESOLVED flag need not
    // survive a reload; the RESOLVED translation persists via the existing save
    // (AC5, Card 4's reload proof — not this card's).
    const [resolvedStaleSignatures, setResolvedStaleSignatures] = useState<
      ReadonlyMap<number, string>
    >(() => new Map());
    const visibleStaleLeads = useMemo(
      () =>
        visibleStaleLeadsFromSignatures(
          staleSignatures,
          resolvedStaleSignatures,
        ),
      [staleSignatures, resolvedStaleSignatures],
    );

    // Original-column selection → "Replace Term" affordance state. The menu holds
    // the ARRAY-POSITION selection (already index-translated); the modal reuses it.
    const [originalMenu, setOriginalMenu] = useState<{
      positionXY: { x: number; y: number };
      selection: { startIndex: number; endIndex: number };
    } | null>(null);
    const [originalReplace, setOriginalReplace] = useState<{
      startIndex: number;
      endIndex: number;
    } | null>(null);

    // PLAT-10850 (E13 / DS5) — load the workspace glossary entries ONCE here and
    // share the id→entry map with both TM consumers: the aligned view (underlines
    // only the matched term) and the "TM" panel (labels each fragment's term).
    // Best-effort: resolve the showcase the way the Settings tab does
    // (`organization?.id`); a missing showcase / load failure leaves the map
    // empty (no term underline, generic panel labels — the highlight + list
    // still render). Built inline (this component is an `observer`) so the async
    // entry fill reflects without a stale memo.
    const videoCreatorStore = useVideoCreatorStore();
    // PLAT-10852 — the same flag that gates the Settings ▸ Translation Memory
    // tab gates the promote action here (nothing should create entries the user
    // can't then manage). The TM highlight / panel stay ungated: they only
    // appear when a fragment actually carries provenance.
    const { enableTranslationMemory, enableAutoRedubOnEdit } =
      useFlagsCombination(useUserIdentity().currentRole);
    const tmEntryStore = useTranslationMemoryEntryStore();
    const tmCollectionStore = useTranslationMemoryStore();
    const datoPerson = useDatoPerson();
    const tmShowcaseId = videoCreatorStore.organization?.id;
    useEffect(() => {
      if (tmShowcaseId) {
        void tmEntryStore.loadForShowcase(tmShowcaseId);
        // PLAT-10878 — collections resolve the entry's collection name in the
        // provenance "Details" view and feed the inline edit modal's picker.
        void tmCollectionStore.loadForShowcase(tmShowcaseId);
      }
    }, [tmShowcaseId, tmEntryStore, tmCollectionStore]);
    const tmEntriesById = new Map<string, TranslationMemoryEntry>();
    if (tmShowcaseId) {
      for (const e of tmEntryStore.getState(tmShowcaseId).entries) {
        tmEntriesById.set(e.id, e);
      }
    }
    // PLAT-10878 — provenance drill-in + inline entry edit. Collection names for
    // the Details view; supported languages / default source / collection options
    // for the reused entry editor; the person for "last updated by" stamping.
    const tmCollections = tmShowcaseId
      ? tmCollectionStore.getState(tmShowcaseId).collections
      : [];
    const tmCollectionNameById = new Map<string, string>();
    for (const c of tmCollections) tmCollectionNameById.set(c.id, c.name);
    const tmCollectionOptions: CollectionOption[] = tmCollections.map(c => ({
      id: c.id,
      name: c.name,
    }));
    const tmLanguages =
      videoCreatorStore.organization?.supportedLanguages ?? [];
    const tmDefaultLanguage = videoCreatorStore.organization?.defaultLanguage;
    const tmPrimarySourceLanguageId = tmDefaultLanguage
      ? (tmLanguages.find(
          l => l.value?.toLowerCase() === tmDefaultLanguage.toLowerCase(),
        )?.id ?? null)
      : null;
    const tmAuthor = datoPerson.person?.id
      ? { id: datoPerson.person.id, name: datoPerson.person.name }
      : null;
    const [editingTmEntry, setEditingTmEntry] =
      useState<TranslationMemoryEntry | null>(null);
    // Both entry-editor submits (PLAT-10878 edit, PLAT-10852 promote) report
    // the same way: `null` closes the modal, a string shows inline.
    const tmSubmitMessage = (result: Result<unknown>) =>
      result.ok ? null : result.error.message || 'Failed to save entry.';
    // Shared analytics shape for the entry events (PLAT-10853) — codes and
    // booleans only, never the glossary text. Mirrors the Settings surface so
    // edits from either place land in one stream, sliced by `surface`.
    const tmEntryEventProps = (input: TranslationMemoryEntryInput) => ({
      showcase_id: tmShowcaseId,
      collection_id: input.collectionId,
      source_lang: input.sourceLanguage.value ?? undefined,
      target_lang: input.translationLanguage?.value ?? null,
      is_dnt: input.translation === input.sourceTerm,
    });
    const handleUpdateTmEntry = async (
      input: TranslationMemoryEntryInput,
    ): Promise<string | null> => {
      if (!tmShowcaseId || !editingTmEntry) return 'No workspace selected.';
      const previousCollectionId = editingTmEntry.collectionId;
      // On success the store mutates the cached entries in-place, so this
      // observer re-renders and the card's resolved provenance updates without
      // a reload (same contract as the Settings entry table).
      const result = await tmEntryStore.updateEntry(
        tmShowcaseId,
        editingTmEntry.id,
        input,
        tmAuthor,
      );
      if (result.ok) {
        trackTmEntryUpdated({
          ...tmEntryEventProps(input),
          has_comment: !!input.comment?.trim(),
          surface: 'translation_review',
        });
        if (previousCollectionId !== input.collectionId) {
          trackTmEntryMoved({
            showcase_id: tmShowcaseId,
            from_collection_id: previousCollectionId,
            to_collection_id: input.collectionId,
            surface: 'translation_review',
          });
        }
      }
      return tmSubmitMessage(result);
    };
    // PLAT-10852 (E15) — promote a manual translation into a TM entry. The
    // overlay hands up the fragment's source text + the reviewer's current
    // translation; we seed the SAME entry editor (create mode) so they can trim
    // the sentence down to the actual term and pick a collection before it is
    // written. Gated on the Settings flag: with the Translation Memory tab
    // hidden there'd be no way to manage what this creates.
    const [promotingTm, setPromotingTm] = useState<{
      fragmentKey: string;
      sourceTerm: string;
      translation: string;
    } | null>(null);
    // Fragment → entry id for promotions made in THIS session (PLAT-10852). The
    // backend only stamps `tmEntryIds` on a re-translate, so without this the
    // overlay and the cards would keep offering "Save to Translation Memory" for
    // a fragment the reviewer just saved. Every surface reads it through
    // `resolveFragmentTmEntry` so they agree on what counts as "is an entry".
    const [promotedTmEntryIds, setPromotedTmEntryIds] = useState<
      ReadonlyMap<string, string>
    >(new Map());
    // Keys are source indices, which mean different fragments per target
    // language, and an entry is itself language-pair-specific. Both entry
    // points pass `language` as a plain prop (no remount on switch — see
    // `usePhraseEditing`, which resets for the same reason), so without this a
    // term promoted while reviewing Spanish would surface as "already an entry"
    // on the German review of the same story.
    useEffect(() => setPromotedTmEntryIds(new Map()), [language]);
    // Live count of TM-backed fragments for the "TM (N)" tab badge — computed
    // here (like `changeCount`) so it stays accurate when the TM tab is not the
    // mounted one. Reactive: reads the same store data the panel lists, through
    // the same session-promote rule, so the badge and the list can't disagree.
    const tmCount = countTmBackedFragments(
      translationStore.getSegments(language),
      promotedTmEntryIds,
    );
    // Resolve the run's language pair to the workspace's `language` records via
    // the shared BCP-47 matcher (PLAT-10900), NOT an exact-string find: a review
    // of `es-MX` against a workspace that stocks `es-ES` must still prefill
    // Spanish. An exact-only match would miss and leave the editor's target on
    // the empty option — which reads as "applies to every target language" (E4),
    // silently widening a promoted term into a global rule.
    const resolveTmLanguageId = (value: string | undefined | null) =>
      value
        ? (resolveDetectedLanguage(value, tmLanguages, [
            tmPrimarySourceLanguageId,
          ])?.id ?? null)
        : null;
    const canPromoteToTm = enableTranslationMemory && !!tmShowcaseId;
    const handlePromoteToTm = async (
      input: TranslationMemoryEntryInput,
    ): Promise<string | null> => {
      if (!tmShowcaseId) return 'No workspace selected.';
      const result = await tmEntryStore.createEntry(
        tmShowcaseId,
        input,
        tmAuthor,
        { origin: 'promoted' },
      );
      // A promote is the only way an entry is born here — it fires its own
      // event, never `_entry_created`, so the two don't double count.
      if (result.ok) {
        trackTmPromotedFromManual(tmEntryEventProps(input));
        // Remember which fragment this entry came from, so the overlay and the
        // cards immediately show the "Translation Memory Entry" link instead of
        // offering to promote the same text again.
        const fragmentKey = promotingTm?.fragmentKey;
        const entryId = result.value.id;
        if (fragmentKey) {
          setPromotedTmEntryIds(prev =>
            new Map(prev).set(fragmentKey, entryId),
          );
          // …and stamp the link onto the fragment itself so it OUTLIVES this
          // session (PLAT-10852): the map above dies on refresh, and the
          // backend only stamps `tmEntryIds` on a re-translate, so without this
          // a reload made every surface offer to promote the same term again.
          // `phrasesForPromoteKey` resolves the unit the way the promoting
          // surface keyed it (a fragment, or every derived fragment of an
          // authored override span). Read the translation FRESH — an edit may
          // have replaced the phrases array since this render. Fire-and-forget:
          // the entry itself is already saved, and the store logs a failed link
          // save (surfacing it here would read as "the entry failed").
          const current = translationStore.getTranslation(language);
          const linked = phrasesForPromoteKey(
            fragmentKey,
            current?.phrases,
            current?.parentPhrases,
          ).map(p => p.originalIndices);
          void translationStore.attachTmEntryToPhrases(
            language,
            linked,
            entryId,
          );
        }
      }
      return tmSubmitMessage(result);
    };
    // Opening an entry from any surface reuses the PLAT-10878 edit modal.
    const handleViewTmEntry = (entry: TranslationMemoryEntry) =>
      setEditingTmEntry(entry);
    const label =
      supportedLanguages.find(l => l.value === language)?.caption ??
      formatLanguageCaption(language);
    // The subject being reviewed (DialogSubtitle semantics): the clip entry
    // passes `story` explicitly; the Content Studio entry resolves it off the
    // scoped creator. Absent (still loading) ⇒ the header shows the action only.
    const storyTitle = story?.title ?? videoCreator.story?.title;

    const [rightTab, setRightTab] = useState<RightTab>(initialRightTab);

    // PLAT-10258 (Atom 4) — whole-translation directed re-translate entry point.
    // Header action toggles this; the DirectedRetranslateModal orchestrates the
    // rebase on the SAME whole-story translationStore this workspace already
    // holds. Default closed. Lives here so it inherits `enableTranslationReview`
    // (D9 containment — no new flag).
    const [isRetranslateOpen, setIsRetranslateOpen] = useState(false);
    // PLAT-10600 (T12) — the review's transient Original/Dubbed/Both audio mode.
    // Seeded from `initialAudioMode` (the originating surface's mode) on open
    // and never persisted. On the whole-story entry `useDubPlayback` composes
    // the dub over the shared clock and is a no-op unless `active`; the compact
    // player below is muted only in Dubbed mode, so `both` leaves the original
    // audible under the dub (the overlay). On the clip entry (PLAT-11340) the
    // same mode is handed to `ReviewClipPlayer`, whose own renderer composites
    // the dub — so the overlay must stay off there or the dub plays twice.
    // PLAT-11381 — the host now DECLARES this (`transcriptScope`). The old
    // inference stays as the fallback for a host that has not been updated:
    // the clip entry is the only one that hands in both a clip and its edit
    // session. Everything that used to read the inference reads this one
    // value, so there is a single answer to "story or clip" on this surface.
    const isClipPath = transcriptScope
      ? transcriptScope === 'clip'
      : Boolean(sessionStore && clip);
    const dubMonitorEnabled = enableDubMonitor;
    // The mode `reviewMode` below opens on — needed here because opening on
    // Dubbing also decides the Voice and Music the review starts with.
    const openingReviewMode: ReviewMode =
      initialReviewMode ?? (dubMonitorEnabled ? 'dubbing' : 'translation');
    // Where Dubbing Review puts Voice and Music, whichever way it is entered:
    // opening on it, switching into it, or a dub landing under it. Voice is
    // the dub — on Both when the clip is on Both (PLAT-11927: the review keeps
    // that choice on the clip, so it comes back to it), on Dubbed otherwise.
    // Music is the story's bed under the dub when there is one to play, for a
    // clip never dubbed; a clip with a saved dub keeps its own Music, since
    // the review saves that choice. A bed that lands later is picked up by the
    // effect beside `handleReviewModeChange`.
    const dubbingVoice: AudioMode =
      initialAudioMode === 'both' ? 'both' : 'dubbed';
    const dubbingMusic: DubMusicMode =
      dubMusicBedUrl && initialAudioMode === 'original'
        ? 'original'
        : initialMusicMode;
    // With no dub to monitor there is only the original to hear (PLAT-11704):
    // whatever the host was playing, this opens on Original, so a language
    // that has just lost its dub never arrives here muted.
    const [audioMode, setAudioMode] = useState<AudioMode>(
      !dubMonitorEnabled
        ? 'original'
        : openingReviewMode === 'dubbing'
          ? dubbingVoice
          : initialAudioMode,
    );
    const [musicMode, setMusicMode] = useState<DubMusicMode>(
      openingReviewMode === 'dubbing' ? dubbingMusic : initialMusicMode,
    );
    // Translation vs. Dubbing Review (PLAT-11649, see `handleReviewModeChange`).
    // Declared up here because the dub-landing seed below reads it.
    const [reviewMode, setReviewMode] = useState<ReviewMode>(openingReviewMode);
    // PLAT-11685 — a dub generated from inside the review lands as a host prop
    // change: the language gains a dub and the host flips itself to Dubbed. The
    // Voice picker appears for the first time on that render, so it must appear
    // on what the host is playing rather than on the Original this opened with.
    // Re-seeded on that one false→true transition, so a reviewer's own later
    // choice still stands. Adjusted during render (not in an effect) so the
    // picker never paints the stale mode first.
    // The reverse transition — the dub goes away under an open review — drops
    // the mode to Original for the same reason the seed above does.
    //
    // On the Dubbing tab the dub landing puts the review where entering
    // Dubbing Review does (`dubbingVoice` / `dubbingMusic`) — not on the
    // host's mode as such: the clip entry still reports Original on the render
    // the dub lands.
    //
    // The previous value is held in STATE, not a ref: StrictMode renders twice
    // and commits the second pass, so a ref flipped on the first pass makes
    // the second skip this block and drops the setAudioMode it queued.
    const [hadDubMonitor, setHadDubMonitor] = useState(dubMonitorEnabled);
    if (dubMonitorEnabled !== hadDubMonitor) {
      setHadDubMonitor(dubMonitorEnabled);
      if (dubMonitorEnabled && reviewMode === 'dubbing') {
        if (audioMode !== dubbingVoice) setAudioMode(dubbingVoice);
        if (musicMode !== dubbingMusic) setMusicMode(dubbingMusic);
      } else if (dubMonitorEnabled && initialAudioMode !== audioMode) {
        setAudioMode(initialAudioMode);
      } else if (!dubMonitorEnabled && audioMode !== 'original') {
        setAudioMode('original');
      }
    }
    const handleAudioModeChange = useCallback(
      (mode: AudioMode) => {
        setAudioMode(mode);
        // PLAT-11927 — on a clip the Voice choice is kept: leaving the review
        // returns to the clip playing what was last chosen here. With the
        // Music it is heard with, which may be the review's unsaved default.
        if (isClipPath) onApplyToClip?.({ voiceMode: mode, musicMode });
        trackDubReviewModeToggled({
          story_id: videoCreator.storyId ?? undefined,
          language,
          mode,
        });
      },
      [isClipPath, onApplyToClip, musicMode, videoCreator.storyId, language],
    );
    // PLAT-11899 / PLAT-11927 — the review's own levels, from the clip's; like
    // Voice and Music they change nothing on the clip until the reviewer
    // applies them.
    const appliedLevels = useMemo<DubLevels>(
      () => ({
        voiceVolume: clipVoiceVolume,
        originalVolume: clipOriginalVolume,
      }),
      [clipVoiceVolume, clipOriginalVolume],
    );
    const [levels, setLevels] = useState<DubLevels>(appliedLevels);
    const { voiceVolume, originalVolume } = levels;
    // Apply to Clip saves the levels with the Voice and Music they were heard
    // under, which may be the review's unsaved defaults.
    // The original lead-in on Both: heard as it is set, like the levels, and
    // kept on the clip by the same Apply to Clip.
    const [leadIn, setLeadIn] = useState<DubLeadIn | undefined>(clipLeadIn);
    const applyLevels = useCallback(
      (applied: Partial<DubLevels>, appliedLeadIn?: DubLeadIn | null) =>
        onApplyToClip?.({
          voiceMode: audioMode,
          musicMode,
          levels: applied,
          leadIn: appliedLeadIn,
        }),
      [onApplyToClip, audioMode, musicMode],
    );
    // PLAT-11665 — Music under the dub. Heard under Dubbed, and on a clip
    // under Both too (PLAT-12244).
    // PLAT-11927 — on a clip the Music choice is kept, like the Voice choice.
    const applyMusicMode = useCallback(
      (mode: DubMusicMode) => {
        setMusicMode(mode);
        if (isClipPath)
          onApplyToClip?.({ voiceMode: audioMode, musicMode: mode });
      },
      [isClipPath, onApplyToClip, audioMode],
    );
    const applyMusicModeRef = useRef(applyMusicMode);
    applyMusicModeRef.current = applyMusicMode;
    const handleMusicModeChange = useCallback(
      (mode: DubMusicMode) => {
        applyMusicMode(mode);
        trackMusicModeToggled({
          story_id: videoCreator.storyId ?? undefined,
          language,
          mode,
        });
      },
      [applyMusicMode, videoCreator.storyId, language],
    );
    // PLAT-11678 — bumped when an automatic re-dub lands, so the composed
    // dub buffer (story entry) / the clip player's dub description (clip
    // entry) are read again and the next play carries the new words.
    const [dubReloadToken, setDubReloadToken] = useState(0);
    // PLAT-11683 — Captions over the player, on by default, in BOTH modes.
    // Transient like the voice and music choices, and DISPLAY ONLY: the review
    // never writes captions to a clip or the story (PLAT-11702 — the reviewer is
    // a translator, not the clip's creator). What it shows: the video's own
    // captions when it has them, else the showcase's caption template; never
    // the built-in default.
    const [captionsMode, setCaptionsMode] = useState<CaptionsMode>('on');
    // What the clip's own editor had when the review opened (`null` until the
    // review player reports it, and on the story entry). Drives the Preview tag
    // and the "No captions set up" state.
    const [clipHadCaptionsAtOpen, setClipHadCaptionsAtOpen] = useState<
      boolean | null
    >(null);
    // The template the clip's own captions came from, so the dropdown can name
    // that row after it. `null` ⇒ none, or one the showcase no longer lists.
    const [clipCaptionTemplateAtOpen, setClipCaptionTemplateAtOpen] = useState<
      string | null
    >(null);
    const handleCaptionTrackAtOpen = useCallback(
      (hadTrack: boolean, templateId?: string | null) => {
        setClipHadCaptionsAtOpen(hadTrack);
        setClipCaptionTemplateAtOpen(templateId ?? null);
      },
      [],
    );
    const handleCaptionsModeChange = useCallback(
      (mode: CaptionsMode) => {
        setCaptionsMode(mode);
        trackTranslationReview_CaptionsModeChanged({
          story_id: videoCreator.storyId ?? undefined,
          language,
          mode,
          entry: isClipPath ? 'clip' : 'story',
        });
      },
      [videoCreator.storyId, language, isClipPath],
    );
    // PLAT-11702 follow-up — the showcase caption template the reviewer picked
    // for this session. `null` ⇒ no pick; the review draws the showcase's most
    // recently used (the head of `captionTemplates`), exactly as before. Never
    // persisted: closing the review forgets it, like Voice and Music.
    const [captionTemplateId, setCaptionTemplateId] = useState<string | null>(
      null,
    );
    const handleCaptionTemplateChange = useCallback(
      // `null` ⇒ back to the video's own style, dropping the preview.
      (templateId: string | null) => {
        setCaptionTemplateId(templateId);
        trackTranslationReview_CaptionsModeChanged({
          story_id: videoCreator.storyId ?? undefined,
          language,
          mode: 'on',
          entry: isClipPath ? 'clip' : 'story',
          ...(templateId ? { template_id: templateId } : {}),
        });
      },
      [videoCreator.storyId, language, isClipPath],
    );
    // Only a deliberate pick forces a style onto the review's editor; with no
    // pick the existing seed path runs untouched. Memoised on the host's own
    // memoised list, so the effects below don't see a fresh object per render
    // (which would re-produce the karaoke track on every render).
    const pickedCaptionTemplate = useMemo(() => {
      if (!captionTemplateId) return null;
      const picked = (captionTemplates ?? []).find(
        template => template.templateId === captionTemplateId,
      );
      return picked
        ? { config: picked.config, templateId: picked.templateId }
        : null;
    }, [captionTemplateId, captionTemplates]);
    const storySavedStyle = storyCaptions?.savedStyle ?? null;
    // PLAT-11702 — nothing of the showcase's to show: the video has no captions
    // of its own and the showcase has no caption template. The picker says so
    // instead of drawing the built-in default. Unknown-yet (the clip player has
    // not reported) counts as available, so the row does not flash the notice.
    const captionsUnavailable = isClipPath
      ? clipHadCaptionsAtOpen === false && !captionFallbackTemplate
      : storyCaptions !== undefined &&
        storySavedStyle === null &&
        !captionFallbackTemplate;
    const captionsOnHint = 'Over the video, in the reviewed language';
    // What is on screen is the review's own render, not saved captions: always
    // on the story entry, on a clip that has none, and on a clip whose own
    // captions the reviewer has previewed a showcase template over (that pick
    // is never written either). Said next to the picker, so nobody signs off
    // on captions the video does not carry.
    const captionsPreviewReason =
      captionsMode !== 'on' || captionsUnavailable
        ? null
        : !isClipPath
          ? 'Review only — these captions aren’t saved to the story. Add captions in Content Studio or Creator Studio.'
          : clipHadCaptionsAtOpen === false
            ? 'Review only — this clip has no captions. Add captions in Content Studio.'
            : captionTemplateId !== null
              ? 'Review only — this caption style isn’t saved to the clip; it keeps its own.'
              : null;
    // PLAT-11702 follow-up — the showcase's templates are listed whenever it
    // has any, on both entries. Where the video has captions of its own they
    // lead the list and stay the default, so the reviewer sees what the video
    // actually carries first and the templates only as alternatives. Unknown
    // yet (the clip player has not reported, the story's captions have not
    // loaded) counts as "has its own", so the default row does not flip once
    // the answer arrives.
    const videoHasOwnCaptions = isClipPath
      ? clipHadCaptionsAtOpen !== false
      : storyCaptions === undefined || storySavedStyle !== null;
    // The row standing for the video's own captions, named after the template
    // they came from wherever the showcase can name it — the picker prefers a
    // listed template's own title (and then lists it only once); this covers
    // the ids it does not carry. The generic label is the last resort: a
    // template since deleted.
    const ownCaptionTemplateId = isClipPath
      ? clipCaptionTemplateAtOpen
      : (storySavedStyle?.parent_template_id ?? null);
    const reviewOwnCaptionStyle = {
      templateId: ownCaptionTemplateId,
      label:
        (ownCaptionTemplateId
          ? resolveCaptionTemplateName?.(ownCaptionTemplateId)
          : undefined) ?? (isClipPath ? 'Clip’s Style' : 'Story’s Style'),
      source: captionStyleSource(ownCaptionTemplateId, isPresetCaptionTemplate),
    };
    const reviewCaptionTemplateChoice = captionTemplates?.length
      ? {
          options: captionTemplates,
          selectedId: captionTemplateId,
          onSelect: handleCaptionTemplateChange,
          ownStyle: videoHasOwnCaptions ? reviewOwnCaptionStyle : null,
        }
      : undefined;
    // Story entry: the review's caption track lives on the host's
    // transcript-scoped editor (no renderer there), drawn by the overlay.
    // The cascade: the story's own captions' style → the showcase's caption
    // template → nothing (PLAT-11702); produced in the reviewed language,
    // review-only, and removed again when the review closes.
    const storyCaptionsEditor = storyCaptions?.editor ?? null;
    const storyCaptionsReady = !!storyCaptions?.isReady;
    const storyCaptionsRefresh = storyCaptions?.refreshKaraokeState;
    // PLAT-11683 — read the refresh through a ref so the effects below don't
    // list it as a dependency. Calling it reassigns the host editor's
    // observable `karaokeLines`, which re-renders the (observer) host; if that
    // host hands back a fresh `refreshKaraokeState` identity, depending on it
    // here would re-fire the effect → refresh → re-render, an unbounded loop
    // (the host stabilises the callback too, but this keeps the loop closed for
    // any caller).
    const storyCaptionsRefreshRef = useRef(storyCaptionsRefresh);
    storyCaptionsRefreshRef.current = storyCaptionsRefresh;
    useEffect(() => {
      if (isClipPath || !storyCaptionsReady || !storyCaptionsEditor) return;
      if (captionsMode === 'on') {
        ensureReviewCaptions(storyCaptionsEditor, {
          language,
          fallbackTemplate: captionFallbackTemplate,
          savedStyle: storySavedStyle,
          pickedTemplate: pickedCaptionTemplate,
        });
      } else {
        removeReviewCaptions(storyCaptionsEditor);
      }
      storyCaptionsRefreshRef.current?.();
    }, [
      isClipPath,
      storyCaptionsReady,
      storyCaptionsEditor,
      storySavedStyle,
      captionsMode,
      language,
      captionFallbackTemplate,
      pickedCaptionTemplate,
    ]);
    useEffect(() => {
      if (isClipPath || !storyCaptionsEditor) return undefined;
      return () => {
        // Leave the host's editor as the review found it.
        if (removeReviewCaptions(storyCaptionsEditor))
          storyCaptionsRefreshRef.current?.();
      };
    }, [isClipPath, storyCaptionsEditor]);
    // The caption fonts a showcase uploaded, so the overlay can use them.
    useLoadBrandKitFonts(useVideoCreatorStore());
    const storyDubActive =
      dubMonitorEnabled && !isClipPath && audioMode !== 'original';
    const { resolved: storyDub } = useDubPlayback({
      videoCreator,
      storyId: videoCreator.storyId ?? null,
      language,
      active: storyDubActive,
      reloadToken: dubReloadToken,
      musicBedUrl: dubMusicBedUrl,
      playMusicBed: audioMode === 'dubbed' && musicMode === 'original',
      voiceVolume,
      // PLAT-11906 — the captions and highlights below follow only the lines
      // the dub places, so the voice must not play a superseded copy either.
      placedLinesOnly: true,
    });
    // PLAT-11906 — the story entry plays the dub itself, so it tells the
    // caption editor which dub is heard: its captions then follow the voice,
    // under Both as under Dubbed. When the voice goes back to Original, the
    // dub description changes, or the review closes, the editor is put back
    // as the review found it.
    useEffect(() => {
      if (!storyCaptionsEditor || !storyDubActive || !storyDub) {
        return undefined;
      }
      followReviewDub(storyCaptionsEditor, language, storyDub);
      storyCaptionsRefreshRef.current?.();
      return () => {
        stopFollowingReviewDub(storyCaptionsEditor, language);
        storyCaptionsRefreshRef.current?.();
      };
    }, [storyCaptionsEditor, storyDubActive, storyDub, language]);
    // The same dub, on the same clock and stretch, for the transcript's
    // reading (the clip entry's comes from its own player's editor).
    const storyDubPlayhead = useMemo<DubOnReviewClock | null>(
      () =>
        storyDubActive && storyDub
          ? dubCaptionInputs({
              manifest: storyDub.manifest,
              renditionUrls: storyDub.renditionUrls,
              spans: storyDubStretch(storyDub),
            })
          : null,
      [storyDubActive, storyDub],
    );
    const [clipDubPlayhead, setClipDubPlayhead] =
      useState<DubOnReviewClock | null>(null);
    // Levels for the dubbed line under the playhead alone, saved with the
    // Voice and Music they were heard under, as Apply to Clip saves.
    const applyLineLevels = useCallback(
      (lines: DubSegmentLevels) =>
        onApplyToClip?.({
          voiceMode: audioMode,
          musicMode,
          segmentLevels: lines,
        }),
      [onApplyToClip, audioMode, musicMode],
    );
    const { segmentLevels, levelsLine } = useReviewLevelsLine({
      enabled: isClipPath && !!onApplyToClip,
      dub: clipDubPlayhead,
      currentTime: playback.playbackState.currentTime,
      levels,
      appliedLevels,
      appliedSegmentLevels: clipSegmentLevels,
      showOriginal: audioMode === 'both',
      onSave: applyLineLevels,
    });

    // PLAT-11678 — a saved translation edit re-dubs its segment, no click.
    // On when the flag says so AND the reviewed language has a dub (the same
    // "this language is dubbed" the audio pickers key on). The hook waits for
    // the persist to land and for the dub store to be idle, compares each
    // segment's words with its recording, and runs only what changed; the
    // commit paths below feed it through `handleSpanConfirmed` (inline field,
    // popover, span replace, directed re-translate) and the Changes panel's
    // revert.
    const dubbingStore = useDubbingStore();
    const fetchDubManifest = useDubManifestFetch();
    const autoRedubEnabled = enableAutoRedubOnEdit && dubMonitorEnabled;
    const autoRedub = useAutoRedubOnCommit({
      enabled: autoRedubEnabled,
      storyId: videoCreator.storyId ?? null,
      language,
      languageCaption: label,
      translation: translationStore,
      dubbing: dubbingStore,
      fetchManifest: fetchDubManifest,
      getElements: () => transcriptionStore.getFinalTranscriptionElements(),
      videoDurationSec: playerControls?.duration || undefined,
      onRunCompleted: () => {
        setDubReloadToken(token => token + 1);
        // PLAT-11768 — the surface the review opened over holds its own copy of
        // the dub. Left un-told, it keeps the take from before the edit: on the
        // story entry that is a second player on the same clock, and the edited
        // line is heard twice until the page is reloaded.
        onDubUpdated?.();
      },
    });
    // Read through a ref inside `handleSpanConfirmed`, whose dependency list
    // is deliberately narrow.
    const autoRedubRef = useRef(autoRedub);
    autoRedubRef.current = autoRedub;
    const dubUpdatingCount = useMemo(
      () =>
        [...autoRedub.stateBySegment.values()].filter(
          state => state.status === 'updating',
        ).length,
      [autoRedub.stateBySegment],
    );

    // PLAT-12034 — the dub as recorded, for the lines' delivery markers.
    // Read on the Dubbing tab whatever the Voice picker says and on either
    // entry (the player's own copy is held only while a dub plays, and never
    // on the clip entry), and again whenever a run lands.
    const [dubManifest, setDubManifest] = useState<DubManifestDTO | null>(null);
    const chipsEnabled = reviewMode === 'dubbing' && dubMonitorEnabled;
    useEffect(() => {
      const storyId = videoCreator.storyId;
      if (!chipsEnabled || !storyId) {
        setDubManifest(null);
        return undefined;
      }
      let cancelled = false;
      void fetchDubManifest({ storyId, language })
        .then(resolved => {
          if (!cancelled) setDubManifest(resolved?.manifest ?? null);
        })
        .catch(() => {
          if (!cancelled) setDubManifest(null);
        });
      return () => {
        cancelled = true;
      };
    }, [
      chipsEnabled,
      videoCreator.storyId,
      language,
      fetchDubManifest,
      dubReloadToken,
    ]);
    const dubMarkersByLine = useMemo(
      () => markersByLine(dubManifest),
      [dubManifest],
    );
    const handleMarkerRemove = useCallback(
      (lineId: string, taggedText: string | null) => {
        // Removing a chip is a re-dub, so it goes only where re-dubs run.
        if (!autoRedubEnabled) return;
        const line = dubMarkersByLine.get(lineId);
        trackTranslationReview_MarkerChipRemoved({
          story_id: videoCreator.storyId ?? undefined,
          language,
          tag: line?.markers.map(m => m.tag).join(',') ?? '',
          markers_left: taggedText
            ? (taggedText.match(/\[[^\]]*\]/g) ?? []).length
            : 0,
        });
        autoRedubRef.current.redubLine(
          lineId,
          taggedText ? { kind: 'tags', taggedText } : null,
        );
      },
      [autoRedubEnabled, dubMarkersByLine, language, videoCreator.storyId],
    );
    // Edit Markers… opens the line's markers editor: the Redub Selection
    // popover (PLAT-12022) on that one line, which seeds from the stored
    // markup. Through the same host hand-off a selection uses.
    const handleMarkerEdit = useCallback(
      (lineId: string, anchor: HTMLElement) => {
        if (!onRequestReDubSelection) return;
        const line = dubMarkersByLine.get(lineId);
        const segment = dubManifest?.segments.find(s => s.segmentId === lineId);
        trackTranslationReview_MarkerChipEditRequested({
          story_id: videoCreator.storyId ?? undefined,
          language,
          tag: line?.markers.map(m => m.tag).join(',') ?? '',
        });
        onRequestReDubSelection(language, {
          segmentIds: [lineId],
          speakerLabels: segment ? [segment.speaker] : [],
          anchorElement: anchor,
          translatedText:
            line?.words ?? segment?.sourceTranslationSnapshot ?? '',
          sourceText: '',
        });
      },
      [
        onRequestReDubSelection,
        dubMarkersByLine,
        dubManifest,
        language,
        videoCreator.storyId,
      ],
    );

    // PLAT-11714 — the dub the reviewer started from this page.
    // `DubGenerateModal` closes as soon as it hands the request to the store,
    // on the premise that the page behind it reports the run; the review is not
    // one of the pages that did, so generating from here looked like nothing
    // had happened. Narrowed to the reviewed language: this header speaks for
    // one language, and a run for another belongs to the surface that started
    // it. Only the run's presence is read here — the half-second percent tick
    // lives inside `DubRunProgress`.
    //
    // PLAT-11741 — and the same run the modal and the `DubRunChip` read
    // (`DubbingStore.activeRun`, PLAT-11570), so the header agrees with them
    // and moves the moment Generate is pressed, with the modal open, closed,
    // or never seen from in here (a run started from the language menu counts
    // too). The hook is the one place the story and language narrowing lives.
    const activeDubRun = useActiveDubRun(language);
    const isDubGenerating = activeDubRun !== null;
    // A dub run completing on the Dubbing tab lands on the dub it made, where
    // entering the tab does (`dubbingVoice` / `dubbingMusic`). The
    // dub-landing seed above only covers a language gaining its FIRST dub; a
    // clip dubbed into a language the story already has never flips
    // `dubMonitorEnabled`, so it would stay on whatever voice it had. Only a
    // run a person started, and only one that succeeded: an automatic re-dub
    // of an edited line, or a run that failed or was cancelled, made no dub
    // to land on, so the reviewer's picks stand. With no dub to play at all
    // there is nothing to switch to.
    // Previous value in state, not a ref — see `hadDubMonitor` above.
    const completedDubRun = useLastCompletedDubRun(language);
    const [seenCompletedDubRun, setSeenCompletedDubRun] =
      useState(completedDubRun);
    if (seenCompletedDubRun !== completedDubRun) {
      setSeenCompletedDubRun(completedDubRun);
      if (
        completedDubRun &&
        !completedDubRun.automatic &&
        dubMonitorEnabled &&
        reviewMode === 'dubbing'
      ) {
        if (audioMode !== dubbingVoice) setAudioMode(dubbingVoice);
        if (musicMode !== dubbingMusic) setMusicMode(dubbingMusic);
      }
    }

    // PLAT-11792 — a dub started from in here is RUN by the host's popover, and
    // the host refreshes only its own audio when the run lands. Nothing told
    // this workspace, so its player went on holding the description it fetched
    // before the run — the previous take — and a page reload was the only way
    // to hear the new one. That covers every way a dub can be asked for from
    // here: the first dub, and the re-dub of a selection.
    //
    // The end of the run is already observable here. The store holds the one
    // run in flight and the read above narrows it to this story and this
    // language, so the moment it goes from running to not running is the moment
    // to read the dub again — the same re-read an automatic re-dub asks for.
    //
    // Every ending counts, cancelled and failed included. Re-reading a dub that
    // did not change costs one request and gives back the same description;
    // missing a re-read that was needed is silent.
    const dubRunWasActiveRef = useRef(false);
    useEffect(() => {
      if (isDubGenerating) {
        dubRunWasActiveRef.current = true;
        return;
      }
      if (!dubRunWasActiveRef.current) return;
      dubRunWasActiveRef.current = false;
      setDubReloadToken(token => token + 1);
    }, [isDubGenerating]);

    // PLAT-10287 — linked cross-language selection highlight. Opt-in here (D9):
    // the shared Transcript panes are untouched; this hook watches the selection
    // and paints the mirror words in the opposite pane, scoped per pane (#12).
    const originalPaneRef = useRef<HTMLElement>(null);
    const translatedPaneRef = useRef<HTMLElement>(null);
    /**
     * PLAT-11649 — Translation Review vs. Dubbing Review. Opens on Dubbing when
     * the host says the reviewed language has a dub (`enableDubMonitor` is
     * exactly "dubbing on AND this language is dubbed"), on Translation
     * otherwise. Deliberately NOT persisted or seeded from a prop the way
     * `viewMode` is — Dubbing hides the source column, and a remembered
     * "source hidden" is how someone signs off on a translation they never
     * compared (the PLAT-11633 rule). The default follows the dub instead.
     *
     * `videoExpanded` is the derived alias the PLAT-11633 layout was built on:
     * Dubbing Review IS the expanded-video layout, with rails. Declared here,
     * above the pane consumers: hiding the source column UNMOUNTS it, so
     * anything scoped to `originalPaneRef` keys its re-bind on this flag.
     */
    // (`reviewMode` state is declared above, beside the voice's and music's.)
    const videoExpanded = reviewMode === 'dubbing';
    const handleReviewModeChange = useCallback(
      (next: ReviewMode) => {
        if (next === reviewMode) return;
        setReviewMode(next);
        // Entering Dubbing Review puts the dub on, where opening on it does.
        if (next === 'dubbing' && dubMonitorEnabled) {
          setAudioMode(dubbingVoice);
          setMusicMode(dubbingMusic);
        }
        trackTranslationReview_ModeChanged({
          story_id: videoCreator.storyId ?? undefined,
          language,
          entry: isClipPath ? 'clip' : 'story',
          mode: next,
        });
      },
      [
        reviewMode,
        dubMonitorEnabled,
        dubbingVoice,
        dubbingMusic,
        videoCreator.storyId,
        language,
        isClipPath,
      ],
    );
    // PLAT-11714 — a separation started from this picker takes minutes, and the
    // choice was pinned to None the whole time because there was nothing to
    // choose. Land on From Original when the bed turns up, so the wait ends in
    // the music the reviewer asked for rather than in a second trip through the
    // menu. Only on the null → set edge, so a deliberate None is never undone.
    //
    // The same edge in Dubbing Review, which opens on From Original: a bed
    // that arrives after the review opened (an already-separated story's
    // summary landing from its mount read) is laid under the dub too. The
    // reviewer can't have picked None before then: the picker is inert
    // until there is a bed.
    const sawSeparationRef = useRef(false);
    if (musicSeparation?.separating) sawSeparationRef.current = true;
    const hadMusicBedRef = useRef(!!dubMusicBedUrl);
    useEffect(() => {
      const hasBed = !!dubMusicBedUrl;
      if (
        hasBed &&
        !hadMusicBedRef.current &&
        (sawSeparationRef.current ||
          (reviewMode === 'dubbing' && initialAudioMode === 'original'))
      ) {
        // A bed the reviewer asked for is kept on the clip like a pick
        // (PLAT-11927); Dubbing Review's own default is not.
        if (sawSeparationRef.current) applyMusicModeRef.current('original');
        else setMusicMode('original');
      }
      hadMusicBedRef.current = hasBed;
    }, [dubMusicBedUrl, reviewMode, initialAudioMode]);
    // Page layout: the panel's left edge follows the app sidebar, with the
    // same spring `MainContent` uses, so the two move as one.
    const isPage = layout === 'page';
    const { sidebarWidth } = useSidebarState();
    // PLAT-11661 — one three-column layout for both modes, the player always
    // at the top of the right column. Switching folds the ORIGINAL column away
    // and the right column takes the clip preview's stage width (PLAT-11657,
    // 705px), the translation getting what is left. Widths are measured
    // (framer cannot animate `fr`), and the fold clips a fixed-width inner
    // grid — the Story Manager folder tree's trick — so the translation text
    // holds still through the spring and reflows once, when the fold lands.
    // The original column unmounts only once the fold has landed, and mounts
    // before the unfold starts, so the reviewer never sees it pop.
    const bodyRef = useRef<HTMLDivElement>(null);
    const [bodyWidth, setBodyWidth] = useState<number | null>(null);
    useLayoutEffect(() => {
      const el = bodyRef.current;
      if (!el || typeof ResizeObserver === 'undefined') return;
      setBodyWidth(el.clientWidth);
      const ro = new ResizeObserver(entries => {
        const width = entries[0]?.contentRect.width;
        if (width) setBodyWidth(width);
      });
      ro.observe(el);
      return () => ro.disconnect();
    }, []);
    // Unmeasured (first paint, jsdom): no motion, the CSS defaults lay it out
    // and the original column follows the mode at once.
    const canAnimate = bodyWidth !== null && bodyWidth > 0;
    const contentWidth = (bodyWidth ?? 0) - BODY_GUTTER;
    const alignedOpenWidth = Math.max(0, contentWidth - RIGHT_COLUMN_DOCKED);
    const rightWidth = videoExpanded
      ? Math.max(
          RIGHT_COLUMN_DOCKED,
          Math.min(RIGHT_COLUMN_STAGE, contentWidth - MIN_TRANSLATION_WIDTH),
        )
      : RIGHT_COLUMN_DOCKED;
    const alignedWidth = videoExpanded
      ? Math.max(0, contentWidth - rightWidth)
      : alignedOpenWidth;
    const [originalHidden, setOriginalHidden] = useState(videoExpanded);
    useEffect(() => {
      // Unfold: the column comes back BEFORE the wrapper widens, so it is
      // revealed rather than popped in. Fold without motion: hide at once.
      if (!videoExpanded) setOriginalHidden(false);
      else if (!canAnimate) setOriginalHidden(true);
    }, [videoExpanded, canAnimate]);
    const reduceMotion = useReducedMotion();
    const layoutSpring = reduceMotion ? { duration: 0 } : SIDEBAR_SPRING;
    // The two widths are driven as motion values on `style`, not through the
    // `animate` prop: the widths exist only once the body is measured, and a
    // motion element mounted with `initial={false}` and no `animate` DROPS the
    // first target it is later given (framer treats it as the initial render),
    // so a review opened straight into Dubbing stayed on the docked 360px
    // column until the next mode switch (PLAT-11662). Here the first measured
    // layout — and any plain resize — snaps; only a mode change springs.
    const alignedWidthValue = useMotionValue(0);
    const rightWidthValue = useMotionValue(RIGHT_COLUMN_DOCKED);
    const laidOutModeRef = useRef<boolean | null>(null);
    useLayoutEffect(() => {
      if (!canAnimate) return;
      const modeChanged =
        laidOutModeRef.current !== null &&
        laidOutModeRef.current !== videoExpanded;
      laidOutModeRef.current = videoExpanded;
      if (!modeChanged) {
        alignedWidthValue.jump(alignedWidth);
        rightWidthValue.jump(rightWidth);
        if (videoExpanded) setOriginalHidden(true);
        return;
      }
      const controls = [
        animate(alignedWidthValue, alignedWidth, {
          ...layoutSpring,
          onComplete: () => {
            // Fold: the original column unmounts once the fold has landed.
            if (videoExpanded) setOriginalHidden(true);
          },
        }),
        animate(rightWidthValue, rightWidth, layoutSpring),
      ];
      return () => controls.forEach(c => c.stop());
      // `layoutSpring` is a stable module constant or `{ duration: 0 }`.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canAnimate, videoExpanded, alignedWidth, rightWidth]);
    // PLAT-10611 (F5) — the cross-language link phrases now carry BOTH key spaces:
    // `originalElementIds` (the source ids the phrase covers) and `leadUuid` (the
    // stable id of the element at the phrase's lead = `min(originalIndices)` — the
    // SAME value the aligned translated column stamps on `data-source-uuid` via
    // `elementByInitialIndex.get(lead)`, since a clip element carries its story id
    // in `source_uuid`). Threading these makes the link drift-correct; the integer
    // `originalIndices` is retained as the fallback. Active fragments only —
    // retired (D10 `active: false`) phrases aren't rendered.
    const crossLangPhrases = useMemo<LinkedPhrase[]>(() => {
      const elByIndex = new Map<
        number,
        { source_uuid?: string; id?: string }
      >();
      for (const el of sourcedArray) {
        if (el.initial_index >= 0) elByIndex.set(el.initial_index, el);
      }
      return (translation?.phrases ?? [])
        .filter(p => p.active !== false)
        .map(p => {
          let leadIdx = Infinity;
          for (const idx of p.originalIndices) if (idx < leadIdx) leadIdx = idx;
          const leadEl = Number.isFinite(leadIdx)
            ? elByIndex.get(leadIdx)
            : undefined;
          return {
            originalIndices: p.originalIndices,
            originalElementIds: p.originalElementIds,
            leadUuid: leadEl ? (leadEl.source_uuid ?? leadEl.id) : undefined,
          };
        });
    }, [translation, sourcedArray]);
    useLinkedCrossLanguageHighlight({
      // Off while the source column is hidden: there is no pane to mirror
      // between, and the hook's `recompute` bails on the missing pane BEFORE it
      // can clear — so disabling it is also what takes the last paint down (its
      // teardown clears both names).
      enabled: !originalHidden,
      originalPaneRef,
      translatedPaneRef,
      phrases: crossLangPhrases,
    });

    // PLAT-10266 (Card 1) — capture a right-click on the ORIGINAL column and open
    // the reused "Replace Term" menu over it. Scoped to `originalPaneRef` so the
    // duplicate `#transcript-scrollable-container` (CLAUDE.md #14) is never hit.
    // The column stamps `data-initial-index` / `data-source-uuid`, but the reused
    // chain consumes ARRAY POSITIONS — so translate the selection here (TOP GAP,
    // DESIGN §5) and drop it as a no-op when it can't be resolved.
    useEffect(() => {
      const pane = originalPaneRef.current;
      if (!pane || !editableOriginalEnabled) return;

      const wordRefFromNode = (node: Node | null): OriginalWordRef | null => {
        const host =
          node instanceof HTMLElement ? node : (node?.parentElement ?? null);
        // PLAT-10266 (Card 6) — match by the live array position FIRST (stamped on
        // every rendered word, added or not), falling back to the `initial_index`
        // stamp for surfaces that carry only it (e.g. the reference-language proxy
        // column). An ADDED word carries only `data-array-position`, so without
        // this it resolved to no word / -1 and the "Replace Term" menu was
        // suppressed — you could not re-edit an added/replaced word.
        const wordEl = host?.closest<HTMLElement>(
          '[data-array-position], [data-initial-index]',
        );
        if (!wordEl || !pane.contains(wordEl)) return null;
        const arrayPosAttr = wordEl.getAttribute('data-array-position');
        const arrayPosition =
          arrayPosAttr !== null ? Number(arrayPosAttr) : null;
        const hasArrayPos =
          arrayPosition !== null &&
          Number.isInteger(arrayPosition) &&
          arrayPosition >= 0;
        const initialIndexAttr = wordEl.getAttribute('data-initial-index');
        const initialIndex =
          initialIndexAttr !== null ? Number(initialIndexAttr) : NaN;
        // Need at least one resolvable handle.
        if (!hasArrayPos && !Number.isFinite(initialIndex)) return null;
        return {
          initialIndex: Number.isFinite(initialIndex) ? initialIndex : -1,
          sourceUuid: wordEl.getAttribute('data-source-uuid'),
          arrayPosition: hasArrayPos ? arrayPosition : null,
        };
      };

      const onContextMenu = (e: MouseEvent) => {
        const target = e.target as Node;
        if (!pane.contains(target)) return;
        const selection = pane.ownerDocument.getSelection();
        let startRef: OriginalWordRef | null = null;
        let endRef: OriginalWordRef | null = null;
        if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
          const range = selection.getRangeAt(0);
          startRef = wordRefFromNode(range.startContainer);
          endRef = wordRefFromNode(range.endContainer);
        }
        // No drag-selection ⇒ the single right-clicked word.
        if (!startRef || !endRef) {
          const ref = wordRefFromNode(target);
          if (!ref) return;
          startRef = ref;
          endRef = ref;
        }
        // Read the array LIVE (a prior edit may have grown/shifted it).
        const elements = transcriptionStore.getFinalTranscriptionElements();
        const range = resolveSelectionArrayRange(elements, startRef, endRef);
        if (!range) return; // unresolved → no-op (never feed -1 to the replace chain)
        e.preventDefault();
        setOriginalMenu({
          positionXY: { x: e.clientX, y: e.clientY },
          selection: range,
        });
      };

      // PLAT-10266 (2026-07-28) — TRIGGER PARITY with Transcript & Speakers, which
      // opens this same menu on BOTH a right-click and a mouse-up after highlighting
      // (`Transcript.tsx:3196` / `:3203` → `handleMouseUp`). The original column bound
      // only `contextmenu`, so highlighting a span — the gesture reviewers reach for
      // first — produced nothing at all. Mirrors `Transcript.handleMouseUp`'s idiom:
      // left button only, let the selection settle, require a REAL (non-collapsed)
      // selection so a plain word click still just seeks, and drop silently when the
      // selection can't be resolved. Resolution + menu opening reuse the same
      // `wordRefFromNode` / `resolveSelectionArrayRange` / `setOriginalMenu` chain as
      // the right-click path above — one behaviour, two triggers.
      let settleTimer: number | undefined;
      const onMouseUp = (e: MouseEvent) => {
        if (e.button !== 0) return;
        if (!pane.contains(e.target as Node)) return;
        // Read the cursor position NOW; the event is gone by the time the timer runs.
        const { clientX, clientY } = e;
        window.clearTimeout(settleTimer);
        // The selection is not final on mouseup (same hack as Transcript.tsx:1952)
        // — reading it synchronously can capture the PREVIOUS selection.
        settleTimer = window.setTimeout(() => {
          const selection = pane.ownerDocument.getSelection();
          if (
            !selection ||
            selection.rangeCount === 0 ||
            selection.isCollapsed ||
            !selection.toString().trim()
          ) {
            return;
          }
          const range = selection.getRangeAt(0);
          const startRef = wordRefFromNode(range.startContainer);
          const endRef = wordRefFromNode(range.endContainer);
          if (!startRef || !endRef) return;
          // Read the array LIVE (a prior edit may have grown/shifted it).
          const elements = transcriptionStore.getFinalTranscriptionElements();
          const resolved = resolveSelectionArrayRange(
            elements,
            startRef,
            endRef,
          );
          if (!resolved) return; // unresolved → no-op (never feed -1 to the replace chain)
          setOriginalMenu({
            positionXY: { x: clientX, y: clientY },
            selection: resolved,
          });
        }, 200);
      };

      pane.addEventListener('contextmenu', onContextMenu);
      pane.addEventListener('mouseup', onMouseUp);
      return () => {
        window.clearTimeout(settleTimer);
        pane.removeEventListener('contextmenu', onContextMenu);
        pane.removeEventListener('mouseup', onMouseUp);
      };
      // `videoExpanded`: hiding the source column unmounts it, so a collapse
      // hands `originalPaneRef` a NEW node — without this the listeners stay on
      // the detached one and Replace Term is dead for the rest of the session.
    }, [editableOriginalEnabled, transcriptionStore, originalHidden]);

    // Changes-card hover → correspondence emphasis in BOTH aligned columns
    // (the Google-Translate-style alignment reveal; side-panel items act as a
    // remote control for the editor). The workspace only holds the hovered
    // indices; `AlignedBlocksView` owns the block-aware painting (word-level
    // blue for a partial-block change, one calm row tint for a fully covered
    // block) because only it knows the block boundaries.
    const [hoveredChangeIndices, setHoveredChangeIndices] = useState<
      readonly number[] | null
    >(null);

    // Reset the hover emphasis whenever the Changes tab is left — a hovered
    // card's mouseleave never fires when the panel is removed from the DOM.
    useEffect(() => {
      if (rightTab === 'changes') return;
      setHoveredChangeIndices(null);
    }, [rightTab]);

    // Changes-card click → scroll both aligned columns to the change. The
    // nonce re-fires on repeat clicks of the same card (the
    // `pendingMontageSeek` pattern); `AlignedBlocksView` consumes it.
    const [revealRequest, setRevealRequest] = useState<{
      initialIndex: number;
      nonce: number;
    } | null>(null);

    // Reverse direction: hovering a changed (green) phrase in the translated
    // column reports its Changes-panel entry key; the panel highlights and
    // scrolls to the matching card. Harmless while another tab is active (the
    // panel is unmounted); phrase mouseleave always clears.
    const [hoveredChangeKey, setHoveredChangeKey] = useState<string | null>(
      null,
    );

    // Proxy REFERENCE language for the left column (PLAT-10655): null = the
    // true original; a language code = show that existing translation as the
    // read-only reference (reviewers often don't speak the source language).
    // Workspace-local per D6; resets each open (a URL-restored open seeds it).
    // Choices are EXISTING translations only — the cache the opening surface
    // already discovered.
    const [referenceLanguage, setReferenceLanguage] = useState<string | null>(
      initialReferenceLanguage,
    );
    const proxyLanguages = [...translationStore.translations.keys()]
      .filter(l => l !== language)
      .sort((a, b) =>
        formatLanguageCaption(a).localeCompare(formatLanguageCaption(b)),
      );
    const sourceLabel = translation?.sourceLanguage
      ? formatLanguageCaption(translation.sourceLanguage)
      : undefined;

    // PLAT-10214 (T6) — span replace. Watches the translated pane for a
    // contiguous-run selection (≥2 words), surfaces a "Fix Translation" trigger,
    // opens the `ReplaceSpanEditor`, locks the mapped original region, and on
    // confirm authors a reversible override segment (D10). Opt-in + contained
    // here (D9); per-pane DOM scoping (#12).
    //
    // `fragmenter` is left undefined for now: the T9 on-demand splitter
    // (PLAT-10269, arbor-v1) is not yet wired into `TranslationRepository`, so a
    // confirmed override lands as a SINGLE active fragment — still fully
    // reversible (one undo restores the exact machine tiling) and re-fragmentable
    // by T10 once the splitter ships. Pass the real splitter call here when
    // PLAT-10269 merges to get immediate word-level fine-splitting on confirm.
    // PLAT-10264 (T5) — view-mode is transient workspace UI state (the store
    // stays a pure data cache). Seeded from `initialViewMode` (default
    // 'segment' since PLAT-10777). Declared
    // before useSpanReplace because the span resolver needs it (Segment view
    // expands a selection to the whole segment).
    const [viewMode, setViewMode] =
      useState<TranslationViewMode>(initialViewMode);
    // PLAT-10948 — mirror view-mode changes out so the parent surface can
    // persist the choice as its review param's value.
    const handleViewModeChange = useCallback(
      (next: TranslationViewMode) => {
        setViewMode(next);
        onViewModeChange?.(next);
      },
      [onViewModeChange],
    );

    // PLAT-10268 (T8) — transient per-lead coverage verdict cache (D6: workspace
    // state, never the store). Keyed by the fragment's LEAD stable id (leadUuid =
    // `coveredElementIds[0]`), NOT a positional index (stable-id migration).
    // `coverageRequest` drives the Summary-panel slot's loading / error / result.
    // Cleared on unmount; a stale entry is filtered out at read time by
    // `verdictForLead` (AC 5).
    const [coverageByLeadUuid, setCoverageByLeadUuid] = useState<CoverageCache>(
      () => new Map(),
    );
    const [coverageRequest, setCoverageRequest] = useState<{
      lead: string;
      status: 'loading' | 'done' | 'error';
    } | null>(null);
    // PLAT-10268 (T8) QA polish — per-lead in-flight set (SPEC AC 4). A lead uuid
    // is added when its coverage check starts and removed on success/error, so the
    // translated column can show a loading indicator AT the analyzed fragment's
    // coverage slot (replaced by the badge when the verdict resolves). Multiple
    // segments can be under check at once, hence a Set keyed by lead uuid (the
    // same key `coverageByLeadUuid` uses). Separate from the single
    // `coverageRequest` that drives the Summary "Coverage" row (latest edit).
    const [loadingCoverageLeads, setLoadingCoverageLeads] = useState<
      ReadonlySet<string>
    >(() => new Set());

    // PLAT-11335 — verdict lookup for the always-on coverage surfaces: the
    // phrase-editor popover's badge and the Changes tab's badge. Resolves the
    // fresh verdict covering the given stable ids by overlap against the live
    // phrases (same freshness rules as the column badges). Distinct from the
    // Segment-view-only COLUMN display, whose props are withheld outside
    // Segment view (QA round 2) — these two surfaces show the score wherever
    // the reviewer actually meets the phrase, Paragraph view included.
    const resolveCoverageForPhrase = useCallback(
      (originalElementIds: readonly string[]) => {
        if (originalElementIds.length === 0) return undefined;
        const phrases =
          translationStore.getTranslation(language)?.phrases ?? [];
        return verdictForPhrase(
          coverageByLeadUuid,
          originalElementIds,
          phrases,
        );
      },
      [coverageByLeadUuid, translationStore, language],
    );

    // Fired by `useSpanReplace` after a confirmed span replacement persists (both
    // persist paths). Runs the coverage check for the span and caches the verdict
    // per lead. Fire-and-forget: `confirm` does not await this, so it never blocks
    // the save (invariant F). `checkCoverage` never throws (degrades to a
    // `covered`/100 verdict), so the catch is a last-resort UI guard only.
    const handleSpanConfirmed = useCallback(
      ({
        lang,
        coveredElementIds,
      }: {
        lang: string;
        coveredElementIds: string[];
      }) => {
        if (lang !== language || coveredElementIds.length === 0) return;
        // PLAT-11678 — every committed edit over these words reaches the
        // automatic re-dub; it decides for itself whether the dub changed.
        autoRedubRef.current.notifyCommit(coveredElementIds);
        const lead = coverageLead(coveredElementIds);
        // PLAT-10266 (Card 3 + Card 5/F1) — a confirmed span replacement over this
        // fragment resolves its staleness: clear the amber (DESIGN §7). Fires on the
        // manual "Replace manually" confirm AND the plain-machine re-translate
        // confirm — clearing off the CALLBACK, never a `confirm` return, so the
        // single-fragment `createOverrideSegment` null-on-success case still clears.
        // EDIT-KEYED (F1): record the lead's CURRENT stale signature, but ONLY when
        // it is currently stale, so a coverage "Fix Translation" on a NOT-stale
        // fragment records nothing and never pre-suppresses a future first edit (R3);
        // a later fresh source edit changes the signature and re-flags amber (R2).
        //
        // IDENTITY BRIDGE (dev merge 2026-07-27): dev migrated this callback to
        // stable element ids (`coveredElementIds`) and `coverageLead` now returns an
        // id — but the amber/stale map is still keyed by the NUMERIC lead
        // (min original index, the `data-initial-index` the amber paints on). So
        // resolve the covered ids back to their indices through the live source
        // array using the repo's UUID-first join (`source_uuid ?? id`) and key the
        // signature off that. Added tokens carry `initial_index: -1`; they are
        // excluded so the lead stays the canonical min ORIGINAL index.
        const coveredIdSet = new Set(coveredElementIds);
        const coveredSourceIndices = sourcedArray
          .filter(el => {
            // `id` is optional on a source element, so the stable id is
            // `string | undefined` — the store's own join idiom
            // (`TranslationStore.checkCoverage`) skips the undefined case. An
            // id-less element can never be in the covered id set anyway.
            const elementId = el.source_uuid ?? el.id;
            return elementId !== undefined && coveredIdSet.has(elementId);
          })
          .map(el => el.initial_index)
          .filter((n): n is number => typeof n === 'number' && n >= 0);
        if (coveredSourceIndices.length > 0) {
          const staleLead = Math.min(...coveredSourceIndices);
          setResolvedStaleSignatures(prev =>
            recordResolvedSignature(
              prev,
              staleLead,
              staleSignaturesRef.current,
            ),
          );
        }
        setCoverageRequest({ lead, status: 'loading' });
        // Mark this fragment's coverage slot as in-flight (SPEC AC 4): the
        // translated column shows a spinner there until the verdict resolves.
        setLoadingCoverageLeads(prev => {
          const next = new Set(prev);
          next.add(lead);
          return next;
        });
        const clearLoadingLead = () =>
          setLoadingCoverageLeads(prev => {
            if (!prev.has(lead)) return prev;
            const next = new Set(prev);
            next.delete(lead);
            return next;
          });
        void (async () => {
          // Fingerprint the span's translation as read for the check, so a later
          // edit invalidates this verdict (AC 5) via `verdictForLead`.
          const phrases = translationStore.getTranslation(lang)?.phrases ?? [];
          const signature = coverageSignature(phrases, coveredElementIds);
          try {
            const verdict = await translationStore.checkCoverage(
              lang,
              coveredElementIds,
            );
            setCoverageByLeadUuid(prev => {
              const next = new Map(prev);
              next.set(lead, {
                verdict,
                originalElementIds: coveredElementIds,
                signature,
              });
              return next;
            });
            setCoverageRequest({ lead, status: 'done' });
          } catch {
            setCoverageRequest({ lead, status: 'error' });
          } finally {
            clearLoadingLead();
          }
        })();
      },
      [language, translationStore],
    );

    // PLAT-10268 (T8) — Atom 6. Which fragment's coverage details popover is
    // open, keyed by the fragment's LEAD stable id (the same key
    // `coverageByLeadUuid` uses). Only one open at a time. Held here (AC 1); the
    // badge click toggles it, and AlignedBlocksView mounts the popover anchored
    // to the clicked badge.
    const [openCoverageLead, setOpenCoverageLead] = useState<string | null>(
      null,
    );
    const handleCoverageBadgeClick = useCallback((lead: string) => {
      setOpenCoverageLead(prev => (prev === lead ? null : lead));
    }, []);
    const handleCoverageClose = useCallback(() => {
      setOpenCoverageLead(null);
    }, []);
    // Undo from the popover: revert the fragment to its machine original via the
    // shipped path (PLAT-10214), drop the now-stale cached verdict for that lead
    // (so the badge disappears), and close the popover (AC 3). The span's source
    // word stable ids are held on the cached entry.
    //
    // SHAPE-AWARE (PLAT-10268 review finding 1): a confirmed span replace authors
    // an OVERRIDE SEGMENT whose fragments are `isUserEdited: true` with no captured
    // machine original, so `revertTranslation` no-ops on them (its "case 3"). Route
    // by shape exactly as `useSpanReplace.revertOverride` does — for an override
    // span drop the whole segment with `revertOverrideSegment` (it reactivates the
    // retired machine fragments), and fall back to per-phrase `revertTranslation`
    // only for the non-override case. The override segment id is resolved from the
    // covered phrases' stable ids via the SAME shipped helper the span editor uses
    // (`buildSpanDisplayModel` → `existingOverrideSegmentId`), so the id-scheme
    // logic is not duplicated.
    const handleCoverageUndo = useCallback(
      (lead: string) => {
        const data = translationStore.getTranslation(language);
        // Resolve the cached entry by the clicked fragment's STABLE IDS, not its
        // lead: a re-fix re-tiles the segment so the fragment lead no longer
        // equals the entry key (BUG B). Recover the fragment's full id span from
        // the active phrase whose lead id is `lead`, then match the entry the
        // badge showed via stable-id overlap.
        const phrases = data?.phrases ?? [];
        const fragmentElementIds = phrases.find(
          p => p.active !== false && p.originalElementIds?.[0] === lead,
        )?.originalElementIds ?? [lead];
        const entry = entryForPhrase(
          coverageByLeadUuid,
          fragmentElementIds,
          phrases,
        );
        if (entry) {
          const overrideSegmentId = data
            ? resolveOverrideSegmentIdForElementIds(
                data.phrases,
                data.parentPhrases ?? [],
                entry.originalElementIds,
              )
            : undefined;
          if (overrideSegmentId) {
            void translationStore.revertOverrideSegment(
              language,
              overrideSegmentId,
            );
          } else {
            translationStore.revertTranslation(
              language,
              entry.originalElementIds,
            );
          }
          // PLAT-11678 — the coverage popover's Undo reverts the fix's text, so
          // the dub for that segment is now stale: route it to the auto re-dub
          // like every other revert surface.
          autoRedubRef.current.notifyCommit(entry.originalElementIds);
        }
        // Drop the entry under its own key (the span's lead id) — which need not
        // equal the clicked fragment's `lead` after a re-tile.
        const entryKey = entry ? coverageLead(entry.originalElementIds) : lead;
        setCoverageByLeadUuid(prev => {
          if (!prev.has(entryKey)) return prev;
          const next = new Map(prev);
          next.delete(entryKey);
          return next;
        });
        setOpenCoverageLead(null);
      },
      [coverageByLeadUuid, language, translationStore],
    );

    // PLAT-10270 (T10) — the shared lifecycle for the popover's Split /
    // Undo split / Redo split: fire the store op, close the popover
    // immediately (the `handleCoverageUndo` idiom — closing on action also
    // blocks re-entry while a backend split is in flight), and once the op
    // resolves having changed the tiling, RE-FINGERPRINT the acted-on
    // fragment's cached coverage verdict against the re-tiled document.
    //
    // BUG-1 fix: a Split / Undo-split / Redo-split RE-TILES the segment but
    // preserves its translated TEXT (every transform is a faithful partition —
    // the fragments join back to the same words). The coverage verdict still
    // describes that text; only its freshness fingerprint, which is keyed to
    // the exact fragment boundaries, goes stale after the re-tile. The previous
    // code DROPPED the verdict here — which removed the coverage badge, the
    // ONLY entry point to the Split / Undo split / Redo split controls, so the
    // reviewer could never re-open the popover to reach "Undo split" after a
    // Split (`isSegmentSplit` was correctly true, but the popover was
    // unreachable). Refreshing the signature instead keeps the badge alive and
    // lets the popover re-open showing the new split state. The store resolves
    // false and warns on failure, leaving the translation — and the verdict —
    // unchanged, so nothing is touched then.
    const runSplitAction = useCallback(
      (segmentId: string, action: () => Promise<boolean>) => {
        // Resolve the cached entry BEFORE the store re-tiles (collapse /
        // reapply mutate synchronously): the badge being preserved is the OPEN
        // one, keyed by the open fragment's lead ELEMENT id (`openCoverageLead`)
        // — NOT `segmentId`. For a paragraph-parented segment `segmentId` is the
        // segment's `parentId` (a `p_…` id), which never equals a raw element
        // id, so resolving by it would miss the entry, drop the badge, and make
        // the split controls unreachable after one re-tile. Resolve by
        // `openCoverageLead` — the same lead-element-id space
        // `handleCoverageUndo` uses; it equals `segmentId` for an unsplit
        // fragment and is the correct badge key for a parented one. `segmentId`
        // still drives the store tiling op.
        const badgeLead = openCoverageLead ?? segmentId;
        const phrases =
          translationStore.getTranslation(language)?.phrases ?? [];
        const fragmentElementIds = phrases.find(
          p => p.active !== false && p.originalElementIds?.[0] === badgeLead,
        )?.originalElementIds ?? [badgeLead];
        const entry = entryForPhrase(
          coverageByLeadUuid,
          fragmentElementIds,
          phrases,
        );
        const entryKey = entry
          ? coverageLead(entry.originalElementIds)
          : badgeLead;
        void (async () => {
          if (!(await action())) return;
          // Re-fingerprint against the re-tiled document so the badge survives
          // (see the block comment above). The verdict + the covered source
          // ids are unchanged; only the signature is recomputed from the new
          // active-fragment boundaries.
          const nextPhrases =
            translationStore.getTranslation(language)?.phrases ?? [];
          setCoverageByLeadUuid(prev => {
            const existing = prev.get(entryKey);
            if (!existing) return prev;
            const next = new Map(prev);
            next.set(entryKey, {
              ...existing,
              signature: coverageSignature(
                nextPhrases,
                existing.originalElementIds,
              ),
            });
            return next;
          });
        })();
        setOpenCoverageLead(null);
      },
      [coverageByLeadUuid, language, openCoverageLead, translationStore],
    );

    // PLAT-10270 (T10) — the REAL `SplitControls` provider (replaces the
    // PLAT-10268 placeholder): segment-id-keyed pass-throughs to the store's
    // tiling ops. The predicates are synchronous document derivations; the
    // popover reads them on open and closes after each action, so its
    // enabled/disabled logic (which lives in `CoveragePopover`) needs no
    // change here.
    const splitControls: SplitControls = useMemo(
      () => ({
        isSplit: segmentId =>
          translationStore.isSegmentSplit(language, segmentId),
        hasCachedSplit: segmentId =>
          translationStore.hasCachedSegmentSplit(language, segmentId),
        splitSegment: segmentId =>
          runSplitAction(segmentId, () =>
            translationStore.splitSegment(language, segmentId),
          ),
        undoSplit: segmentId =>
          runSplitAction(segmentId, () =>
            translationStore.collapseSegmentSplit(language, segmentId),
          ),
        redoSplit: segmentId =>
          runSplitAction(segmentId, () =>
            translationStore.reapplySegmentSplit(language, segmentId),
          ),
      }),
      [language, runSplitAction, translationStore],
    );

    // PLAT-10270 (T10) — the global "Split into Words" Header action:
    // fine-split the WHOLE translation via the on-demand splitter. Disabled
    // until the reviewer has made at least one TYPED manual fix on the current
    // language (SPEC criterion 8) — the action preserves reviewer fixes, so it
    // only offers itself once there is a typed fix to preserve. Also disabled
    // while a split is in flight — a backend round trip, so the in-flight flag
    // guards the double-click double-spend — and before the translation loads.
    // The gate uses the shared `collectUserFixes` classifier (via
    // `hasTypedManualFix`), which EXCLUDES `directed` re-translates; it is
    // reactive because this component is a MobX observer reading the store.
    const [isSplittingWhole, setIsSplittingWhole] = useState(false);
    const canSplitWhole =
      !!translation &&
      hasTypedManualFix(translation.phrases, translation.parentPhrases ?? []);
    const handleSplitWholeTranslation = useCallback(() => {
      setIsSplittingWhole(true);
      void translationStore
        .splitWholeTranslation(language)
        .finally(() => setIsSplittingWhole(false));
    }, [language, translationStore]);

    // PLAT-10266 (Card R2) — HIGHLIGHTING a stale (amber) fragment must open the resolve
    // menu, not the "Fix Translation" span popover. The selection is owned by
    // `useSpanReplace`; the resolve menu's state is local to `AlignedBlocksView`. These
    // three pieces join them: an opener handed up by ref, the lead of the current
    // single-stale selection (which the right-click path also routes on, so the two
    // triggers agree), and a guard so a drag that re-resolves on every frame does not
    // re-open the menu on each one.
    const resolveMenuOpenerRef = useRef<
      | ((hit: { lead: number; anchorElement: HTMLElement } | null) => void)
      | null
    >(null);
    const [staleSelectionLead, setStaleSelectionLead] = useState<number | null>(
      null,
    );
    const lastOpenedStaleLeadRef = useRef<number | null>(null);
    const handleStaleSelectionChange = useCallback(
      (hit: { lead: number; anchorElement: HTMLElement } | null) => {
        setStaleSelectionLead(hit ? hit.lead : null);
        if (!hit) {
          // The selection stopped being a single stale fragment. If WE opened the menu
          // for an earlier state of this drag, close it — a drag that starts on a stale
          // word is briefly single-stale before it widens to mixed, which left the menu
          // open over a mixed selection alongside the span trigger (caught live).
          // Closing is a no-op for a menu the reviewer opened by right-clicking.
          if (lastOpenedStaleLeadRef.current !== null) {
            lastOpenedStaleLeadRef.current = null;
            resolveMenuOpenerRef.current?.(null);
          }
          return;
        }
        if (lastOpenedStaleLeadRef.current === hit.lead) return;
        lastOpenedStaleLeadRef.current = hit.lead;
        resolveMenuOpenerRef.current?.(hit);
      },
      [],
    );

    // Mirror the URL-addressable attributes out to the host surface (which puts
    // them in the query string) whenever they change, including on mount so the
    // link is complete the moment the workspace opens. The callback is read
    // through a ref so the effect fires on ATTRIBUTE changes only: an unstable
    // (inline) callback from a host that setStates on report would otherwise
    // re-fire the effect every render — a render→report→render loop.
    const onViewStateChangeRef = useRef(onViewStateChange);
    onViewStateChangeRef.current = onViewStateChange;
    useEffect(() => {
      onViewStateChangeRef.current?.({
        viewMode,
        panel: rightTab,
        referenceLanguage,
        reviewMode,
      });
    }, [viewMode, rightTab, referenceLanguage, reviewMode]);

    // PLAT-11382 — hand the selection's dub line ids to the host, which opens
    // the same dub popover it opens for a selection re-dub started on its own
    // transcript. No analytics event is emitted here: instrumenting the
    // directed re-dub is its own later ticket, and inventing an event name now
    // would have to be renamed then.
    // PLAT-12025 — the lesson the hint under the pane header teaches; a re-dub
    // asked for ends it, for good, like any other action on a coach mark.
    const [sectionRedubTaught, setSectionRedubTaught] = useState(false);
    const handleRequestReDubSelection = useCallback(
      (request: ReDubSelectionRequest) => {
        if (!onRequestReDubSelection || request.segmentIds.length === 0) return;
        dismissContextualHint(SECTION_REDUB_HINT_KEY);
        setSectionRedubTaught(true);
        onRequestReDubSelection(language, request);
      },
      [onRequestReDubSelection, language],
    );

    const spanReplace = useSpanReplace({
      enabled: true,
      language,
      viewMode,
      translatedColumnRef: translatedPaneRef,
      originalColumnRef: originalPaneRef,
      fragmenter: undefined,
      onSpanConfirmed: handleSpanConfirmed,
      // PLAT-10266 (Card R2) — both gated on the SAME `editableOriginalEnabled` as the
      // resolve actions themselves, so the clip host and shared prose homes stay inert
      // (AC6) and the hook's two other consumers are untouched (they pass neither).
      staleLeads: editableOriginalEnabled ? visibleStaleLeads : undefined,
      onStaleSelectionChange: editableOriginalEnabled
        ? handleStaleSelectionChange
        : undefined,
      // PLAT-10266 (Card 7, §D) — the load-time baseline lets confirm /
      // directed-re-translate resolve + re-key a vanished covered span (a
      // "Replace Term"-deleted source word) so the authored override renders by
      // identity instead of failing / re-orphaning. Undefined until the first
      // load (baseline captured in render above) or when the feature is gated off.
      sourceBaseline: sourceBaselineRef.current ?? undefined,
      // PLAT-11382 — "Re-dub AI Voice" in the review's own selection menu. The
      // hook already turns a selection into the dub line ids it covers, and it
      // already has this hand-off: the inline prose surface has used it since
      // PLAT-10616, and the review is the one place that never wired it.
      // Offered only when the reviewed language actually has a dub to re-record
      // and the host has somewhere to take the request. The direction is typed
      // in the host's popover, which already decides what may be offered from
      // the voices the run will use — nothing about that rule lives here.
      onRequestReDub:
        onRequestReDubSelection && dubMonitorEnabled
          ? handleRequestReDubSelection
          : undefined,
    });
    // PLAT-12047 / PLAT-12056 — a re-dub or a fix handed over from a host
    // transcript (see the prop).
    useReviewHandoff({
      pendingHandoff,
      rootRef: translatedPaneRef,
      openRedub: handleRequestReDubSelection,
      openFix: spanReplace.openForIndices,
      onConsumed: onHandoffConsumed,
    });

    // PLAT-10257 (A4) — the coverage popover's "Fix Translation": open the
    // SAME two-mode span editor for the popover entry's covered indices. The
    // resolution is the `handleCoverageUndo` recipe verbatim (lead → active
    // phrase → cached entry), so the editor opens over exactly the span the
    // popover shows and its Undo reverts. The guard (overlap notice for a
    // span touching an existing fix; instruction-mode block for the re-edit
    // case) is computed by `openForIndices` from current store data — the
    // popover path never reaches the op unguarded (no silent no-op).
    // Stable-id migration (dev merge 2026-07-27): the coverage cache is keyed by
    // the fragment's LEAD ELEMENT ID, so the lead + the entry resolution now live
    // in the id space — the `handleCoverageUndo` recipe verbatim, as before. Only
    // the final hand-off converts back: `openForIndices` is still an INDEX-space
    // API, so map the resolved span's stable ids to the source indices of the
    // active phrases carrying them. Widening is harmless — `openForIndices`
    // re-unions the full index set of every phrase the indices touch, so any
    // index set selecting the same phrases yields the same span.
    const handleCoverageFixTranslation = useCallback(
      (lead: string) => {
        const phrases =
          translationStore.getTranslation(language)?.phrases ?? [];
        const fragmentElementIds = phrases.find(
          p => p.active !== false && p.originalElementIds?.[0] === lead,
        )?.originalElementIds ?? [lead];
        const entry = entryForPhrase(
          coverageByLeadUuid,
          fragmentElementIds,
          phrases,
        );
        // PLAT-10268 identity migration: coverage is keyed by stable element id
        // now, but `openForIndices` is still index-keyed, so map the covered ids
        // back to the story-space indices of the phrases carrying them. Passing
        // an empty set hits `openForIndices`'s loud defensive floor rather than
        // silently doing nothing.
        const targetIds = new Set(
          entry?.originalElementIds ?? fragmentElementIds,
        );
        spanReplace.openForIndices(
          phrases
            .filter(
              p =>
                p.active !== false &&
                (p.originalElementIds ?? []).some(id => targetIds.has(id)),
            )
            .flatMap(p => p.originalIndices),
        );
        // This entry IS the directed one (PLAT-10257) — clear any stale-flag marker so
        // the editor keeps its two-mode instruction UI here (PLAT-10266, 2026-07-28).
        setStaleResolveEntry(null);
        setOpenCoverageLead(null);
      },
      [
        coverageByLeadUuid,
        language,
        translationStore,
        spanReplace.openForIndices,
      ],
    );

    // PLAT-10266 (Card 2) — resolve a STALE fragment via "Re-translate (machine)".
    // The inline amber affordance calls `handleResolveRetranslate(lead)`. It opens
    // the span model so the branch can be read from its `existingOverrideSegmentId`
    // (DESIGN §7), and records the request. React commits `openForIndices`' editor
    // state on the NEXT render, so the branch + store calls run in the effect below
    // once the open editor reflects this span (`confirmDirected` operates on that
    // open editor).
    // PLAT-10266 (2026-07-28) — WHICH entry opened the span editor. Three actions open
    // the SAME editor through `spanReplace.openForIndices`: the coverage popover's "Fix
    // Translation" (PLAT-10257 — directed, instruction-driven BY DESIGN) and the two
    // stale-flag resolves. The editor's props are wired once at its single render site,
    // so before this the stale-flag entries inherited the instruction UI they must not
    // have (SPEC: the re-translate offered here "does NOT take a free-text instruction
    // — that is PLAT-10257"). Recording the entry lets the render site withhold
    // `onConfirmDirected` and offer an instruction-free machine regenerate instead.
    //
    // Keyed by covered span, not just a boolean: the key is re-checked against the
    // OPEN editor's own `coveredIndices` before it is honoured (same self-validating
    // idiom as `pendingRetranslate` below), so a lingering value can never mis-label a
    // later editor opened from a different entry.
    const [staleResolveEntry, setStaleResolveEntry] = useState<{
      lead: number;
      coveredKey: string;
    } | null>(null);
    const coveredKeyOf = (indices: readonly number[]) =>
      [...indices].sort((a, b) => a - b).join(',');

    // Honour the marker ONLY while the editor that is actually open is the one the
    // stale-flag action opened. Re-checking against the live editor's own covered span
    // means a leftover marker cannot mis-label an editor opened later from the coverage
    // popover or a translated-column selection — no cleanup effect needed.
    const staleEntryForOpenEditor =
      staleResolveEntry &&
      spanReplace.editor &&
      coveredKeyOf(spanReplace.editor.model.coveredIndices) ===
        staleResolveEntry.coveredKey
        ? staleResolveEntry
        : null;

    // PLAT-10266 (2026-07-28) — why the last machine regenerate was REFUSED, surfaced in
    // the span editor's shipped notice. It lives here, not in the editor, because the op
    // runs in the effect below (it needs the OPENED editor's `existingOverrideSegmentId`
    // to choose its branch), so the answer arrives after the click and is handed down.
    const [regenerateFailure, setRegenerateFailure] =
      useState<ConfirmDirectedFailureReason | null>(null);

    const [pendingRetranslate, setPendingRetranslate] = useState<{
      lead: number;
      coveredKey: string;
      nonce: number;
    } | null>(null);
    const retranslateNonceRef = useRef(0);
    const handleResolveRetranslate = useCallback(
      (lead: number) => {
        const phrases =
          translationStore.getTranslation(language)?.phrases ?? [];
        const fragment = phrases.find(
          p =>
            p.active !== false &&
            p.originalIndices.length > 0 &&
            Math.min(...p.originalIndices) === lead,
        );
        const coveredIndices = fragment?.originalIndices ?? [lead];
        spanReplace.openForIndices(coveredIndices);
        setStaleResolveEntry({
          lead,
          coveredKey: coveredKeyOf(coveredIndices),
        });
        setPendingRetranslate({
          lead,
          coveredKey: coveredKeyOf(coveredIndices),
          nonce: ++retranslateNonceRef.current,
        });
      },
      [translationStore, language, spanReplace.openForIndices],
    );

    // Runs once the editor `openForIndices` opened reflects the pending span:
    // read `existingOverrideSegmentId` and route (DESIGN §7). The MACHINE branch
    // reuses `confirmDirected('')`, which OWNS the open editor and closes it on
    // success — left open here. The OVERRIDE branch's net-new in-place path does
    // NOT use the editor, so close the briefly-opened one first (`cancel`). A
    // nonce ref guards against re-firing after the async op flips the editor state.
    const handledRetranslateNonce = useRef(-1);
    useEffect(() => {
      const req = pendingRetranslate;
      if (!req) return;
      if (handledRetranslateNonce.current === req.nonce) return;
      const ed = spanReplace.editor;
      if (!ed) return; // wait for openForIndices' state commit
      const edKey = [...ed.model.coveredIndices]
        .sort((a, b) => a - b)
        .join(',');
      if (edKey !== req.coveredKey) return; // the open editor is not (yet) ours
      handledRetranslateNonce.current = req.nonce;
      setPendingRetranslate(null);
      // A reason left over from an earlier attempt would mislead about THIS one (the
      // same care `switchMode` takes with `directedFailure` in ReplaceSpanEditor).
      setRegenerateFailure(null);

      const { existingOverrideSegmentId, coveredIndices, coveredElementIds } =
        ed.model;
      // PLAT-10266 (2026-07-28) — the override branch's `spanReplace.cancel()` used to
      // run HERE, before the op. That left an override-branch failure with nowhere to
      // report: the editor was already gone. It now runs only on SUCCESS below, so both
      // branches behave alike — the editor stays open while the op runs and closes when
      // it lands. The end state on success is unchanged (the machine branch's
      // `confirmDirected` closes the editor itself).
      void retranslateStaleFragment({
        language,
        lead: req.lead,
        coveredIndices,
        coveredElementIds,
        existingOverrideSegmentId,
        transcriptionElements: sourcedArray,
        // Card 7 (§D) — the override-in-place branch's vanished-source fallback.
        sourceBaseline: sourceBaselineRef.current ?? undefined,
        fragmenter: undefined,
        confirmDirected: spanReplace.confirmDirected,
        directedRetranslateSpan: (l, els, instr) =>
          translationStore.directedRetranslateSpan(l, els, instr),
        updateOverrideSegment: (l, id, val, opts) =>
          translationStore.updateOverrideSegment(l, id, val, opts),
        // EDIT-KEYED clear (F1): record the resolved lead's CURRENT stale
        // signature; a fresh later source edit changes it and re-flags amber.
        onResolved: l =>
          setResolvedStaleSignatures(prev =>
            recordResolvedSignature(prev, l, staleSignaturesRef.current),
          ),
      }).then(outcome => {
        // PLAT-10266 (2026-07-28) — CONSUME the outcome. This was a bare `void` call, so
        // every typed failure the op returns was discarded: no notice, no log, nothing.
        // A refused regenerate looked like a dead button — proven live on the fragment
        // "обычно" (editor stayed open, ZERO backend requests, silence on every channel).
        if (outcome.ok) {
          if (existingOverrideSegmentId !== undefined) spanReplace.cancel();
          return;
        }
        setRegenerateFailure(toEditorFailureReason(outcome.reason));
        // Repo rule: no silent failures, and never `console.*`.
        logger.warn('stale-fragment machine re-translate refused', {
          branch: outcome.branch,
          reason: outcome.reason,
          lead: req.lead,
          coveredIndices,
        });
      });
    }, [
      pendingRetranslate,
      spanReplace.editor,
      spanReplace.confirmDirected,
      spanReplace.cancel,
      language,
      sourcedArray,
      translationStore,
    ]);

    // PLAT-10266 (Card 3) — resolve a STALE fragment via "Replace manually".
    // The inline amber menu's second row calls `handleResolveManual(lead)`. It
    // OPENS the shipped type-replacement span editor on the fragment's covered
    // span; the reviewer types a correction and confirms in that editor
    // (`ReplaceSpanEditor` → `spanReplace.confirm(value)`, DESIGN §7). We do NOT
    // call `confirm` ourselves — the editor UI does. `openForIndices` opens the
    // editor DIRECTLY (it sets both `pendingSpan` and `editor` in one commit,
    // useSpanReplace.ts:810-811), so no `openEditor` follow-up is needed — the
    // SAME idiom the sibling `handleResolveRetranslate` and PLAT-10257's
    // `handleCoverageFixTranslation` use. `confirm` routes to
    // `updateOverrideSegment` when the span is inside an existing override else
    // `createOverrideSegment` — it handles BOTH the fresh and already-overridden
    // cases (C9). The amber clears in `handleSpanConfirmed` off the resulting
    // `onSpanConfirmed`, so it is robust to the single-fragment null-on-success.
    const handleResolveManual = useCallback(
      (lead: number) => {
        const phrases =
          translationStore.getTranslation(language)?.phrases ?? [];
        const fragment = phrases.find(
          p =>
            p.active !== false &&
            p.originalIndices.length > 0 &&
            Math.min(...p.originalIndices) === lead,
        );
        const coveredIndices = fragment?.originalIndices ?? [lead];
        spanReplace.openForIndices(coveredIndices);
        // Mark the entry so the editor renders WITHOUT the instruction mode and WITH
        // the one-click machine regenerate (PLAT-10266, 2026-07-28).
        setStaleResolveEntry({
          lead,
          coveredKey: coveredKeyOf(coveredIndices),
        });
      },
      [translationStore, language, spanReplace.openForIndices],
    );

    // PLAT-10270 (T10) — Backspace recombine. In Fragment view, Backspace
    // with the caret at the start of a fragment merges it into the previous
    // same-segment fragment (the store guards first-fragment / cross-segment
    // internally). Registered only while Fragment view is active and scoped
    // to the translated pane's spans by the hook's caret guard chain (D9 —
    // it never touches the karaoke caption commands).
    useFragmentRecombine({
      enabled: viewMode === 'fragment',
      language,
      translatedColumnRef: translatedPaneRef,
      translationStore,
    });

    // PLAT-11649 — the header's ambient save state for THIS language. A failed
    // persist is an event, not a parked state: it surfaces as a sticky error
    // toast (the default for `error`) and the status returns to idle.
    const saveStatus = useTranslationSaveStatus(translationStore, language, {
      onFailure: () =>
        videoCreatorRef.current.showToast({
          state: 'error',
          message: "Couldn't save your translation changes",
        }),
    });

    const lastSavedAtMs = translationStore.lastSavedAtFor(language);
    const lastSavedAt = useMemo(
      () => (lastSavedAtMs ? new Date(lastSavedAtMs) : null),
      [lastSavedAtMs],
    );

    // Every way out goes through here, so leaving is one event with the
    // affordance that took the reviewer out. Host-initiated unmounts (a story
    // switch) are not exits and fire nothing.
    const handleExit = useCallback(
      (method: ReviewExitMethod) => {
        trackTranslationReview_Exited({
          story_id: videoCreatorRef.current.storyId ?? undefined,
          language,
          entry: isClipPath ? 'clip' : 'story',
          method,
          view_mode: viewMode,
        });
        onClose();
      },
      [onClose, language, isClipPath, viewMode],
    );

    // Close on Escape. Capture-phase + stopPropagation so it doesn't also
    // trigger background transcript shortcuts (D9: scoped to this modal).
    useEffect(() => {
      const onKey = (e: KeyboardEvent) => {
        if (e.key !== 'Escape') return;
        // PLAT-11657 — full-screen video: Escape leaves full screen first,
        // never the review.
        if (isPlayerFullscreen) {
          e.stopPropagation();
          setIsPlayerFullscreen(false);
          return;
        }
        // The directed-retranslate modal owns Escape while it's open
        // (PLAT-10258 F2). Bail here — let the event through to the modal's own
        // Escape handler so Escape closes the MODAL (respecting its in-flight
        // guard `disableEscapeClose`), never this workspace. Without this bail
        // the capture-phase `stopPropagation()` below pre-empts the modal and
        // closes the whole workspace, orphaning an in-flight retranslate.
        if (isRetranslateOpen) return;
        // PLAT-10266 (Card 8) — an open right-click menu owns Escape: it closes
        // ITSELF (and stops the event). Bail so this workspace-level handler does
        // not ALSO close the whole review. The "Replace Term" menu is workspace
        // state (`originalMenu`); the stale-fragment resolve menu lives inside
        // `AlignedBlocksView`, so detect it in the DOM. (This handler is a window
        // capture-phase listener registered when the workspace mounts — before a
        // menu mounts — so the menu's own `stopPropagation` cannot pre-empt it;
        // a bail here is what keeps the review open.)
        // PLAT-12022 — likewise the selection re-dub popover: it closes
        // itself on Escape (a document capture-phase listener, registered
        // after this one) and the review stays open.
        if (
          originalMenu ||
          document.querySelector(
            '[data-testid="stale-fragment-resolve-menu"]',
          ) ||
          document.querySelector('[data-testid="redub-selection-popover"]')
        ) {
          return;
        }
        // PLAT-10266 (Card 9) — the open "Replace Term" MODAL owns Escape: it
        // CANCELS ITSELF and the review stays open (Escape always closes the
        // innermost thing). Handled here rather than deferred to the modal
        // because neither of the two paths that could close it works from this
        // window capture-phase listener: the modal does not autofocus its input
        // (`WordReplacementModalUI`), so with focus on `body` the input bail
        // below misses and Escape reached `onClose()` — closing the whole review
        // and losing the in-progress edit; and with the caret IN the input the
        // bail returned early, yet the input's own `onKeyDownCapture`
        // `stopImmediatePropagation` then kept the event from ever reaching
        // `Modal`'s document-level Escape listener, so Escape did nothing at
        // all. Clearing `originalReplace` IS the modal's cancel path (its
        // `discard` prop below), so this discards the edit exactly like Cancel.
        if (originalReplace) {
          e.stopPropagation();
          setOriginalReplace(null);
          return;
        }
        // Escape from an open in-pane editor/popover (the translated-phrase
        // editor) belongs to THAT editor — it cancels the edit and must NOT
        // close the whole workspace (which would discard the user's typed
        // text). Let the event reach the focused editor's own Escape handler.
        const target = e.target as HTMLElement | null;
        if (target?.closest('input, textarea, [contenteditable="true"]')) {
          return;
        }
        e.stopPropagation();
        // A place does not vanish on Escape; a dialog does. The inner chain
        // above still owns Escape for menus, modals and editors in both.
        if (isPage) return;
        handleExit('escape');
      };
      window.addEventListener('keydown', onKey, true);
      return () => window.removeEventListener('keydown', onKey, true);
    }, [
      handleExit,
      isPage,
      isPlayerFullscreen,
      isRetranslateOpen,
      originalMenu,
      originalReplace,
    ]);

    // Pause any background page player when the workspace opens mid-playback,
    // so it isn't left running behind the modal (double-audio edge, T4 /
    // PLAT-10263). Every mounted `TranscriptVideoPlayer` listens for this;
    // dispatched once. Child effects register before this parent effect runs,
    // so the workspace's own player catches it too (a harmless no-op — it
    // mounts paused).
    //
    // Also snapshot the playhead at open and RESTORE it on close: review
    // playback drives the SHARED videoCreator.time (the background page
    // player follow-seeks it by design — D6 single time owner), so without a
    // restore the page would land wherever review playback stopped. The modal
    // is a sandbox: closing returns the page to where it was. Refs (not deps)
    // keep this strictly mount/unmount even if the prop identity changes.
    const videoCreatorRef = useRef(videoCreator);
    videoCreatorRef.current = videoCreator;
    const entryTimeRef = useRef(videoCreator.time);
    const surfaceRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      // Pause every player on open (deliberate broadcast — silence all audio).
      window.dispatchEvent(new CustomEvent(TRANSCRIPT_PAUSE_EVENT));
      const entryTime = entryTimeRef.current;
      // The compact player owns playback while the review is open, so the
      // transcript panes' seek handlers must NOT also resume the (clip)
      // renderer — doing so spawns a second audio source (PLAT-10286).
      videoCreatorRef.current.suppressRendererPlayback = true;
      // CONTINUE FROM THE PAUSED FRAME (PLAT-10286, both entries): the review
      // opens where the originating surface was. `videoCreator.time` already
      // holds that position (entryTime), so there is nothing to reset here —
      // the compact player anchors to it on load (`anchorToCreatorTimeOnLoad`)
      // and the panes anchor to that cursor on their own. The host supplies a
      // poster that is the frame AT entryTime, so the still, the cursor, and
      // where Play resumes all agree. (Replaces the earlier "reset to 0 +
      // scroll panes to top" sandbox; that reversal is signed off in the
      // delivery plan and flagged to Dmitry for the story entry's PLAT-10263
      // acceptance criterion.)
      return () => {
        videoCreatorRef.current.suppressRendererPlayback = false;
        // Sandbox: restore the originating surface's position on close, scoped
        // to this workspace's videoCreator. In the S&T entry the page player
        // SHARES this creator (the restore reaches it); in the clip entry the
        // bridge restores its own renderer. Stays paused — open cleared
        // isPlaying and closing must never start audio.
        dispatchTranscriptSeek(entryTime, videoCreatorRef.current);
        void videoCreatorRef.current.setTime(entryTime, true);
      };
    }, []);

    // Clip path only (PLAT-10286 Step 3): the review mounts its OWN renderer
    // (`ReviewClipPlayer`), so the preview's renderer must go silent while the
    // review is open — otherwise both play and you get dual audio. Restore its
    // prior play state on close (only resume if it was actually playing, so we
    // never START audio that wasn't). Content Studio has no separate preview
    // renderer, so this is gated off there to keep that path unchanged.
    useEffect(() => {
      if (!isClipPath) return;
      const vc = videoCreatorRef.current;
      const wasPlaying = vc.isPlaying;
      vc.renderer?.pause();
      return () => {
        if (wasPlaying) vc.renderer?.play();
      };
    }, [isClipPath]);

    // Lift the shared body-portaled overlays (context menus, speaker modals,
    // tooltips) above this workspace ONLY while the clip-preview entry is open
    // (liftOverlays). Those overlays read the TOP_LAYER_VARS custom properties,
    // defaulting to their historical z; setting the vars here raises them, and
    // the cleanup clears them so every other surface keeps its resting z. The
    // S&T entry doesn't lift (its lower z already clears them). PLAT-10286.
    useEffect(() => {
      if (!liftOverlays) return;
      const root = document.documentElement;
      const lifts: ReadonlyArray<readonly [string, number]> = [
        [TOP_LAYER_VARS.contextMenu, TOP_LAYER_Z.contextMenu],
        [TOP_LAYER_VARS.modalBackdrop, TOP_LAYER_Z.modalBackdrop],
        [TOP_LAYER_VARS.modal, TOP_LAYER_Z.modal],
        [TOP_LAYER_VARS.tooltip, TOP_LAYER_Z.tooltip],
        // PLAT-11656 — the sidebar too, so it paints crisp beside the page
        // layout's panel instead of under the clip preview's scrim.
        [TOP_LAYER_VARS.sidebar, TOP_LAYER_Z.sidebar],
      ];
      for (const [name, value] of lifts) {
        root.style.setProperty(name, String(value));
      }
      return () => {
        for (const [name] of lifts) root.style.removeProperty(name);
      };
    }, [liftOverlays]);

    // PLAT-11037 — "Copy Review Link": the shareable link for this review IS the
    // current URL. Both entry points keep it in sync while the workspace is open
    // (PLAT-10948): Content Studio S&T writes `?lang` + `?translationReview`, the
    // clip-preview bridge writes the `?clip*` family + `?clipTranslationReview`,
    // and in both cases the review param's VALUE is the granularity. So a reviewer
    // opening this link lands on the same review — same clip, language and
    // granularity — and there is nothing to build beyond reading `location.href`.
    const [linkCopied, setLinkCopied] = useState(false);
    const copiedTimerRef = useRef<ReturnType<typeof setTimeout>>();
    useEffect(
      () => () => {
        if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      },
      [],
    );
    const handleCopyReviewLink = useCallback(async () => {
      await handleCopyToClipboard(window.location.href);
      trackTranslationReview_LinkCopied({
        story_id: videoCreatorRef.current.storyId ?? undefined,
        language,
        entry: isClipPath ? 'clip' : 'story',
        view_mode: viewMode,
      });
      setLinkCopied(true);
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setLinkCopied(false), 3000);
    }, [isClipPath, language, viewMode]);

    // What the bar paints. Clip entry: `playback` (fed by the review renderer).
    // Story entry: the shared clock, plus the local scrub override.
    const barPlaybackState: PlaybackState = isClipPath
      ? playback.playbackState
      : {
          isPlaying: videoCreator.isPlaying,
          currentTime: videoCreator.time,
          duration: playerControls?.duration ?? 0,
          seekTime: storySeekTime,
          isSeeking: storySeeking,
        };

    // PLAT-11512 / PLAT-11618 — Space belongs to THIS player while the
    // workspace is open, in BOTH entries (the clip renderer and the Content
    // Studio story player). It lives up here rather than in `ReviewClipPlayer`
    // because the story entry has no player component of its own to host it,
    // and without a handler the key falls through to the browser and scrolls
    // the page instead. The verbs are whichever player reported them, so the
    // end-of-clip restart and the on-screen control can never drift apart.
    // `ownsKeyUnderBackdrop`: this workspace IS a modal backdrop, so the hook's
    // default stand-down would stand it down against itself. Everything
    // underneath (the clip preview's own bar, the Transcript & Speakers tab)
    // stands down on that same marker, so exactly one player answers the key.
    const barIsPlaying = barPlaybackState.isPlaying;
    const togglePlayback = useCallback(() => {
      if (!playerControls?.isReady) return;
      if (barIsPlaying) playerControls.pause();
      else playerControls.play();
    }, [playerControls, barIsPlaying]);
    usePlayerSpaceShortcut(togglePlayback, { ownsKeyUnderBackdrop: true });

    const rightTabs: Tab<RightTab>[] = [
      {
        id: 'changes',
        label: changeCount > 0 ? `Changes (${changeCount})` : 'Changes',
        icon: <TrackChangesIcon />,
      },
      {
        id: 'translationMemory',
        label:
          tmCount > 0
            ? `Translation Memory (${tmCount})`
            : 'Translation Memory',
        icon: <BookIcon width="16" height="16" strokeColor="currentColor" />,
      },
      {
        id: 'summary',
        label: 'Summary',
        icon: <DocumentLinesIcon />,
      },
      {
        id: 'speakers',
        label: 'Speakers',
        icon: <SpeakersIcon />,
      },
    ];

    // The level the overlay actually paints at. Anything this workspace
    // portals to `document.body` (the header's save popover) must clear it, or
    // it lands under the opaque Surface.
    const overlayZ = isPlayerFullscreen ? Math.max(zIndex, 1000003) : zIndex;

    return createPortal(
      <Overlay
        role={isPage ? 'region' : 'dialog'}
        aria-modal={isPage ? undefined : 'true'}
        aria-label={`Translation review — ${label}`}
        $page={isPage}
        // Full-screen video: the player area is fixed INSIDE this stacking
        // context, so the overlay itself must clear the sidebar (1000002).
        style={{ zIndex: overlayZ }}
        // The panel's left edge is the sidebar's right edge, animated with the
        // sidebar's own spring so they never part. Zero in the modal layout,
        // where the overlay spans the viewport.
        animate={{ left: isPage ? sidebarWidth : 0 }}
        transition={SIDEBAR_SPRING}
        initial={false}
      >
        <GlobalLinkedHighlightStyle />
        {/* Modal only. No click-to-close either way: a 96vw workspace with
            in-pane editors is exactly the accidental-exit hazard, and every
            remaining way out is named (Back, the crumb, the X, Escape). A page
            has no backdrop at all: the app around it is the point. */}
        {!isPage && <Backdrop data-testid="review-backdrop" />}
        <Surface ref={surfaceRef} $page={isPage}>
          <TranslationReviewHeader
            variant={layout}
            languageLabel={label}
            // PLAT-11991 — the story tab is named for the showcase.
            parentTabLabel={transcriptTabLabel(supportedLanguages.length > 0)}
            storyTitle={storyTitle}
            clipTitle={isClipPath ? clip?.title : undefined}
            clipKind={isClipPath ? clipKind : undefined}
            saveStatus={saveStatus}
            lastSavedAt={lastSavedAt}
            savePopoverZIndex={overlayZ + 1}
            reviewMode={reviewMode}
            onReviewModeChange={handleReviewModeChange}
            // PLAT-11706 — what the Dubbing tab has for this language, told
            // before it is opened. Offered = a dub to hear, or a way to make
            // one; with neither the tab carries no status. A run in flight
            // (PLAT-11741) outranks both: the tab is where the reviewer looks
            // for whether Generate took once the modal is out of the way.
            dubStatus={
              isDubGenerating
                ? 'dubbing'
                : dubMonitorEnabled
                  ? 'dubbed'
                  : onRequestDub
                    ? 'not-dubbed'
                    : undefined
            }
            linkCopied={linkCopied}
            onCopyReviewLink={handleCopyReviewLink}
            onRetranslateAll={() => setIsRetranslateOpen(true)}
            onExit={handleExit}
          />
          {/* One tooltip for every rail button (PLAT-11649). Anchored by id
              so it can live here rather than inside a 44px rail, where it
              would take the rail's width. */}

          <Body ref={bodyRef}>
            {
              // All three modes (Paragraph / Segment / Fragment) render as
              // aligned rows spanning the two transcript columns: original ↔
              // translated, one row per block. `AlignedBlocksView` owns its own
              // 2-column grid + scroll-sync + active-row highlight (T5 /
              // PLAT-10264); PLAT-10288 routed Paragraph here too (one aligned
              // row per paragraph group, epic decision Option A), so it no
              // longer mounts the two standalone prose `<Transcript>` panes.
              //
              // The linked cross-language highlight (PLAT-10287) still works:
              // the SAME originalPaneRef / translatedPaneRef are handed to the
              // aligned columns (`originalColumnRef` / `translatedColumnRef`),
              // and the hook is called once at the top of this component.
            }
            <AlignedPane
              aria-label={`Aligned ${viewMode} view — ${label}`}
              data-testid="review-aligned-pane"
              style={canAnimate ? { width: alignedWidthValue } : undefined}
            >
              <AlignedInner
                style={
                  canAnimate
                    ? {
                        width: originalHidden ? alignedWidth : alignedOpenWidth,
                      }
                    : undefined
                }
              >
                <AlignedHeaders $single={originalHidden}>
                  {!originalHidden && (
                    <PaneHeader>
                      <ReferenceLanguagePicker
                        sourceLabel={sourceLabel}
                        referenceLanguage={referenceLanguage}
                        proxyLanguages={proxyLanguages}
                        onChange={setReferenceLanguage}
                        menuZIndex={zIndex + 1}
                      />
                    </PaneHeader>
                  )}
                  <PaneHeader>
                    {
                      // In Dubbing Review the "Original" qualifier drops: the
                      // rail beside this pane already says the original is
                      // collapsed, and a header still opening with that word
                      // would read as "this is the original". In an element, not
                      // a bare text node, so it can ellipsise in the fixed-width
                      // Dubbing column.
                    }
                    {/* PLAT-12025 — on the Dubbing tab, where a highlighted
                        passage can be re-dubbed, the first open says so once
                        per person; the Translation tab, which cannot, never
                        shows it. */}
                    <SectionRedubHint
                      show={
                        originalHidden &&
                        dubMonitorEnabled &&
                        !!onRequestReDubSelection &&
                        !sectionRedubTaught
                      }
                    >
                      <PaneHeaderLabel
                        title={
                          originalHidden
                            ? `Translation · ${label}`
                            : `Original Translation · ${label}`
                        }
                      >
                        {originalHidden
                          ? `Translation · ${label}`
                          : `Original Translation · ${label}`}
                      </PaneHeaderLabel>
                    </SectionRedubHint>
                    {/* PLAT-11649 — the transcript's own controls live in the
                      transcript's own header: the row granularity (Paragraph /
                      Segment / Words) beside the pane's label, and Split into
                      Words when in the Words view. */}
                    <TranscriptViewControls
                      viewMode={viewMode}
                      onViewModeChange={handleViewModeChange}
                      splitAll={{
                        canSplit: canSplitWhole,
                        isSplitting: isSplittingWhole,
                        onSplit: handleSplitWholeTranslation,
                      }}
                      menuZIndex={zIndex + 1}
                    />
                  </PaneHeader>
                </AlignedHeaders>
                <AlignedBody>
                  {/* PLAT-12024 — inside this column, so the pulse finds the
                      review's own words and never the host transcript's. */}
                  {videoCreator.storyId && (
                    <RedubLandedPulse
                      storyId={videoCreator.storyId}
                      language={language}
                      landed={landedRedub ?? null}
                    />
                  )}
                  <AlignedBlocksView
                    language={language}
                    videoCreator={videoCreator}
                    transcriptElements={sourcedArray}
                    viewMode={viewMode}
                    originalColumnRef={originalPaneRef}
                    translatedColumnRef={translatedPaneRef}
                    hideOriginal={originalHidden}
                    montagePlaybackOverride={
                      sessionStore && clip
                        ? {
                            montageTime: playback.playbackState.currentTime,
                            isPlaying: playback.playbackState.isPlaying,
                          }
                        : undefined
                    }
                    onMontageSeek={
                      sessionStore && clip
                        ? montageTime =>
                            setPendingMontageSeek(prev => ({
                              montageTime,
                              nonce: (prev?.nonce ?? 0) + 1,
                            }))
                        : undefined
                    }
                    // True once the clip renderer's editor is ready and reporting
                    // montage time (PLAT-10464). Gates the highlight so it never
                    // runs against a not-yet-valid `currentTime`. The elements'
                    // `ts`/`end_ts` are already the montage clock, so no per-element
                    // timing needs to arrive first.
                    montageReady={isClipPath && montageReady}
                    dubPlayhead={
                      isClipPath ? clipDubPlayhead : storyDubPlayhead
                    }
                    // PLAT-10268 (QA round 2) — coverage DISPLAY is Segment-view
                    // only: the spec (Jira AC / epic plan D4) describes one badge
                    // at the end of the analyzed SEGMENT and never mentions the
                    // other views. Withholding the props unmounts every coverage
                    // indicator (spinner, badge, details popover) in Paragraph /
                    // Fragment view. The `onPhraseEditSaved` TRIGGER below stays
                    // wired in ALL views — a popover save must still run the
                    // check; only the display is view-gated.
                    {...(viewMode === 'segment'
                      ? {
                          coverageByLead: coverageByLeadUuid,
                          loadingCoverageLeads,
                          openCoverageLead,
                          onCoverageBadgeClick: handleCoverageBadgeClick,
                          onCoverageClose: handleCoverageClose,
                          onCoverageUndo: handleCoverageUndo,
                          // PLAT-10257 (A4) — the popover's "Fix Translation"
                          // entry into the shared two-mode span editor.
                          onCoverageFixTranslation:
                            handleCoverageFixTranslation,
                        }
                      : {})}
                    // PLAT-10268 — the phrase-editor popover save (the re-fix
                    // path: click green text / right-click → Edit translation)
                    // never went through `useSpanReplace.confirm`, so it never
                    // triggered a coverage check. Run the SAME lifecycle the
                    // span-replace confirm uses, over the edited phrase's
                    // source indices.
                    onPhraseEditSaved={coveredElementIds =>
                      handleSpanConfirmed({ lang: language, coveredElementIds })
                    }
                    // PLAT-11335 — always-on (not inside the segment-only spread
                    // above): feeds the phrase-editor popover's badge in every view.
                    resolveCoverageForPhrase={resolveCoverageForPhrase}
                    // PLAT-11678 — the automatic re-dub's per-segment status.
                    // Paragraph and Segment only: Words is not a working zoom
                    // for dubbing (Roman, Sep 11), and its rows are too dense
                    // for a status line.
                    {...(viewMode !== 'fragment'
                      ? {
                          dubStateBySegment: autoRedub.stateBySegment,
                          onRetryDub: autoRedub.retry,
                        }
                      : {})}
                    // PLAT-12034 — delivery-marker chips inside the dubbed
                    // lines, on the Dubbing tab only, under the same Words-view
                    // gate as the status rows. Edit Markers… needs the host's
                    // re-dub hand-off, and Remove the automatic re-dub, so
                    // each is offered only where that is.
                    {...(viewMode !== 'fragment' && chipsEnabled
                      ? {
                          dubMarkers: dubMarkersByLine,
                          ...(autoRedubEnabled
                            ? { onMarkerRemove: handleMarkerRemove }
                            : {}),
                          ...(onRequestReDubSelection
                            ? { onMarkerEdit: handleMarkerEdit }
                            : {}),
                        }
                      : {})}
                    splitControls={splitControls}
                    revealRequest={revealRequest}
                    onChangedPhraseHover={setHoveredChangeKey}
                    hoveredChangeIndices={hoveredChangeIndices}
                    referenceLanguage={referenceLanguage}
                    // PLAT-10266 (Card 1) — amber stale marks on the fragments
                    // whose source words the reviewer edited. Whole-story host only.
                    // Card 5/F1: the set handed to the view is the currently-stale
                    // leads whose EDIT-KEYED signature differs from the resolved one
                    // (`visibleStaleLeads`), so resolving a fragment clears its amber
                    // while its source is unchanged, but a fresh edit re-flags it.
                    staleLeads={visibleStaleLeads}
                    // PLAT-10266 (Card 2) — right-click an amber fragment →
                    // "Re-translate (machine)". Wired only on the whole-story host
                    // (same gate as the editable-original affordance), so the clip
                    // host and shared prose homes never show it (AC6).
                    onResolveRetranslate={
                      editableOriginalEnabled
                        ? handleResolveRetranslate
                        : undefined
                    }
                    // PLAT-10266 (Card 3) — the "Replace manually" sibling action,
                    // wired under the SAME whole-story gate as re-translate, so the
                    // clip host and shared prose homes never show it (AC6).
                    onResolveManual={
                      editableOriginalEnabled ? handleResolveManual : undefined
                    }
                    // PLAT-10266 (Card R2) — the highlight path's two joins: the lead of
                    // the current single-stale selection (right-click routes on it before
                    // the precedence bail) and the opener the highlight calls.
                    staleSelectionLead={
                      editableOriginalEnabled ? staleSelectionLead : null
                    }
                    resolveMenuOpenerRef={
                      editableOriginalEnabled ? resolveMenuOpenerRef : undefined
                    }
                    tmEntriesById={tmEntriesById}
                    tmCollectionNameById={tmCollectionNameById}
                    onPromoteToTm={
                      canPromoteToTm ? seed => setPromotingTm(seed) : undefined
                    }
                    promotedTmEntryIds={promotedTmEntryIds}
                    onViewTmEntry={
                      canPromoteToTm ? handleViewTmEntry : undefined
                    }
                  />
                </AlignedBody>
              </AlignedInner>
            </AlignedPane>

            <RightColumn
              data-testid="review-right-column"
              style={canAnimate ? { width: rightWidthValue } : undefined}
            >
              {
                // The player, at the top of the right column in BOTH modes —
                // one DOM node in one place, so the clip renderer's iframe is
                // never remounted and nothing teleports on a mode switch; the
                // column's width is all that changes.
              }
              <PlayerArea
                $inset={videoExpanded}
                $fullscreen={isPlayerFullscreen}
                style={isPlayerFullscreen ? { zIndex: zIndex + 2 } : undefined}
              >
                {
                  // PLAT-11683 — the row above the player exists in BOTH modes
                  // now, for the Captions picker. In Translation Review it
                  // carries Captions alone (no audio label: the docked player
                  // is a compact monitor); in Dubbing Review the audio label
                  // and the Voice / Music pickers join it as before.
                }
                {!isPlayerFullscreen && (
                  <PlayerHeaderRow>
                    <PlayerHeaderLabel>
                      {videoExpanded
                        ? dubMonitorEnabled
                          ? dubUpdatingCount > 0
                            ? `Dubbed · ${label} · updating ${dubUpdatingCount}`
                            : `Dubbed · ${label}`
                          : // PLAT-11714 / PLAT-11741 — while the dub the
                            // reviewer just asked for is being made, the label
                            // stops saying there isn't one, in the tab's own
                            // words; the progress across the row says what is
                            // happening instead.
                            isDubGenerating
                            ? `Dubbing · ${label}`
                            : // PLAT-11685 — a language with no dub says so,
                              // rather than describing the audio, when this
                              // surface can do something about it.
                              onRequestDub
                              ? `Not dubbed · ${label}`
                              : 'Original audio'
                        : null}
                    </PlayerHeaderLabel>
                    {
                      // PLAT-10600 (T12) / PLAT-11665: the review's quick Voice
                      // (Original / Dubbed / Both — `Both` is the hear-both
                      // overlay for A-B review) and Music (None / From Original)
                      // choices, in the pane header's picker style so every
                      // control on the page reads as one family. Shown only
                      // when dubbing is enabled and the reviewed language has a
                      // dub. PLAYER settings, one step below the mode tabs.
                    }
                    <ReviewPlayerAudioPickers
                      captionsMode={captionsMode}
                      onCaptionsModeChange={handleCaptionsModeChange}
                      captionsOnHint={captionsOnHint}
                      captionsUnavailable={captionsUnavailable}
                      captionsPreviewReason={captionsPreviewReason}
                      captionTemplateChoice={reviewCaptionTemplateChoice}
                      showAudio={videoExpanded && dubMonitorEnabled}
                      audioMode={audioMode}
                      onAudioModeChange={handleAudioModeChange}
                      musicMode={musicMode}
                      onMusicModeChange={handleMusicModeChange}
                      musicAvailable={!!dubMusicBedUrl}
                      musicSeparation={musicSeparation}
                      levels={levels}
                      onLevelsChange={setLevels}
                      // Only a clip has levels to save to.
                      onApplyLevels={
                        isClipPath && onApplyToClip ? applyLevels : undefined
                      }
                      appliedLevels={appliedLevels}
                      levelsLine={levelsLine}
                      // Only a clip's player plays the lead-in.
                      leadIn={leadIn}
                      appliedLeadIn={clipLeadIn}
                      onLeadInChange={
                        isClipPath && onApplyToClip ? setLeadIn : undefined
                      }
                      menuZIndex={zIndex + 1}
                    />
                    {
                      // PLAT-11685 — and where the Voice / Music pickers would
                      // be when there is no dub to set: the way to make one.
                      // The label beside it already says "Not dubbed", so the
                      // button is the whole of the state — a line and an
                      // action, in the player settings row, not an empty state
                      // over the picture (the translation beside it is still
                      // worth reading while undubbed). Dubbing Review only:
                      // this row carries Captions in Translation Review too
                      // (PLAT-11683), where a dub is not the subject.
                    }
                    {
                      // PLAT-11741 — and not the ask, while the run it started
                      // is in flight: the progress takes the button's place, so
                      // there is no second run to start from here.
                    }
                    {videoExpanded &&
                      !dubMonitorEnabled &&
                      (activeDubRun ? (
                        <DubRunProgress run={activeDubRun} />
                      ) : (
                        onRequestDub && (
                          // PLAT-11706 — the button's label says what, not what
                          // happens: the hint beside it says what a press
                          // dubs and that the review of it is here. Once, per
                          // browser; gone the moment the person acts. The two
                          // entries dub different things — the clip entry's
                          // `onRequestDub` fills this clip's own lines, the
                          // story entry's opens the whole-story modal — and
                          // this one lesson is the only place that is said.
                          <ContextualHint
                            hintKey={REVIEW_FIRST_DUB_HINT_KEY}
                            show
                            text={`No dub yet. Dub with AI Voice generates the ${label} dub for ${
                              isClipPath
                                ? `this ${clipKind ?? 'Clip'}`
                                : 'the whole story'
                            }, and you review it here.`}
                            testId="review-first-dub-hint"
                          >
                            <ReviewDubButton
                              type="button"
                              onClick={() => {
                                dismissContextualHint(
                                  REVIEW_FIRST_DUB_HINT_KEY,
                                );
                                onRequestDub(language);
                              }}
                              data-testid="review-dub-generate-button"
                            >
                              <MicrophoneIcon
                                width="14"
                                height="14"
                                fillColor="currentColor"
                              />
                              Dub with AI Voice
                            </ReviewDubButton>
                          </ContextualHint>
                        )
                      ))}
                  </PlayerHeaderRow>
                )}
                <PlayerFrame
                  $rounded={videoExpanded}
                  $fullscreen={isPlayerFullscreen}
                >
                  {sessionStore && clip ? (
                    // Clip path (PLAT-10286 Step 3): the review's OWN renderer
                    // shows the edited montage. Gated so the Content Studio
                    // whole-story entry below stays on the original-video
                    // player.
                    <ReviewClipPlayer
                      story={story}
                      storyId={videoCreator.storyId ?? null}
                      sessionStore={sessionStore}
                      clip={clip}
                      language={language}
                      poster={poster}
                      dubMonitorEnabled={dubMonitorEnabled}
                      audioMode={audioMode}
                      musicMode={musicMode}
                      voiceVolume={voiceVolume}
                      originalVolume={originalVolume}
                      segmentLevels={segmentLevels}
                      leadIn={leadIn}
                      dubMusicBedUrl={dubMusicBedUrl}
                      dubVoiceStemUrl={dubVoiceStemUrl}
                      dubReloadToken={dubReloadToken}
                      onTimeChange={playback.onTimeChange}
                      onPlay={playback.onPlay}
                      onPause={playback.onPause}
                      onDurationChange={playback.onDurationChange}
                      isPlaying={playback.playbackState.isPlaying}
                      currentTime={playback.playbackState.currentTime}
                      duration={playback.playbackState.duration}
                      pendingSeek={pendingMontageSeek}
                      captionFallbackTemplate={captionFallbackTemplate}
                      pickedCaptionTemplate={pickedCaptionTemplate}
                      captionsMode={captionsMode}
                      onCaptionTrackAtOpen={handleCaptionTrackAtOpen}
                      onMontageReady={() => setMontageReady(true)}
                      onControls={setPlayerControls}
                      onDubPlayhead={setClipDubPlayhead}
                    />
                  ) : (
                    <>
                      <TranscriptPlayerWithMagnifier
                        videoCreator={videoCreator}
                        zoomLevel={zoomLevel}
                        timeRange={timeRange}
                        anchorToCreatorTimeOnLoad
                        poster={poster}
                        // Silenced only while a dub plays over it: without a
                        // dub to monitor the picture keeps its own voice.
                        muted={dubMonitorEnabled && audioMode === 'dubbed'}
                        // PLAT-11927 — under the dub on Both, at the
                        // review's Original level.
                        audioLevel={
                          dubMonitorEnabled && audioMode === 'both'
                            ? originalVolume / FULL_DUB_LEVEL
                            : 1
                        }
                        // The bar below is the one set of controls on screen.
                        externalControls
                        onControls={setPlayerControls}
                        // PLAT-11683 — captions live over the picture now; the
                        // magnifier stays a Transcript & Speakers tool.
                        hideMagnifier
                      />
                      {
                        // PLAT-11683 — the story entry plays a plain video, so
                        // the review draws its caption track over it itself.
                      }
                      {captionsMode === 'on' &&
                        !captionsUnavailable &&
                        storyCaptions && (
                          <ReviewCaptionOverlay
                            lines={storyCaptions.karaokeLines}
                            config={
                              storyCaptions.editor?.getDefaultKaraokeConfig() ??
                              null
                            }
                            timeSec={videoCreator.time}
                          />
                        )}
                    </>
                  )}
                </PlayerFrame>
                {
                  // PLAT-11657 — the clip preview's own transport bar, in BOTH
                  // modes (small under the docked player, wide in Dubbing), so
                  // the review player scrubs, jogs and goes full screen the
                  // way every other Arbor player does. Fullscreen is laid out
                  // by PlayerArea itself (the bar stays in flow), because the
                  // bar's own fixed variant pins a z-index below the clip
                  // entry's panel.
                }
                {
                  <PlayerBar
                    data-testid="review-player-bar"
                    $rounded={videoExpanded}
                    $fullscreen={isPlayerFullscreen}
                  >
                    <ClipPreviewControlBar
                      playbackState={barPlaybackState}
                      isReady={playerControls?.isReady ?? false}
                      play={() => playerControls?.play()}
                      pause={() => playerControls?.pause()}
                      setTime={time => playerControls?.setTime(time)}
                      setSeekTime={
                        isClipPath ? playback.setSeekTime : setStorySeekTime
                      }
                      setIsSeeking={
                        isClipPath ? playback.setIsSeeking : setStorySeeking
                      }
                      onToggleFullscreen={() =>
                        setIsPlayerFullscreen(value => !value)
                      }
                      // The docked player is 360px wide; the clip preview's
                      // bar is never that narrow.
                      compact={!videoExpanded}
                      volumeControls={playerControls?.volume}
                    />
                  </PlayerBar>
                }
              </PlayerArea>
              {
                // PLAT-11516 — the shared Tabs in its icon-only presentation,
                // under the player in both modes.
              }
              <RightTabsRow>
                <Tabs<RightTab>
                  variant="icon-only"
                  activeTab={rightTab}
                  onChange={setRightTab}
                  listTestId="review-right-tabs"
                  // Inside the workspace overlay, so the modal tooltip token.
                  tooltipStyle={MODAL_TOOLTIP_STYLE}
                  tabs={rightTabs}
                />
              </RightTabsRow>
              <RightPanelBody data-testid="review-right-panel">
                <RightPanelScroll $tight={videoExpanded}>
                  {rightTab === 'changes' ? (
                    <TranslationChangesPanel
                      language={language}
                      videoCreator={videoCreator}
                      resolveCoverage={resolveCoverageForPhrase}
                      onReverted={autoRedub.notifyCommit}
                      onCommitted={autoRedub.notifyCommit}
                      onEntryHover={setHoveredChangeIndices}
                      onEntryReveal={initialIndex =>
                        setRevealRequest(prev => ({
                          initialIndex,
                          nonce: (prev?.nonce ?? 0) + 1,
                        }))
                      }
                      highlightedEntryKey={hoveredChangeKey}
                      tmEntriesById={tmEntriesById}
                      promotedTmEntryIds={promotedTmEntryIds}
                      onViewTmEntry={
                        canPromoteToTm ? handleViewTmEntry : undefined
                      }
                      onPromoteToTm={
                        canPromoteToTm
                          ? seed => setPromotingTm(seed)
                          : undefined
                      }
                    />
                  ) : rightTab === 'translationMemory' ? (
                    <TranslationMemoryPanel
                      language={language}
                      videoCreator={videoCreator}
                      entriesById={tmEntriesById}
                      collectionNameById={tmCollectionNameById}
                      onPromoteToTm={
                        canPromoteToTm
                          ? seed => setPromotingTm(seed)
                          : undefined
                      }
                      promotedTmEntryIds={promotedTmEntryIds}
                      onViewTmEntry={
                        canPromoteToTm ? handleViewTmEntry : undefined
                      }
                      onEditEntry={setEditingTmEntry}
                      onEntryHover={setHoveredChangeIndices}
                      onEntryReveal={initialIndex =>
                        setRevealRequest(prev => ({
                          initialIndex,
                          nonce: (prev?.nonce ?? 0) + 1,
                        }))
                      }
                    />
                  ) : rightTab === 'summary' ? (
                    // Summary scaffold (D12). Provenance is a minimal read of
                    // TranslationData for now — the richer provenance content
                    // (Epic 4 Story D `TranslationProvenancePopover`) and the
                    // coverage-result slot (T8) layer in later.
                    <Summary>
                      {translation ? (
                        <>
                          <SummaryRow>
                            <SummaryKey>Source</SummaryKey>
                            <SummaryVal>
                              {(() => {
                                const source =
                                  resolvePreviousSource(translation);
                                return source
                                  ? TRANSLATION_SOURCE_SHORT_LABEL[
                                      source.sourceId
                                    ]
                                  : '—';
                              })()}
                            </SummaryVal>
                          </SummaryRow>
                          <SummaryRow>
                            <SummaryKey>Handler</SummaryKey>
                            <SummaryVal>
                              {translation.handler?.id ?? '—'}
                            </SummaryVal>
                          </SummaryRow>
                          <SummaryRow>
                            <SummaryKey>Created</SummaryKey>
                            <SummaryVal>
                              {translation.createdAt ?? '—'}
                            </SummaryVal>
                          </SummaryRow>
                          <SummaryRow>
                            <SummaryKey>Modified</SummaryKey>
                            <SummaryVal>
                              {translation.modifiedAt ?? '—'}
                            </SummaryVal>
                          </SummaryRow>
                          <SummaryRow>
                            <SummaryKey>Phrases</SummaryKey>
                            <SummaryVal>
                              {translation.phrases.length}
                            </SummaryVal>
                          </SummaryRow>
                          <SummaryRow>
                            <SummaryKey>Editors</SummaryKey>
                            <SummaryVal>
                              {(() => {
                                const editors = collectEditors(translation);
                                return editors.length > 0 ? (
                                  <EditorList>
                                    {editors.map(name => (
                                      <li key={name}>{name}</li>
                                    ))}
                                  </EditorList>
                                ) : (
                                  '—'
                                );
                              })()}
                            </SummaryVal>
                          </SummaryRow>
                          {/* PLAT-10268 (T8) — coverage result slot. Plain text
                              for this atom; the color-banded badge is Atom 5. */}
                          <SummaryRow>
                            <SummaryKey>Coverage</SummaryKey>
                            <SummaryVal>
                              {(() => {
                                if (coverageRequest?.status === 'loading') {
                                  return 'Checking…';
                                }
                                if (coverageRequest?.status === 'error') {
                                  return 'Check failed';
                                }
                                const verdict =
                                  coverageRequest != null
                                    ? verdictForLead(
                                        coverageByLeadUuid,
                                        coverageRequest.lead,
                                        translation.phrases,
                                      )
                                    : undefined;
                                if (!verdict) return '—';
                                return `${verdict.coveragePercent}% · ${
                                  verdict.status
                                }${
                                  verdict.explanation
                                    ? ` — ${verdict.explanation}`
                                    : ''
                                }`;
                              })()}
                            </SummaryVal>
                          </SummaryRow>
                        </>
                      ) : (
                        <SummaryEmpty>No translation loaded.</SummaryEmpty>
                      )}
                    </Summary>
                  ) : (
                    <CreatorStudioSpeakersPanel
                      useOriginalTranscript
                      videoCreator={videoCreator}
                      timeRange={timeRange}
                    />
                  )}
                </RightPanelScroll>
              </RightPanelBody>
            </RightColumn>
          </Body>
        </Surface>

        {
          // PLAT-10266 (Card 1) — EDITABLE ORIGINAL. The reused "Replace Term"
          // context menu + modal, mounted over the original column ONLY on the
          // whole-story host (gated by `editableOriginalEnabled`). Both are
          // body-portaled with their own resting z (menu 1000011, modal backdrop
          // 1000010), which clears this workspace's z (1000004) on the S&T entry.
          // The selection is already index-translated to array positions.
        }
        {editableOriginalEnabled && originalMenu && (
          <TranscriptContextMenu
            positionXY={originalMenu.positionXY}
            actions={['replace']}
            actionCallback={action => {
              if (action === 'replace') {
                setOriginalReplace(originalMenu.selection);
              }
              setOriginalMenu(null);
            }}
          />
        )}
        {editableOriginalEnabled && originalReplace && (
          <WordReplacementModal
            selection={originalReplace}
            transcriptElements={sourcedArray}
            discard={() => setOriginalReplace(null)}
            onSubmit={selections => {
              // Single-occurrence only this slice (DESIGN §12 rec b): keep the
              // edit 1:1 with the selected word so the amber flag lights exactly
              // one fragment. "Replace all instances" is a deferred follow-up.
              const single =
                selections.find(
                  s => s.startIndex === originalReplace.startIndex,
                ) ?? selections[0];
              if (single) handleTranscriptTextReplace([single]);
              setOriginalReplace(null);
            }}
          />
        )}

        {
          // PLAT-10214 (T6) — span replace. The "Fix Translation" trigger floats
          // at the selected run (only when no editor is open); the editor opens on
          // its click. Both are `position: fixed` (anchored to the selection word)
          // so they render at the Overlay level, above the panes.
        }
        {spanReplace.pendingSpan && !spanReplace.editor && (
          <SpanReplaceTrigger
            anchorElement={spanReplace.pendingSpan.anchorElement}
            onFixTranslation={spanReplace.openEditor}
            // PLAT-11340 follow-up — "Remove Translation". Wired here only:
            // `SpanReplaceTrigger`'s other host (`InlineSpanReplaceLayer`) is
            // the inline prose surface and passes nothing, so its menu is
            // unchanged.
            onRemove={spanReplace.removeSpan}
            // PLAT-11382 — "Re-dub AI Voice". The row is in the menu only when
            // the host takes the request AND the selection resolves to at least
            // one dub line; `canReDub` is the hook's own answer to both.
            onReDub={spanReplace.canReDub ? spanReplace.reDub : undefined}
          />
        )}
        {spanReplace.editor && (
          /* Remount when the span's stored text changes under the open editor.
             The input pre-fills from `currentValue` ONCE, so if the span is
             reopened over changed store data (a revert, a reopen from the
             coverage popover) the previous text would still be sitting in the
             box, one Save away from being written straight back.
             `editableValue` is a snapshot taken when the span is (re)opened, so
             this changes only on a genuine reopen — never while the reviewer is
             typing. */
          <ReplaceSpanEditor
            key={spanReplace.editor.editableValue}
            anchorElement={spanReplace.editor.anchorElement}
            sourceText={spanReplace.editor.model.originalSnapshot}
            sourceLanguageLabel={
              translation?.sourceLanguage
                ? formatLanguageCaption(translation.sourceLanguage)
                : undefined
            }
            currentValue={spanReplace.editor.editableValue}
            originalValue={spanReplace.editor.originalTranslatedValue}
            wordCount={spanReplace.editor.model.coveredIndices.length}
            replacingWordCount={spanReplace.editor.replacingWordCount}
            onConfirm={spanReplace.confirm}
            onCancel={spanReplace.cancel}
            onRevert={spanReplace.revertOverride}
            revertDisabled={spanReplace.revertDisabled}
            editedBy={spanReplace.editor.editedBy}
            blockedMessage={
              spanReplace.editor.blockedReason
                ? spanBlockMessage(spanReplace.editor.blockedReason)
                : undefined
            }
            // PLAT-10257 (A3) — the second mode behind the same entry (locked
            // decision: no new trigger item). Success closes via the hook and
            // fires the same onSpanConfirmed coverage kick as manual — and no
            // fine-split (directed keeps createOverrideSegment's tiling).
            //
            // PLAT-10266 (2026-07-28) — WITHHELD on the stale-flag entry, whose
            // contract is a straight machine re-translation, explicitly not a
            // directed one. Withholding it is what removes the mode toggle and the
            // instruction box (`ReplaceSpanEditor` renders manual-only without it);
            // `onRegenerate` below supplies the machine option in its place.
            onConfirmDirected={
              staleEntryForOpenEditor ? undefined : spanReplace.confirmDirected
            }
            // The stale-flag entry's machine option: NO instruction to write. Routes
            // through the SHIPPED `handleResolveRetranslate`, which reads the branch
            // off the open editor's `existingOverrideSegmentId` and picks the
            // in-place override path or `confirmDirected('')` accordingly — so this
            // reuses the tested path rather than re-deriving it here.
            onRegenerate={
              staleEntryForOpenEditor
                ? () => handleResolveRetranslate(staleEntryForOpenEditor.lead)
                : undefined
            }
            // Why the last regenerate was refused (PLAT-10266, 2026-07-28). The op runs
            // in the effect above, so its reason comes back down as a prop rather than
            // from the editor's own submit handler.
            regenerateFailureReason={regenerateFailure ?? undefined}
            // The RE-EDIT (within-fix) case AND the absorb case (PLAT-11655):
            // manual editing stays, instruction mode is unavailable — the
            // directed path refuses any span touching a correction (locked
            // decision 2), so the notice says so up front instead of letting
            // a submit fail.
            instructionModeBlockedMessage={
              spanReplace.editor.model.existingOverrideSegmentId ||
              spanReplace.editor.overlappingOverrideSegmentIds?.length
                ? SPAN_MACHINE_RETRANSLATE_UNAVAILABLE_MESSAGE
                : undefined
            }
          />
        )}

        {
          // PLAT-10258 (Atom 4) — the whole-translation directed re-translate
          // modal, opened from the header action above. It orchestrates the
          // rebase on the same whole-story translationStore this workspace
          // holds. Mounted inside the workspace so it inherits the scoped
          // StoreContext and only exists under `enableTranslationReview` (D9
          // containment — no new flag). The shared Modal shell portals itself to
          // document.body.
        }
        <DirectedRetranslateModal
          language={language}
          translationStore={translationStore}
          isOpen={isRetranslateOpen}
          onClose={() => setIsRetranslateOpen(false)}
        />
        {/* PLAT-10878 — inline edit of the TM entry behind a reviewed fragment,
            reusing the Settings entry editor in edit mode. */}
        {editingTmEntry && (
          <EntryFormModal
            mode="edit"
            entry={editingTmEntry}
            languages={tmLanguages}
            collections={tmCollectionOptions}
            defaultSourceLanguageId={tmPrimarySourceLanguageId}
            initialCollectionId={editingTmEntry.collectionId}
            onClose={() => setEditingTmEntry(null)}
            onSubmit={async input => {
              const error = await handleUpdateTmEntry(input);
              if (!error) setEditingTmEntry(null);
              return error;
            }}
          />
        )}
        {/* PLAT-10852 (E15) — promote the reviewer's manual translation into a
            new entry (`origin=promoted`), seeded with the fragment's texts and
            the run's language pair. */}
        {promotingTm && (
          <EntryFormModal
            mode="create"
            languages={tmLanguages}
            collections={tmCollectionOptions}
            defaultSourceLanguageId={
              resolveTmLanguageId(translation?.sourceLanguage) ??
              tmPrimarySourceLanguageId
            }
            initialCollectionId={null}
            initialSourceTerm={promotingTm.sourceTerm}
            initialTranslation={promotingTm.translation}
            initialTranslationLanguageId={resolveTmLanguageId(language) ?? ''}
            onClose={() => setPromotingTm(null)}
            onSubmit={async input => {
              const error = await handlePromoteToTm(input);
              if (!error) setPromotingTm(null);
              return error;
            }}
          />
        )}
      </Overlay>,
      document.body,
    );
  });

// Global `::highlight()` rules for the linked cross-language highlight
// (PLAT-10287) AND the span-replace original-side lock (PLAT-10214, D3).
// Mounted only while the workspace is open, so the rules exist just for this
// surface. The Highlight API registry is document-global, but the painted
// ranges are built per-pane (range provenance) — see the hooks. The two use
// distinct highlight names + hues so they never collide.
const GlobalLinkedHighlightStyle = createGlobalStyle`
  ${linkedHighlightCss}
  ${spanReplaceLockCss}
`;

// PLAT-10463 — how close to `duration` counts as "ended" for the replay-from-0
// path below. Tight enough not to mistake a manual pause for an end; generous
// enough to absorb a slight final-frame undershoot from the renderer's last
// time report.
const END_EPSILON_SECONDS = 0.25;

interface ReviewClipPlayerProps {
  story?: Story;
  /** The story the dub description is fetched for; `null` fetches nothing. */
  storyId: string | null;
  sessionStore: ClipEditSessionStore;
  clip: VideoClipPreview;
  /** Reviewed target language (BCP 47) — applied to the player's own editor. */
  language: string;
  poster?: string;
  /**
   * PLAT-11340 — the review's transient Original/Dubbed/Both choice, and
   * whether the reviewed language has a dub to hear at all. Written onto THIS
   * player's own editor as its dub options, so the dub engine composites the
   * dub into the review's document the same way the clip preview does.
   */
  dubMonitorEnabled: boolean;
  audioMode: AudioMode;
  /** PLAT-11665 — the review's Music choice, written with the voice. */
  musicMode: DubMusicMode;
  /** PLAT-11899 — the review's dub level, written with the voice. */
  voiceVolume: number;
  /** PLAT-11927 — the review's original level on Both, written with it. */
  originalVolume: number;
  /** The review's lines at their own levels, written with them. */
  segmentLevels: DubSegmentLevels | undefined;
  /** The review's original lead-in on Both, written with the voice. */
  leadIn: DubLeadIn | undefined;
  /** The story's kept music bed, for the engine to lay under the dub. */
  dubMusicBedUrl: string | null;
  /** The separated voice, for the engine to lay a lead-in from. */
  dubVoiceStemUrl: string | null;
  /**
   * PLAT-11678 — bumped when an automatic re-dub lands; the dub description
   * held in this player's editor is fetched again so the next play carries
   * the new words.
   */
  dubReloadToken: number;
  onTimeChange: (time: number) => void;
  onPlay: () => void;
  onPause: () => void;
  onDurationChange: (duration: number) => void;
  isPlaying: boolean;
  /** Renderer's montage-clock current time + duration, for end-of-play detection. */
  currentTime: number;
  duration: number;
  pendingSeek: { montageTime: number; nonce: number } | null;
  /**
   * PLAT-10464: fired once the clip renderer's editor is ready and reporting
   * montage time. The parent uses it to release the highlight gate; the
   * elements' `ts`/`end_ts` already carry the montage clock, so no timing
   * payload is needed. Kept here (not lifted) so the clip-only renderer hooks
   * stay off the Content Studio whole-story entry.
   */
  onMontageReady: () => void;
  /** PLAT-10635: custom-template seed applied to the review's own editor. */
  captionFallbackTemplate?: CaptionFallbackTemplate;
  /**
   * PLAT-11702 follow-up — the showcase caption template the reviewer picked
   * in the Captions dropdown, applied over whatever this editor is showing.
   * `null` ⇒ no pick yet; the seed above decides. Review only, like the rest.
   */
  pickedCaptionTemplate?: CaptionFallbackTemplate | null;
  /**
   * PLAT-11683 — the review's Captions choice, applied to THIS player's own
   * editor only: On produces a review-only track when the clip has none, Off
   * removes the track from this editor. The clip's saved track is untouched
   * either way — PLAT-11702 removed the write path entirely, so nothing the
   * review does reaches the clip.
   */
  captionsMode: CaptionsMode;
  /**
   * Reports once, when the editor is ready, whether the clip had a caption
   * track — and which template its style came from, so the Captions dropdown
   * can name the clip's own row after it rather than "Clip's Style".
   */
  onCaptionTrackAtOpen: (hadTrack: boolean, templateId?: string | null) => void;
  /** PLAT-11657 — the verbs for the workspace's transport bar. */
  onControls?: (controls: ExternalPlayerControls | null) => void;
  /**
   * PLAT-11906 — the dub this player's editor makes heard, on its clock, for
   * the transcript's reading; `null` while the voice is Original or no
   * description is held.
   */
  onDubPlayhead?: (dub: DubOnReviewClock | null) => void;
}

/**
 * Clip path only (PLAT-10286 Step 3): the review's OWN Creatomate renderer for
 * the edited montage — an INDEPENDENT second renderer from the clip preview's,
 * mirroring `SocialClipShareModal`'s preview chain (`useClipPreviewRenderer` →
 * `useRendererPlayback` → `CreatomateClipPreview`). It lives in its own
 * component so the clip-only renderer hooks never run on the shared Content
 * Studio path (rules of hooks) — the parent decides whether to mount it. Reuses
 * the threaded `sessionStore` so the review reflects the same edited clip as the
 * preview, but with its own renderer/container. Drives the parent's
 * `usePlaybackState` callbacks so `playbackState` is ready for Step 4.
 */
const ReviewClipPlayer: React.FC<ReviewClipPlayerProps> = observer(
  ({
    story,
    storyId,
    sessionStore,
    clip,
    language,
    poster,
    dubMonitorEnabled,
    audioMode,
    musicMode,
    voiceVolume,
    originalVolume,
    segmentLevels,
    leadIn,
    dubMusicBedUrl,
    dubVoiceStemUrl,
    dubReloadToken,
    onTimeChange,
    onPlay,
    onPause,
    onDurationChange,
    isPlaying,
    currentTime,
    duration,
    pendingSeek,
    captionFallbackTemplate,
    pickedCaptionTemplate,
    captionsMode,
    onCaptionTrackAtOpen,
    onMontageReady,
    onControls,
    onDubPlayhead,
  }) => {
    const sharedRendererProps = useClipPreviewRenderer({
      story,
      // 'player' mode skips the warm-pool claim, so the review COLD-creates its
      // OWN renderer + iframe in its own container — independent of the preview's
      // (the preview keeps the warm renderer). See useClipPreviewRenderer.ts:164.
      // play/pause/setTime are exposed regardless of mode (Step 4 needs them).
      mode: 'player',
      // Open paused — no audio until the user presses play. The pause-on-open
      // effect silences the preview's (now separate) renderer.
      autoPlay: false,
      sessionStore,
      clipPropForReadiness: clip,
    });
    useRendererPlayback({
      renderer: sharedRendererProps.renderer,
      editorStore: sharedRendererProps.editorStore,
      isReady: sharedRendererProps.isReady,
      callbacks: { onTimeChange, onPlay, onPause, onDurationChange },
    });

    const { isReady, isLoading, editorStore, videoSourceStore, adapter } =
      sharedRendererProps;

    // PLAT-11340 — the review hears the dub the way the clip preview does: the
    // dub engine builds the dubbed audio into this player's OWN document from
    // the three dub options on its timeline plus the description held in its
    // editor (PLAT-11511). This hook is the half that can fetch: it keeps the
    // description for the reviewed language in the editor's holder (and the
    // music bed's address), prewarmed whenever the language has a dub so the
    // first press on Dubbed is not a wait.
    const reviewEditor = editorStore?.editor as VideoEditor | undefined;
    const dubDescription = useDubDescriptionRefresh({
      editor: reviewEditor,
      storyId,
      language: dubMonitorEnabled ? language : null,
      isOriginalVideoElement: probe =>
        adapter?.isOriginalVideoElement(probe) ?? false,
      musicBedUrl: dubMusicBedUrl,
      voiceStemUrl: dubVoiceStemUrl,
      refreshSource: () => videoSourceStore?.refreshSource(),
      enabled: dubMonitorEnabled,
    });
    // PLAT-11678 — an automatic re-dub replaced a recording on the server;
    // nothing this hook observes changed, so ask it to fetch again.
    const refreshDubDescription = dubDescription.refreshNow;
    useEffect(() => {
      if (dubReloadToken > 0) refreshDubDescription();
    }, [dubReloadToken, refreshDubDescription]);

    // Write the review's choice onto this player's timeline. Always written,
    // never merely inherited: this editor loads the SAME stored clip as the
    // preview, so it arrives carrying whatever dub the clip is set to — which
    // may be another language's, or Dubbed while the review's control reads
    // Original. The language and the music position are the review's own
    // (PLAT-11665; PLAT-12244 — the engine lays the bed on Both too).
    //
    // Run directly rather than through `editor.executeCommand`: the review's
    // adapter replaces that method to copy the clip onto the user-visible undo
    // list and schedule a save, and this is a transient monitoring choice (DE6)
    // that must do neither — a reviewer switching to Dubbed must not rewrite
    // the clip's export. The command itself still marks dirty and notifies, so
    // the next document pass composites the new mix; `refreshSource()` makes
    // that pass happen now.
    useEffect(() => {
      if (!isReady || !reviewEditor || !videoSourceStore) return;
      // A clip nobody has used the Voice control on holds `null`, which reads
      // as the original voice with no music.
      const current: DubOptions = reviewEditor.timeline.dubOptions ?? {
        language: null,
        voiceMode: 'original',
        musicMode: 'none',
      };
      const next: DubOptions =
        dubMonitorEnabled && audioMode !== 'original'
          ? {
              language,
              voiceMode: audioMode,
              musicMode,
              voiceVolume,
              originalVolume,
              // Only the language the review plays: its player hears no other.
              perLanguageConfiguration: withLanguageSegmentLevels(
                undefined,
                language,
                segmentLevels,
              ),
              leadIn,
            }
          : { ...current, voiceMode: 'original' };
      if (
        current.language === next.language &&
        current.voiceMode === next.voiceMode &&
        current.musicMode === next.musicMode &&
        normalizeDubVoiceVolume(current.voiceVolume) ===
          normalizeDubVoiceVolume(next.voiceVolume) &&
        normalizeDubVoiceVolume(current.originalVolume) ===
          normalizeDubVoiceVolume(next.originalVolume) &&
        sameDubPerLanguageConfiguration(
          current.perLanguageConfiguration,
          next.perLanguageConfiguration,
        ) &&
        sameDubLeadIn(current.leadIn, next.leadIn)
      ) {
        return;
      }
      new SetDubOptionsCommand(reviewEditor, next).execute();
      videoSourceStore.refreshSource();
    }, [
      isReady,
      reviewEditor,
      videoSourceStore,
      dubMonitorEnabled,
      audioMode,
      musicMode,
      voiceVolume,
      originalVolume,
      segmentLevels,
      leadIn,
      language,
    ]);

    // PLAT-11906 — the review's hear-both follows the dub: under Both, this
    // player's captions are the dub-timed ones, as under Dubbed. Only the
    // review's own editors do this; Clip Preview keeps source-timed captions
    // under Both.
    useEffect(() => {
      reviewEditor?.setBothFollowsDub(true);
    }, [reviewEditor]);

    // PLAT-11906 — tell the transcript which dub this editor makes heard, on
    // its clock and stretches, whenever that can change: the voice choice
    // above (this runs after it), or a description arriving or going.
    const onDubPlayheadRef = useRef(onDubPlayhead);
    onDubPlayheadRef.current = onDubPlayhead;
    useEffect(() => {
      if (!isReady || !reviewEditor) return undefined;
      const report = () => {
        const heard = reviewEditor.getHeardDub();
        onDubPlayheadRef.current?.(heard ? dubCaptionInputs(heard) : null);
      };
      report();
      const stopListening = reviewEditor.dubSource.onChange(report);
      return () => {
        stopListening();
        onDubPlayheadRef.current?.(null);
      };
    }, [isReady, reviewEditor, dubMonitorEnabled, audioMode, leadIn, language]);

    // PLAT-10462: this review player mounts its OWN fresh editor (via
    // `useClipPreviewRenderer` in 'player' mode). The on-screen subtitles are
    // the karaoke track, which only translates when the phrase resolver is
    // wired on the editor — `useClipPreviewRenderer` does that at editor
    // construction, so `ProduceKaraokeCommand` emits target-language subtitles
    // here without any per-component registration.
    // PLAT-11683 — what the clip's editor had when the review opened, reported
    // once so the workspace can say whether what it shows is the clip's own.
    // PLAT-11702 — the clip's own caption STYLE is kept too: Off removes the
    // review's track, and On must bring the clip's captions back as they were,
    // not from the showcase template (and not from nothing).
    const reportedTrackAtOpenRef = useRef(false);
    const clipStyleAtOpenRef = useRef<KaraokeConfig | null>(null);
    // The clip's transcript can arrive AFTER `isReady`; `useClipPreviewRenderer`
    // then hydrates the karaoke track in the SAME pass (loadTranscript →
    // hydrateAndLoadKaraoke). Reading `hasKaraokeTrack()` at the first `isReady`
    // would see a captioned clip as uncaptioned whenever its transcript loaded
    // late, and an explicit Captions On would then write and save the clip
    // (breaking DS3). Wait for the transcript to be present — the signal that the
    // late hydration has run; a clip with no transcript can carry no captions.
    const transcriptLoadedCount = editorStore?.transcriptElements?.length ?? 0;
    useEffect(() => {
      if (
        reportedTrackAtOpenRef.current ||
        !isReady ||
        !editorStore?.editor ||
        transcriptLoadedCount === 0
      ) {
        return;
      }
      reportedTrackAtOpenRef.current = true;
      const hadTrack = editorStore.editor.hasKaraokeTrack();
      clipStyleAtOpenRef.current = hadTrack
        ? editorStore.editor.getDefaultKaraokeConfig()
        : null;
      onCaptionTrackAtOpen(
        hadTrack,
        clipStyleAtOpenRef.current?.parent_template_id ?? null,
      );
    }, [isReady, editorStore, transcriptLoadedCount, onCaptionTrackAtOpen]);

    useEffect(() => {
      if (!isReady || !editorStore?.editor || !videoSourceStore) return;
      // PLAT-10462 / PLAT-11683 / PLAT-11702 — the review's captions on ITS
      // editor: Off removes the track (the clip's saved track is untouched); On
      // shows a track in the reviewed language — the clip's own when it has
      // one, else a review-only render from the showcase's template, else
      // nothing. Never written to the clip. The forced re-produce inside makes
      // the track target-language now that the translation resolver is live.
      if (captionsMode === 'off') {
        if (!removeReviewCaptions(editorStore.editor)) return;
      } else {
        const result = ensureReviewCaptions(editorStore.editor, {
          language,
          fallbackTemplate: captionFallbackTemplate,
          savedStyle: clipStyleAtOpenRef.current,
          pickedTemplate: pickedCaptionTemplate,
        });
        if (result === 'no-config' || result === 'no-template') return;
      }
      // Repaint — per CLAUDE.md, a `commandX` from a UI surface is followed by
      // `refreshSource()` (the command's markDirty sync is debounced).
      editorStore.refreshKaraokeState();
      videoSourceStore.refreshSource();
    }, [
      isReady,
      editorStore,
      videoSourceStore,
      language,
      captionFallbackTemplate,
      pickedCaptionTemplate,
      captionsMode,
    ]);

    // Apply a clip word-click seek to THIS renderer. `pendingSeek.nonce` makes
    // repeat clicks on the same word re-fire; the ref guard ensures each nonce
    // seeks once even though `sharedRendererProps` is a fresh object each render.
    // `setTime` is a no-op until the renderer is ready and keeps the current
    // play/pause state (it only re-issues play if already playing).
    const lastSeekNonceRef = useRef<number | null>(null);
    useEffect(() => {
      if (!pendingSeek || pendingSeek.nonce === lastSeekNonceRef.current)
        return;
      lastSeekNonceRef.current = pendingSeek.nonce;
      sharedRendererProps.setTime(pendingSeek.montageTime);
    }, [pendingSeek, sharedRendererProps]);

    // PLAT-10464: once the clip editor is ready (and thus the renderer is
    // reporting montage time), release the parent's highlight gate. The active
    // phrase then tracks the montage clock via the elements' own `ts`/`end_ts`
    // (parity with the clip preview) — recording time lives separately in
    // `media_ts`/`media_end_ts`, so no separate montage snapshot is needed.
    const montageReadyRef = useRef(false);
    useEffect(() => {
      if (montageReadyRef.current) return;
      if (!sharedRendererProps.isReady || !sharedRendererProps.editorStore)
        return;
      montageReadyRef.current = true;
      onMontageReady();
    }, [
      sharedRendererProps.isReady,
      sharedRendererProps.editorStore,
      onMontageReady,
    ]);

    // PLAT-10463 — the Creatomate preview emits no `ended` event; at natural
    // end it fires `onPause` (so `isPlaying` is false) and leaves `currentTime`
    // at (≈)`duration`. A bare `play()` there is treated as already-ended and
    // ignored, so detect the ended state and seek back to 0 before playing.
    // Transcript tracking + autoscroll are `currentTime`-derived, so they reset
    // on their own once time returns to 0 — no manual reset here.
    const hasEnded =
      duration > 0 && currentTime >= duration - END_EPSILON_SECONDS;

    // Play, with the end-of-clip restart: seek to 0 first, then play (setTime
    // alone only re-plays if already playing, which it isn't after end).
    const playFromHere = useCallback(() => {
      if (hasEnded) sharedRendererProps.setTime(0);
      sharedRendererProps.play();
    }, [hasEnded, sharedRendererProps]);

    // The on-screen control's toggle. The Space shortcut lives on the
    // workspace (which owns both entry paths) and runs the same verbs through
    // `onControls`, so the two can never drift apart (the end-of-clip restart
    // in particular).
    const togglePlayback = useCallback(() => {
      if (isPlaying) sharedRendererProps.pause();
      else playFromHere();
    }, [isPlaying, sharedRendererProps, playFromHere]);

    // PLAT-11657 — the workspace's transport bar drives THIS renderer through
    // the same verbs the on-screen control uses. `sharedRendererProps` is a
    // fresh object each render, so the verbs go through a ref and the report
    // only re-fires when readiness or duration changes.
    const verbsRef = useRef({
      play: playFromHere,
      pause: sharedRendererProps.pause,
      setTime: sharedRendererProps.setTime,
    });
    verbsRef.current = {
      play: playFromHere,
      pause: sharedRendererProps.pause,
      setTime: sharedRendererProps.setTime,
    };
    const controlsReady = isReady && !isLoading;
    // The bar's volume control, over THIS renderer (the clip preview's own
    // hook). Its verbs go through a ref too, and the report re-fires only when
    // the values it paints change — a fresh object each render would otherwise
    // report, re-render the parent, and report again.
    const volumeControls = usePlayerVolume({
      enabled: controlsReady,
      getRenderer: () => sharedRendererProps.rendererRef.current,
      getActiveElementIds: () =>
        sharedRendererProps.adapter?.activeElementIds ?? [],
      isPlaying,
    });
    const volumeRef = useRef(volumeControls);
    volumeRef.current = volumeControls;
    const { volume: volumeLevel, muted: volumeMuted } = volumeControls;
    useEffect(() => {
      if (!onControls) return;
      onControls({
        play: () => verbsRef.current.play(),
        pause: () => verbsRef.current.pause(),
        setTime: time => verbsRef.current.setTime(time),
        isReady: controlsReady,
        duration,
        volume: {
          volume: volumeLevel,
          muted: volumeMuted,
          setVolume: (next, opts) => volumeRef.current.setVolume(next, opts),
          toggleMute: () => volumeRef.current.toggleMute(),
        },
      });
      return () => onControls(null);
    }, [onControls, controlsReady, duration, volumeLevel, volumeMuted]);

    // PLAT-11508 follow-up — the frame follows the composition the renderer
    // actually holds, not the selected dimension, so the video always fills it.
    // Shares one rule with the clip preview modal (useRenderedAspectRatio).
    const frameAspectRatio = useRenderedAspectRatio(
      sharedRendererProps.videoSourceStore,
      sessionStore.selectedDimension,
    );

    return (
      // `CreatomateClipPreview` fills 100% with absolutely-positioned children,
      // so it needs a sized box to paint into. The clip's own aspect ratio gives
      // a real height. Scoped to this clip-path component, so the Content Studio
      // player's layout is untouched.
      <ClipPlayerSlot>
        <ClipPlayerFrame $aspectRatio={frameAspectRatio}>
          <CreatomateClipPreview
            aspectRatio={frameAspectRatio}
            sharedRendererProps={sharedRendererProps}
            poster={poster}
            autoPlay={false}
          />
          {/* Centered play/pause overlay — mirrors the Transcript & Speakers
            center control (TranscriptVideoPlayer's PlayPauseOverlay). Clicking
            toggles the review renderer's own playback; native controls stay
            hidden via setControls(false). */}
          {/* PLAT-11516 — one indicator per wait: CreatomateClipPreview shows its
              "Loading video…" state for exactly `!isReady || isLoading`, so the
              play control renders on the precise complement. Otherwise the two
              stack and the user is invited to press play on a video that has
              not loaded. */}
          {isReady && !isLoading && !onControls && (
            <PlayPauseOverlay
              $alwaysVisible={!isPlaying}
              onClick={togglePlayback}
              title={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <MuxPauseIcon fillColor="var(--accent)" />
              ) : (
                <CirclePlayIcon />
              )}
            </PlayPauseOverlay>
          )}
        </ClipPlayerFrame>
      </ClipPlayerSlot>
    );
  },
);

// PLAT-11508 — the player slot's HEIGHT is fixed, whatever the clip's shape.
//
// It used to be `width: 100%` plus the clip's own aspect ratio, so a 9:16 clip
// asked for a box nearly twice the panel's width in height and swallowed the
// whole right-hand column. The slot now always reserves the height a 16:9 clip
// occupies at full width, and the frame inside is fitted to that height — so a
// 9:16 or 1:1 clip is simply narrower and centred, never taller.
const ClipPlayerSlot = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  display: flex;
  justify-content: center;
`;

const ClipPlayerFrame = styled.div<{ $aspectRatio?: string }>`
  position: relative;
  /* Height-led: the slot decides how tall, the ratio decides how wide. */
  height: 100%;
  aspect-ratio: ${props => props.$aspectRatio ?? '9 / 16'};

  /* Square corners. The player is a panel region docked into the workspace,
     not a floating card, so rounding read as a stray card edge at every
     ratio. The shared preview rounds all four of its OWN corners, so the
     children inherit this radius (0) to override it — without that they
     re-round what is squared off here. */
  overflow: hidden;
  border-radius: 0;

  & > div {
    border-radius: inherit;
  }
`;

/** Centered play/pause control, mirroring TranscriptVideoPlayer's
    PlayPauseOverlay (dark scrim, fades out while playing, returns on hover). */
const PlayPauseOverlay = styled.div<{ $alwaysVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.3);
  opacity: ${({ $alwaysVisible }) => ($alwaysVisible ? 1 : 0)};
  transition: opacity var(--transition-normal);
  cursor: pointer;
  z-index: 1;

  &:hover {
    opacity: 1;
  }
`;

/**
 * Left-pane reference-language picker (PLAT-10655): toggles the reference
 * column between the true original and an existing translation the reviewer
 * understands (proxy). Ghost trigger in the pane header (Language Selector
 * trigger pattern); menu portals via the shared `useAnchoredDropdown` and
 * follows the documented dropdown-menu styling (rows hover
 * `--surface-elevated`, selected value gold). With no other translations
 * cached it renders the plain header label — nothing to pick.
 */
const ReferenceLanguagePicker: React.FC<{
  /** Source language display name; absent while the translation loads. */
  sourceLabel: string | undefined;
  referenceLanguage: string | null;
  proxyLanguages: string[];
  onChange: (language: string | null) => void;
  /** Sits above the workspace overlay (which varies by entry, PLAT-10286). */
  menuZIndex: number;
}> = ({
  sourceLabel,
  referenceLanguage,
  proxyLanguages,
  onChange,
  menuZIndex,
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const { menuRef, style } = useAnchoredDropdown(open, triggerRef, close, 260);

  const originalLabel = sourceLabel ? `Original · ${sourceLabel}` : 'Original';
  const triggerLabel = referenceLanguage
    ? `Reference · ${formatLanguageCaption(referenceLanguage)}`
    : originalLabel;

  if (proxyLanguages.length === 0) return <>{originalLabel}</>;

  return (
    <>
      <PickerTrigger
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Reference language"
        onClick={() => setOpen(o => !o)}
      >
        {triggerLabel}
        <ChevronDownIcon width="12" height="12" strokeColor="currentColor" />
      </PickerTrigger>
      {open &&
        style &&
        createPortal(
          <PickerMenu
            ref={menuRef}
            // The anchoring hook pins `width` to the trigger; language names
            // must never wrap (Language Selector standard), so grow to fit
            // content, keeping the trigger width as the floor.
            style={{
              ...style,
              zIndex: menuZIndex,
              width: 'max-content',
              minWidth: style.width,
            }}
            role="listbox"
            aria-label="Reference language options"
          >
            <PickerRow
              type="button"
              role="option"
              aria-selected={referenceLanguage === null}
              $selected={referenceLanguage === null}
              onClick={() => {
                onChange(null);
                close();
              }}
            >
              {originalLabel}
            </PickerRow>
            {proxyLanguages.map(lang => (
              <PickerRow
                key={lang}
                type="button"
                role="option"
                aria-selected={referenceLanguage === lang}
                $selected={referenceLanguage === lang}
                onClick={() => {
                  onChange(lang);
                  close();
                }}
              >
                {formatLanguageCaption(lang)}
                <PickerRowMeta>{lang}</PickerRowMeta>
              </PickerRow>
            ))}
          </PickerMenu>,
          document.body,
        )}
    </>
  );
};

// PLAT-11512 — data-modal-backdrop is the marker the clip preview's
// document-level Space handler checks before acting. This workspace covers
// that player completely, so without the marker Space toggled a video the
// reviewer could not see. The workspace does not use the shared Modal (it owns
// its own layout), so it carries the marker itself.
const Overlay = styled(motion.div).attrs({ 'data-modal-backdrop': 'true' })<{
  $page: boolean;
}>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: ${REVIEW_WORKSPACE_Z};
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Modal layout only: the shared Modal's scrim (`Modal.tsx` Main — a 50% black
// wash plus `--modal-blur`), so the surface underneath stays visible, dimmed.
const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: var(--modal-blur);
`;

const Surface = styled.div<{ $page: boolean }>`
  /* The review panes use a smaller transcript font than Creator Studio, so the
     paused-playhead caret (TranscriptionText '.with-cursor') must scale down
     with it — Creator Studio's default 26px spills above the line here. Scoped
     to this modal only; other surfaces keep the 26px default (PLAT-10286). */
  --with-cursor-caret-height: 1em;

  /* Make the original-pane caret render with the EXACT same box model as the
     translated pane's caret (TranscriptionTextTranslated '.cursor-before'): an
     in-flow inline-block, text-bottom aligned, with a 1px right margin. The
     shared default is an ABSOLUTE ::before (left:-2px, bottom:0) that takes no
     layout space and just overlays the inter-word gap — so its vertical anchor
     AND its horizontal spacing both differed from the translated pane. Going
     in-flow makes the spacing identical by construction. position:static also
     neutralizes the default's left/bottom offsets. Height comes from the
     --with-cursor-caret-height var (1em). Scoped to the review only. */
  & .with-cursor::before {
    position: static;
    vertical-align: text-bottom;
    margin-right: 1px;
  }

  position: relative;
  display: flex;
  flex-direction: column;
  background: var(--background);
  overflow: hidden;

  ${props =>
    props.$page
      ? css`
          /* A place, not a dialog: fills the panel edge to edge, with only a
             hairline against the sidebar. */
          width: 100%;
          height: 100%;
          border-left: 1px solid var(--border);
        `
      : css`
          width: min(96vw, 1600px);
          height: min(94vh, 980px);
          border: 1px solid var(--border);
          border-radius: var(--radius-lg, 12px);
          box-shadow: var(--elevation-lg);
        `}
`;

// Translation Review: the three panels, as they were. Dubbing Review: the
// original's rail, the translation column, the player, and the right pane —
// which is a rail or the full 360px. Only the last swap animates: browsers
// interpolate `grid-template-columns` between track lists of equal length
// with unit-compatible tracks (360px ↔ 44px), and snap otherwise (3 ↔ 4
// tracks, `fr` ↔ `px`) — which is fine for the mode switch, whose columns
// remount anyway.
/** Space between the columns, and around them — lines were doing this job. */
const BODY_GUTTER = 16;
/** The right column when the player is small (Translation Review). */
const RIGHT_COLUMN_DOCKED = 360;
/**
 * The right column in Dubbing Review: the clip preview's stage width
 * (PLAT-11657), so the video reads at the size every other Arbor player has.
 */
const RIGHT_COLUMN_STAGE = 705;
/** What the translation column will not shrink below to give the stage room. */
const MIN_TRANSLATION_WIDTH = 360;

// PLAT-11661 — three columns separated by space, not lines: the aligned
// transcript (original + translation), then the right column card (player on
// top, panel below). Widths are animated by the panes themselves.
const Body = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  gap: ${BODY_GUTTER}px;
  padding: 0 ${BODY_GUTTER}px ${BODY_GUTTER}px;
`;

const HEADER_ROW_HEIGHT = '40px';

const RightTabsRow = styled.div`
  flex-shrink: 0;

  /* The shared Tabs draws its own rule under the tab row; the card's tone is
     the separation here. */
  & > div > div:first-child {
    border-bottom: none;
  }

  [role='tablist'] {
    height: ${HEADER_ROW_HEIGHT};
    gap: var(--spacing-sm);
    padding: 0 var(--spacing-md) 0 var(--spacing-sm);
  }

  /* The shared Tabs also renders an (empty) tab panel with a 16px top inset;
     the review draws its own panel below, so that box only added a gap. */
  [role='tabpanel'] {
    display: none;
  }
`;

const PaneHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  box-sizing: border-box;
  height: ${HEADER_ROW_HEIGHT};
  /* Left inset matches the row text below it (Column padding + ROW_TEXT_INSET)
     so "Original · …" sits on the same line as the speaker label. */
  padding: 0 12px 0 20px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
`;

// The header's own text, so it can be the flex item that gives way beside the
// view controls. `min-width: 0` is the half a bare text node can't have.
const PaneHeaderLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// The aligned transcript's window. Its width animates; the fixed-width grid
// inside is pinned to the RIGHT edge, so folding the window clips the ORIGINAL
// column away while the translation column stays exactly where it is.
const AlignedPane = styled(motion.section)`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  justify-content: flex-end;
  overflow: hidden;
`;

const AlignedInner = styled.div`
  flex: none;
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const AlignedHeaders = styled.div<{ $single: boolean }>`
  flex-shrink: 0;
  display: grid;
  grid-template-columns: ${props => (props.$single ? '1fr' : '1fr 1fr')};
`;

const AlignedBody = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
`;

// The right column: a soft card, the player on top, the panel below. Its width
// animates between the docked 360px and the space the original column gave up.
const RightColumn = styled(motion.aside)`
  flex: none;
  width: ${RIGHT_COLUMN_DOCKED}px;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  /* The same breath above the card as the Body leaves below it. */
  margin-top: ${BODY_GUTTER}px;
  background: var(--surface);
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
`;

const RightPanelBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const RightPanelScroll = styled.div<{ $tight: boolean }>`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  /* Dubbing: the player takes most of the column's height, so the panel
     starts right under its tab row — the card's own padding is the gap. */
  ${props =>
    props.$tight &&
    css`
      padding-top: 0;
    `}
  /* PLAT-11516 — the LEFT inset matches the tab row's spacing-sm, so the
     active tab's fill and the cards beneath it share one left edge. The right
     keeps the larger inset: it faces the scrollbar, not an alignment partner. */
  padding: 12px 12px 12px var(--spacing-sm);
`;

/**
 * The player, at the top of the right column: its own dark surface, the header
 * row above the frame. Width-led — it simply fills whatever the column is.
 */
const PlayerArea = styled.div<{ $inset: boolean; $fullscreen: boolean }>`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  min-width: 0;
  /* Dubbing: a stage object inset on the card. Translation: the compact
     player fills the column edge to edge, canvas and bar as one block, with a
     breath above the canvas. */
  /* No top inset: the column's own top margin is the space above (so the
     audio row and the compact canvas sit where they did). */
  /* The horizontal inset is the tab row's own (spacing-sm), so the canvas's
     left edge lines up with the selected tab beneath it. */
  padding: ${props => (props.$inset ? '0 var(--spacing-sm) 4px' : '0')};

  ${props =>
    props.$fullscreen &&
    css`
      /* PLAT-11657 — pseudo-fullscreen, like the clip preview's Stage: the
         whole area takes the viewport, the 16:9 frame is centred at the
         largest size that fits above the bar, and the bar sits under it at
         the same width. Escape leaves. */
      position: fixed;
      inset: 0;
      padding: var(--spacing-md);
      background: #000;
      justify-content: center;
      align-items: center;
    `}
`;

/* Fullscreen frame: the widest 16:9 that leaves room for the bar. */
const FULLSCREEN_FRAME_WIDTH = 'min(100%, calc((100vh - 112px) * 16 / 9))';

/**
 * The player is width-led (16:9 off the column), so when the column is wide
 * and the window short it would resolve taller than the pane. Cap the frame's
 * width to what the Surface's height budget can show at 16:9 and centre it.
 */
const EXPANDED_PLAYER_MAX_WIDTH = 'calc((min(94vh, 980px) - 300px) * 16 / 9)';

// The clip preview's Stage: a dark canvas, the bar completing the shape
// below. Rounded top corners when it sits as an inset object (Dubbing);
// square when it fills its column (Translation).
const PlayerFrame = styled.div<{ $rounded: boolean; $fullscreen: boolean }>`
  position: relative;
  min-width: 0;
  width: 100%;
  max-width: ${EXPANDED_PLAYER_MAX_WIDTH};
  align-self: center;
  overflow: hidden;
  border-radius: ${props => (props.$rounded ? '10px 10px 0 0' : '0')};
  background: #000;

  ${props =>
    props.$fullscreen &&
    css`
      width: ${FULLSCREEN_FRAME_WIDTH};
      max-width: none;
      border-radius: 0;
    `}
`;

// The bar sits flush under the frame, the two reading as one player (the
// frame's bottom corners are square, the bar's are rounded — the clip
// preview's Stage + bar).
const PlayerBar = styled.div<{ $rounded: boolean; $fullscreen: boolean }>`
  width: 100%;
  max-width: ${EXPANDED_PLAYER_MAX_WIDTH};
  align-self: center;

  /* The total time reads at the current time's weight here, not the shared
     bar's 40% — the review's times are what the reviewer is checking. The
     doubled class outranks the bar's own .end rule, which is injected after
     this one and would otherwise win the tie. */
  && span.end.end {
    /* !important: the shared bar's .end rule is injected after this one and
       has won the cascade in practice; this is the one place the review
       overrides it, and it must hold. Colour set too, so the total never
       inherits a muted parent. */
    opacity: 0.8 !important;
    color: var(--foreground) !important;
  }

  ${props =>
    props.$fullscreen &&
    css`
      width: ${FULLSCREEN_FRAME_WIDTH};
      max-width: none;
    `}

  ${props =>
    !props.$rounded &&
    css`
      /* Edge to edge in Translation: the bar's own bottom radii go too. */
      & > div {
        border-radius: 0;
      }
    `}
`;

/**
 * The player's header row, one line like the transcript pane's: which audio
 * this is on the left (both research participants could not say whether they
 * were hearing the dub), the Original / Dubbed / Both control on the right.
 * Same type as "Translation · <language>" across the way — not a badge.
 */
const PlayerHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
  min-height: ${HEADER_ROW_HEIGHT};
  padding: 0 12px 0 var(--spacing-md);
`;

const PlayerHeaderLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
`;

// PLAT-11685 — the first dub, offered from the player settings row when the
// reviewed language has none. The same outline chrome as the language menu's
// own "Dub with AI Voice" (TranslationToggle's HeaderDubButton), so the two
// ways to start a dub read as the one action; a secondary control, not the
// page's primary.
/** The first-dub lesson's key (PLAT-11706); one per browser, not per story. */
const REVIEW_FIRST_DUB_HINT_KEY = 'review-first-dub';

const ReviewDubButton = styled.button`
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 8px;
  padding: 3px 10px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 6px;
  cursor: pointer;
  transition:
    background-color 120ms ease,
    color 120ms ease;

  &:hover {
    background: var(--surface-elevated);
  }
`;

const Summary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: 88px 1fr;
  gap: 8px;
  align-items: baseline;
`;

const SummaryKey = styled.span`
  font-size: var(--text-xs);
  color: var(--text-muted);
`;

const SummaryVal = styled.span`
  font-size: var(--text-sm);
  color: var(--foreground);
  word-break: break-word;
`;

const SummaryEmpty = styled.div`
  font-size: var(--text-sm);
  color: var(--text-muted);
`;

// One name per line — the Editors row may hold several reviewers. Resets the
// default list padding/marker so it aligns flush with the other summary values.
const EditorList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;

  & > li + li {
    margin-top: 4px;
  }
`;

/**
 * PLAT-11877 — where a caption style came from, for the Captions picker's own
 * row: the showcase's template, an Arbor preset, or a hand-set style with no
 * template behind it.
 */
export function captionStyleSource(
  templateId: string | null | undefined,
  isPreset?: (templateId: string) => boolean,
): string {
  if (!templateId) return 'Custom Style';
  return isPreset?.(templateId) ? 'Arbor Preset' : 'Workspace Template';
}
